import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { 
  Loader2, Lightbulb, Compass, Target, Rocket, Layers, 
  LayoutList, Package, LayoutGrid, Sparkles, Filter, Search,
  ChevronRight, ChevronDown, Plus, Users, Pencil, Lock, CheckCircle, Settings,
  Check, X, TrendingUp, Building2, Globe, DollarSign, BarChart3,
  ExternalLink, ThumbsUp, ThumbsDown, Trash2, Edit3, UserSearch,
  Zap, Clock, Inbox, AlertTriangle, Tag, Trophy, Archive, RotateCcw,
  MessageSquareText, Play, Square, Crown, Coins
} from 'lucide-react';
import Sidebar from '../components/layout/Sidebar';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuCheckboxItem,
  DropdownMenuSeparator,
  DropdownMenuItem,
} from '../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { fetchProductHierarchy, epicsApi, featuresApi, productsApi, releasesApi, teamApi, copilotApi, goalsApi, personasApi, unifiedCopilotApi, deepResearchApi, unifiedCollectionCopilotApi, collectionActionsApi, initiativesApi, stripeApi, tokenApi } from '../services/api';
import { useSafeMode } from '../contexts/SafeModeContext';
import { usePermissions } from '../contexts/PermissionsContext';
import { useFeatureFlags, FEATURE_IDS } from '../hooks/useFeatureFlags';
import { cn } from '../lib/utils';
import UsageBanner from '../components/billing/UsageBanner';
import ReactMarkdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import { toast } from 'sonner';
import CollectionCopilotDrawer from '../components/copilot/CollectionCopilotDrawer';
import { useAuth } from '../contexts/AuthContext';
import GlobalSearch from '../components/search/GlobalSearch';
import UpgradeTab from '../components/product/UpgradeTab';
import FreeProductAvatarMenu from '../components/layout/FreeProductAvatarMenu';

// Import tab components
import KanbanBoard from '../components/product/KanbanBoard';
import FeatureDetailModal from '../components/product/FeatureDetail';
import ProductSettingsModal from '../components/product/ProductSettingsModal';
import DeliveryTab from '../components/product/DeliveryTab';
import ArchitectTab from '../components/product/ArchitectTab';
import InitiativeDetailModal from '../components/initiative/InitiativeDetailModal';
import EpicDetailModal from '../components/epic/EpicDetailModal';
import RequestsTab from '../components/requests/RequestsTab';
import PersonaDetailModal from '../components/product/PersonaDetailModal';
import GoalDetailModal from '../components/goal/GoalDetailModal';
import SegmentDetailModal from '../components/product/SegmentDetailModal';
import SplitFeatureModal from '../components/product/SplitFeatureModal';
import { EntityModalProvider, useEntityModal } from '../contexts/EntityModalContext';
import EntityModals from '../components/copilot/EntityModals';
import { useProductDateCache } from '../contexts/ProductDateCacheContext';
import { MarkdownFieldEditModal } from '../components/ui/markdown-field-edit-modal';

// Tab configuration with visual groupings
// Tab to feature flag mapping
const TAB_FEATURE_FLAGS = {
  'ideation': FEATURE_IDS.TAB_IDEATION,
  'requests': FEATURE_IDS.TAB_IDEATION, // Requests is part of ideation/discover
  'strategy': FEATURE_IDS.TAB_STRATEGY,
  'market': FEATURE_IDS.TAB_MARKET,
  'personas': FEATURE_IDS.TAB_PERSONAS,
  'goals': FEATURE_IDS.TAB_GOALS,
  'initiatives': FEATURE_IDS.TAB_INITIATIVES,
  'epics': FEATURE_IDS.TAB_EPICS,
  'features': FEATURE_IDS.TAB_FEATURES,
  'releases': FEATURE_IDS.TAB_RELEASES,
  'kanban': FEATURE_IDS.TAB_KANBAN,
  'architect': FEATURE_IDS.TAB_ARCHITECT,
  'delivery': FEATURE_IDS.TAB_DELIVERY,
};

// Roles that have full access to all tabs
const FULL_ACCESS_ROLES = ['owner', 'product_manager', 'admin'];

// Tabs that collaborators CAN access
const COLLABORATOR_ALLOWED_TABS = ['ideation', 'releases', 'kanban', 'delivery'];

const TABS = [
  // Discover Group - Raw input and ideation
  { id: 'ideation', label: 'Ideation', icon: Lightbulb, group: 'discover' },
  { id: 'requests', label: 'Requests', icon: Inbox, group: 'discover', separator: true },
  // Define Group - Strategy and planning
  { id: 'strategy', label: 'Strategy', icon: Compass, group: 'define' },
  { id: 'market', label: 'Market', icon: TrendingUp, group: 'define', separator: true },
  { id: 'personas', label: 'Personas', icon: Users, group: 'define' },
  { id: 'goals', label: 'Goals', icon: Target, group: 'define' },
  { id: 'initiatives', label: 'Initiatives', icon: Rocket, group: 'define', separator: true },
  // Build Group - What we're building
  { id: 'epics', label: 'Epics', icon: Layers, group: 'build' },
  { id: 'features', label: 'Features', icon: LayoutList, group: 'build', separator: true },
  // Ship Group - Delivery
  { id: 'releases', label: 'Releases', icon: Package, group: 'ship' },
  { id: 'kanban', label: 'Kanban', icon: LayoutGrid, group: 'ship' },
  { id: 'architect', label: 'Architect', icon: Building2, group: 'ship' },
  { id: 'delivery', label: 'Delivery', icon: Zap, group: 'ship' },
  // Upgrade tab (free tier only, added dynamically)
  { id: 'upgrade', label: 'Upgrade', icon: Crown, group: 'upgrade', freeOnly: true },
];

const ProductDetailPage = () => {
  const formatTokensShort = (n) => {
    if (n == null) return '...';
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `${Math.round(n / 1_000)}K`;
    return String(n);
  };

  const { getThemeColors } = useSafeMode();
  const { canCrudProducts, canUseAI, isOwner, isCollaborator, role } = usePermissions();
  const { isEnabled, refresh: refreshFlags } = useFeatureFlags();
  const { user, organization, loading: authLoading } = useAuth();
  const isFreeProduct = organization?.account_type === 'free_product';
  const colors = getThemeColors();
  const navigate = useNavigate();
  const { productId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Token balance for free tier pill
  const [freeTokenBalance, setFreeTokenBalance] = useState(null);
  
  // Check if user has full tab access
  const hasFullTabAccess = FULL_ACCESS_ROLES.includes(role) || !role;
  
  // Data state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [product, setProduct] = useState(null);
  const [personas, setPersonas] = useState([]);
  const [goals, setGoals] = useState([]);
  const [initiatives, setInitiatives] = useState([]);
  const [epics, setEpics] = useState([]);
  const [allFeatures, setAllFeatures] = useState([]);
  const [releases, setReleases] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [kanbanColumns, setKanbanColumns] = useState(['Backlog', 'In Progress', 'Review', 'Done']);
  
  // ==================== CASCADING REFRESH SYSTEM ====================
  // Tracks which collections need refresh due to upstream changes
  // Example: KPI change on Goal → marks initiatives, epics, features as stale
  const [staleCollections, setStaleCollections] = useState(new Set());
  
  // Cascade rules: when entity type changes, which collections become stale
  const CASCADE_RULES = useMemo(() => ({
    goals: {
      kpiChange: ['initiatives', 'epics', 'features'],
      delete: ['initiatives'],
      titleChange: ['initiatives']
    },
    initiatives: {
      kpiChange: ['epics', 'features'],
      delete: ['epics'],
      titleChange: ['epics']
    },
    epics: {
      kpiChange: ['features'],
      delete: ['features'],
      titleChange: ['features']
    },
    personas: {
      delete: ['epics', 'features'],
      titleChange: ['epics', 'features']
    },
    releases: {
      delete: ['features'],
      titleChange: ['features']
    }
  }), []);
  
  // Mark collections as needing refresh
  const markStale = useCallback((collections) => {
    if (!collections || collections.length === 0) return;
    setStaleCollections(prev => {
      const next = new Set(prev);
      collections.forEach(c => next.add(c));
      return next;
    });
  }, []);
  
  // Clear stale flag for a collection (after refresh)
  const clearStale = useCallback((collection) => {
    setStaleCollections(prev => {
      const next = new Set(prev);
      next.delete(collection);
      return next;
    });
  }, []);
  
  // Helper: Get cascade targets based on entity type and change type
  const getCascadeTargets = useCallback((entityType, changeType) => {
    return CASCADE_RULES[entityType]?.[changeType] || [];
  }, [CASCADE_RULES]);
  
  // Product date cache for calendar highlighting
  const { loadProductDates, refreshReleaseDates } = useProductDateCache();
  
  // Fun loading messages that rotate
  const loadingMessages = [
    "A few minutes here makes everything faster later...",
    "Good things come to those who wait...",
    "Brewing up your product data...",
    "Gathering the bits and bytes...",
    "Almost there, just dotting the i's...",
    "Loading awesomeness, please hold...",
    "Your product is worth the wait...",
    "Preparing your command center...",
    "Connecting the dots...",
    "Making everything just right..."
  ];
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
  
  // Rotate loading messages every 3.5 seconds
  useEffect(() => {
    if (!loading) return;
    
    const interval = setInterval(() => {
      setLoadingMessageIndex(prev => (prev + 1) % loadingMessages.length);
    }, 3500);
    
    return () => clearInterval(interval);
  }, [loading, loadingMessages.length]);
  
  // UI state
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [selectedEpic, setSelectedEpic] = useState(null);
  const [selectedEpicContext, setSelectedEpicContext] = useState({ epic: null, siblingFeatures: [] });
  const [showProductSettings, setShowProductSettings] = useState(false);
  // For stacked modals: feature modal opened from within epic modal
  const [stackedFeature, setStackedFeature] = useState(null);
  const [stackedEpicContext, setStackedEpicContext] = useState({ epic: null, siblingFeatures: [] });
  const epicFeaturePatchRef = useRef(null);
  
  // Search navigation state - for opening entities from global search
  const [searchNavTarget, setSearchNavTarget] = useState(null);
  
  // Active tab from URL, defaults to product's default_tab or first visible tab
  const urlTab = searchParams.get('tab');
  const defaultTab = isFreeProduct ? 'strategy' : (product?.default_tab || 'ideation');
  const activeTab = urlTab || defaultTab;
  const setActiveTab = (tab) => setSearchParams({ tab });
  
  // Redirect collaborators away from restricted tabs
  useEffect(() => {
    if (!hasFullTabAccess && activeTab && !COLLABORATOR_ALLOWED_TABS.includes(activeTab)) {
      // Redirect to ideation (the default allowed tab)
      setSearchParams({ tab: 'ideation' });
    }
  }, [hasFullTabAccess, activeTab, setSearchParams]);

  // Handle return from Stripe checkout after upgrade
  useEffect(() => {
    if (searchParams.get('upgraded') === 'true') {
      toast.success('Upgrade complete! Welcome to the full StackRanked experience.');
      // Finalize upgrade: ensure scaffolding (Goal + Initiative) is created
      stripeApi.finalizeUpgrade().catch(() => {});
      refreshFlags();
      // Clean up the query param
      const next = new URLSearchParams(searchParams);
      next.delete('upgraded');
      if (!next.get('tab')) next.set('tab', 'strategy');
      setSearchParams(next, { replace: true });
    }
  }, []); // Run once on mount

  // Fetch token balance for free tier pill
  useEffect(() => {
    if (!isFreeProduct) return;
    tokenApi.getStatus()
      .then(res => setFreeTokenBalance(res.data?.remaining ?? null))
      .catch(() => {});
  }, [isFreeProduct]);

  // Map tabs to their primary collection for stale-checking
  const TAB_COLLECTION_MAP = useMemo(() => ({
    'goals': 'goals',
    'initiatives': 'initiatives', 
    'epics': 'epics',
    'features': 'features',
    'personas': 'personas',
    'releases': 'releases'
  }), []);

  // Check for stale collections when tab changes - refresh if needed
  // This is a ref to track if we've already triggered a refresh for this tab
  const lastRefreshedTab = useRef(null);
  
  useEffect(() => {
    const collection = TAB_COLLECTION_MAP[activeTab];
    if (!collection) return; // Tab doesn't have a primary collection
    if (!staleCollections.has(collection)) return; // Collection not stale
    if (lastRefreshedTab.current === `${activeTab}-${collection}`) return; // Already refreshing
    
    // Mark that we're refreshing this tab/collection combo
    lastRefreshedTab.current = `${activeTab}-${collection}`;
    
    // Trigger collection refresh and clear stale flag
    console.log(`[Cascade] Tab "${activeTab}" is stale, refreshing "${collection}" collection`);
    
    // We need to call refresh after it's defined, so we use a microtask
    const doRefresh = async () => {
      try {
        switch (collection) {
          case 'goals':
            const goalsRes = await goalsApi.list(productId);
            setGoals(goalsRes.data || []);
            break;
          case 'initiatives':
            const initRes = await initiativesApi.listByProduct(productId);
            setInitiatives(initRes.data || []);
            break;
          case 'epics':
            const epicsRes = await epicsApi.listByProduct(productId, { includeArchived: true });
            setEpics(epicsRes.data || []);
            break;
          case 'features':
            if (epics.length > 0) {
              const featPromises = epics.map(epic => 
                featuresApi.list(epic.epic_id).catch(() => ({ data: [] }))
              );
              const featResults = await Promise.all(featPromises);
              setAllFeatures(featResults.flatMap(r => r.data || []));
            }
            break;
          case 'personas':
            const personasRes = await personasApi.list(productId);
            setPersonas(personasRes.data || []);
            break;
          case 'releases':
            const releasesRes = await releasesApi.list(productId);
            setReleases(releasesRes.data || []);
            break;
        }
        clearStale(collection);
      } catch (err) {
        console.error(`[Cascade] Error refreshing stale collection "${collection}":`, err);
      }
    };
    
    doRefresh();
  }, [activeTab, staleCollections, TAB_COLLECTION_MAP, productId, epics, clearStale]);

  // Helper: Get contrast color for text on colored backgrounds
  const getContrastColor = (hexColor) => {
    if (!hexColor) return '#ffffff';
    const hex = hexColor.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness > 128 ? '#1f2937' : '#ffffff';
  };

  // Load product data
  useEffect(() => {
    const loadProduct = async () => {
      try {
        setLoading(true);
        const [hierarchyData, epicsData, initiativesData, teamData, columnsData, releasesData] = await Promise.all([
          fetchProductHierarchy(productId),
          epicsApi.listByProduct(productId, { includeArchived: true }).catch(() => ({ data: [] })),
          initiativesApi.listByProduct(productId).catch(() => ({ data: [] })),
          teamApi.list().catch(() => ({ data: { members: [] } })),
          productsApi.getKanbanColumns(productId).catch(() => ({ data: { columns: ['Backlog', 'In Progress', 'Review', 'Done'] } })),
          releasesApi.list(productId).catch(() => ({ data: [] }))
        ]);
        setProduct(hierarchyData.product);
        setPersonas(hierarchyData.personas);
        setGoals(hierarchyData.goals);
        setInitiatives(initiativesData.data || []);
        setEpics(epicsData.data || []);
        setReleases(releasesData.data || []);
        
        // Load product date cache for calendar highlighting
        loadProductDates(productId);
        
        // Transform team members for the feature modal format
        const members = (teamData.data?.members || []).map(m => ({
          id: m.user_id,
          user_id: m.user_id,
          name: m.name || m.email?.split('@')[0] || 'Unknown',
          email: m.email,
          role: m.role
        }));
        setTeamMembers(members);
        
        // Set kanban columns (statuses)
        setKanbanColumns(columnsData.data?.columns || ['Backlog', 'In Progress', 'Review', 'Done']);
        
        // Load all features for the features tab
        if (epicsData.data?.length > 0) {
          const featurePromises = epicsData.data.map(epic => 
            featuresApi.list(epic.epic_id).catch(() => ({ data: [] }))
          );
          const featureResults = await Promise.all(featurePromises);
          const features = featureResults.flatMap(r => r.data || []);
          setAllFeatures(features);
        }
        
        setError(null);
      } catch (err) {
        console.error('Error loading product:', err);
        setError('Failed to load product details');
      } finally {
        setLoading(false);
      }
    };

    // Wait for auth to be ready before loading product
    if (productId && !authLoading && user) {
      loadProduct();
    } else if (!authLoading && !user) {
      // Auth check complete but no user - will redirect via ProtectedRoute
      setLoading(false);
    }
  }, [productId, authLoading, user]);

  // Handle opening entities from URL params (from Executive AI navigation)
  useEffect(() => {
    const openType = searchParams.get('open');
    const entityId = searchParams.get('id');
    
    if (!openType || !entityId || loading) return;
    
    // Clear the URL params after handling
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('open');
    newParams.delete('id');
    setSearchParams(newParams, { replace: true });
    
    // Find and open the entity
    const openEntity = async () => {
      try {
        switch (openType) {
          case 'feature':
            const feature = allFeatures.find(f => f.feature_id === entityId);
            if (feature) {
              handleSelectFeature(feature);
            } else {
              // Try to fetch directly
              const featRes = await featuresApi.get(entityId);
              if (featRes.data) {
                handleSelectFeature(featRes.data);
              }
            }
            break;
          case 'epic':
            const epic = epics.find(e => e.epic_id === entityId);
            if (epic) {
              setSelectedEpic(epic);
            }
            break;
          case 'goal':
            const goal = goals.find(g => g.goal_id === entityId);
            if (goal) {
              setSelectedGoal(goal);
            }
            break;
          case 'persona':
            const persona = personas.find(p => p.persona_id === entityId);
            if (persona) {
              setSelectedPersona(persona);
            }
            break;
          default:
            console.log('Unknown entity type to open:', openType);
        }
      } catch (err) {
        console.error('Error opening entity from URL:', err);
      }
    };
    
    // Wait a tick for data to be loaded
    setTimeout(openEntity, 100);
  }, [searchParams, loading, allFeatures, epics, goals, personas, setSearchParams]);

  // ==================== UNIFIED REFRESH SYSTEM ====================
  // Three levels: 'product' (full), 'collection' (one type), 'entity' (single item)
  // Usage:
  //   refresh('product')                    - Full refresh, all data
  //   refresh('collection', 'goals')        - Refresh all goals
  //   refresh('entity', 'goals', goalId)    - Update single goal in state
  //   refresh('entity', 'goals', goalData)  - Update with provided data (optimistic)
  
  const refresh = useCallback(async (scope, collection, entityIdOrData) => {
    try {
      switch (scope) {
        case 'product':
          // Full refresh - fetches everything
          const [hierarchyData, epicsData, initiativesData, releasesData] = await Promise.all([
            fetchProductHierarchy(productId),
            epicsApi.listByProduct(productId, { includeArchived: true }).catch(() => ({ data: [] })),
            initiativesApi.listByProduct(productId).catch(() => ({ data: [] })),
            releasesApi.list(productId).catch(() => ({ data: [] }))
          ]);
          setProduct(hierarchyData.product);
          setPersonas(hierarchyData.personas);
          setGoals(hierarchyData.goals);
          setInitiatives(initiativesData.data || []);
          setEpics(epicsData.data || []);
          setReleases(releasesData.data || []);
          
          if (epicsData.data?.length > 0) {
            const featurePromises = epicsData.data.map(epic => 
              featuresApi.list(epic.epic_id).catch(() => ({ data: [] }))
            );
            const featureResults = await Promise.all(featurePromises);
            setAllFeatures(featureResults.flatMap(r => r.data || []));
          }
          break;
          
        case 'collection':
          // Refresh one collection type
          switch (collection) {
            case 'goals':
              const goalsRes = await goalsApi.list(productId);
              setGoals(goalsRes.data || []);
              break;
            case 'personas':
              const personasRes = await personasApi.list(productId);
              setPersonas(personasRes.data || []);
              break;
            case 'initiatives':
              console.log('[refresh] Refreshing initiatives collection');
              const initRes = await initiativesApi.listByProduct(productId);
              console.log('[refresh] Got initiatives:', initRes.data?.length);
              setInitiatives(initRes.data || []);
              break;
            case 'epics':
              const epicsRes = await epicsApi.listByProduct(productId, { includeArchived: true });
              setEpics(epicsRes.data || []);
              break;
            case 'features':
              if (epics.length > 0) {
                const featPromises = epics.map(epic => 
                  featuresApi.list(epic.epic_id).catch(() => ({ data: [] }))
                );
                const featResults = await Promise.all(featPromises);
                setAllFeatures(featResults.flatMap(r => r.data || []));
              }
              break;
            case 'releases':
              const relRes = await releasesApi.list(productId);
              setReleases(relRes.data || []);
              break;
          }
          break;
          
        case 'entity':
          // Update single entity - can accept ID (fetch) or data object (optimistic)
          const isDataObject = entityIdOrData && typeof entityIdOrData === 'object';
          
          switch (collection) {
            case 'goals':
              if (isDataObject) {
                // Optimistic merge — preserve computed fields (e.g. available_kpi_count)
                const goalData = entityIdOrData;
                setGoals(prev => prev.map(g => 
                  g.goal_id === goalData.goal_id ? { ...g, ...goalData } : g
                ));
              } else {
                // Fetch fresh from API
                const goalRes = await goalsApi.get(entityIdOrData);
                if (goalRes.data) {
                  setGoals(prev => prev.map(g => 
                    g.goal_id === goalRes.data.goal_id ? goalRes.data : g
                  ));
                }
              }
              break;
            case 'personas':
              if (isDataObject) {
                const personaData = entityIdOrData;
                setPersonas(prev => prev.map(p => 
                  p.persona_id === personaData.persona_id ? { ...p, ...personaData } : p
                ));
              } else {
                const personaRes = await personasApi.get(entityIdOrData);
                if (personaRes.data) {
                  setPersonas(prev => prev.map(p => 
                    p.persona_id === personaRes.data.persona_id ? personaRes.data : p
                  ));
                }
              }
              break;
            case 'initiatives':
              if (isDataObject) {
                const initData = entityIdOrData;
                setInitiatives(prev => prev.map(i => 
                  i.initiative_id === initData.initiative_id ? { ...i, ...initData } : i
                ));
              } else {
                const initRes = await initiativesApi.get(entityIdOrData);
                if (initRes.data) {
                  setInitiatives(prev => prev.map(i => 
                    i.initiative_id === initRes.data.initiative_id ? initRes.data : i
                  ));
                }
              }
              break;
            case 'epics':
              if (isDataObject) {
                const epicData = entityIdOrData;
                setEpics(prev => prev.map(e => 
                  e.epic_id === epicData.epic_id ? { ...e, ...epicData } : e
                ));
              } else {
                const epicRes = await epicsApi.get(entityIdOrData);
                if (epicRes.data) {
                  setEpics(prev => prev.map(e => 
                    e.epic_id === epicRes.data.epic_id ? epicRes.data : e
                  ));
                }
              }
              break;
            case 'features':
              if (isDataObject) {
                const featData = entityIdOrData;
                setAllFeatures(prev => prev.map(f => 
                  f.feature_id === featData.feature_id ? { ...f, ...featData } : f
                ));
              } else {
                const featRes = await featuresApi.get(entityIdOrData);
                if (featRes.data) {
                  setAllFeatures(prev => prev.map(f => 
                    f.feature_id === featRes.data.feature_id ? featRes.data : f
                  ));
                }
              }
              break;
          }
          break;
      }
    } catch (err) {
      console.error(`Error in refresh(${scope}, ${collection}):`, err);
    }
  }, [productId, epics]);
  
  // Legacy alias - components still using refreshData will get full refresh
  // TODO: Remove once all components migrated to refresh()
  const refreshData = useCallback(() => refresh('product'), [refresh]);

  // Handle feature selection
  const handleSelectFeature = async (feat) => {
    setSelectedFeature(feat);
    const epic = epics.find(e => e.epic_id === feat.epic_id);
    if (epic) {
      try {
        const res = await featuresApi.list(epic.epic_id);
        setSelectedEpicContext({ epic, siblingFeatures: res.data || [] });
      } catch {
        setSelectedEpicContext({ epic, siblingFeatures: [] });
      }
    }
  };

  // Handle saving a market segment (for EntityModals)
  const handleSaveSegment = async (segmentData) => {
    if (!segmentData?.title?.trim()) {
      toast.error('Segment title is required');
      return;
    }
    
    try {
      const currentSegments = product?.market_segments || [];
      const existingIndex = currentSegments.findIndex(s => s.segment_id === segmentData.segment_id);
      
      let updatedSegments;
      if (existingIndex >= 0) {
        // Update existing
        updatedSegments = [...currentSegments];
        updatedSegments[existingIndex] = segmentData;
      } else {
        // Add new
        updatedSegments = [...currentSegments, segmentData];
      }
      
      await productsApi.update(productId, { market_segments: updatedSegments });
      await refreshData();
      toast.success(existingIndex >= 0 ? 'Segment updated' : 'Segment added');
    } catch (err) {
      console.error('Failed to save segment:', err);
      toast.error('Failed to save segment');
      throw err;
    }
  };
  
  // Handle deleting a market segment (for EntityModals)
  const handleDeleteSegment = async (segmentId) => {
    try {
      const updatedSegments = (product?.market_segments || []).filter(s => s.segment_id !== segmentId);
      await productsApi.update(productId, { market_segments: updatedSegments });
      await refreshData();
      toast.success('Segment deleted');
    } catch (err) {
      console.error('Failed to delete segment:', err);
      toast.error('Failed to delete segment');
      throw err;
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className={cn("flex h-screen", colors.bgPrimary)}>
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center max-w-md">
            <Loader2 className={cn("w-8 h-8 animate-spin mx-auto mb-4", colors.textAccent)} />
            <p 
              className={cn(
                "text-lg animate-fade-in",
                colors.textSecondary
              )}
              key={loadingMessageIndex}
              style={{ 
                animation: 'fadeIn 0.5s ease-in-out'
              }}
            >
              {loadingMessages[loadingMessageIndex]}
            </p>
          </div>
        </div>
        <style>{`
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(5px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}</style>
      </div>
    );
  }

  // Error state
  if (error || !product) {
    return (
      <div className={cn("flex h-screen", colors.bgPrimary)}>
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <p className="text-red-600 mb-4">{error || 'Product not found'}</p>
            <Button onClick={() => navigate('/products')}>Back to Products</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <EntityModalProvider
      features={allFeatures}
      epics={epics}
      personas={personas}
      goals={goals}
      initiatives={initiatives}
      products={product ? [product] : []}
      onNavigateToTab={setActiveTab}
    >
    <div className={cn("flex h-screen", colors.bgPrimary)}>
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Header - Uses product card_color if set */}
        <div 
          className={cn(
            "px-6 py-4 border-b flex items-center", 
            colors.borderPrimary
          )}
          style={{ 
            backgroundColor: product.card_color || undefined,
            ...(product.card_color ? {} : {})
          }}
        >
          {/* Free product avatar - inline in header */}
          {isFreeProduct && <div className="mr-4 flex-shrink-0"><FreeProductAvatarMenu /></div>}
          {/* Product Info - fixed width (same as settings side for centering) */}
          <div className="w-[200px] flex-shrink-0">
            <h1 
              className={cn("text-xl font-semibold", colors.textPrimary)}
              style={product.card_color ? { color: getContrastColor(product.card_color) } : undefined}
            >
              {product.name}
            </h1>
            <p 
              className={cn("text-sm", colors.textMuted)}
              style={product.card_color ? { color: getContrastColor(product.card_color), opacity: 0.8 } : undefined}
            >
              {epics.filter(e => e.status !== 'archived').length} epics · {allFeatures.length} features
            </p>
          </div>
          
          {/* Centered Global Search - only for non-collaborators */}
          {!isCollaborator && (
          <div className="flex-1 flex justify-center">
            <GlobalSearch 
              productId={productId}
              className="w-full max-w-lg"
              onSelectResult={(result) => {
              // Navigate to the selected entity
              const tabMap = {
                persona: 'personas',
                goal: 'goals',
                initiative: 'initiatives',
                epic: 'epics',
                feature: 'features',
                file: 'features' // Files are attached to features
              };
              const tab = tabMap[result.type] || 'features';
              setActiveTab(tab);
              
              // Set navigation target for the tab to handle
              setSearchNavTarget(result);
              
              // Handle features and epics which have top-level state
              if (result.type === 'feature' || result.type === 'file') {
                featuresApi.get(result.type === 'file' ? result.parent_id : result.id).then(res => {
                  if (res.data) {
                    setSelectedFeature(res.data);
                    // Set epic context
                    const featureEpic = epics.find(e => e.epic_id === res.data.epic_id);
                    if (featureEpic) {
                      featuresApi.list(featureEpic.epic_id).then(featRes => {
                        setSelectedEpicContext({ epic: featureEpic, siblingFeatures: featRes.data || [] });
                      }).catch(() => {
                        setSelectedEpicContext({ epic: featureEpic, siblingFeatures: [] });
                      });
                    }
                  }
                }).catch(console.error);
              } else if (result.type === 'epic') {
                const epic = epics.find(e => e.epic_id === result.id);
                if (epic) setSelectedEpic(epic);
              }
            }}
          />
          </div>
          )}
          
          {/* Settings Button - fixed width to match left side for centering */}
          <div className={cn("flex-shrink-0 flex justify-end items-center gap-2", !isCollaborator && "w-[200px]")}>
            {/* Token Balance Pill — Free Tier Only */}
            {isFreeProduct && (
              <button
                onClick={() => setSearchParams({ tab: 'upgrade' })}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-500/15 border border-amber-500/30 hover:bg-amber-500/25 transition-colors cursor-pointer"
                data-testid="token-balance-pill"
                title="AI Token Balance — Click to buy more"
              >
                <Coins className="w-3.5 h-3.5 text-amber-400" />
                <span className="text-xs font-semibold text-amber-400">
                  {formatTokensShort(freeTokenBalance)}
                </span>
              </button>
            )}
            {canCrudProducts && (
              <Button 
                variant="outline" 
                className={cn("border", product.card_color ? "border-white/30 hover:bg-white/10" : colors.borderPrimary)}
                style={product.card_color ? { color: getContrastColor(product.card_color) } : undefined}
                onClick={() => setShowProductSettings(true)}
              >
                <Settings className="w-4 h-4 mr-2" />
                Settings
              </Button>
            )}
          </div>
        </div>
      
      {/* Tab Navigation - Pill Style */}
      <div className={cn("px-6 py-3 flex justify-center", colors.bgSecondary)}>
        <div className={cn("inline-flex items-center gap-1 p-1 rounded-lg border", colors.bgCard, colors.borderPrimary)}>
          {(() => {
            // Get navigation settings (from product or defaults)
            const navSettings = product?.navigation_settings || {};
            
            // Filter tabs based on role, navigation settings AND feature flags
            const visibleTabs = TABS.filter(tab => {
              // Free-only tabs (like Upgrade) only appear for free product users
              if (tab.freeOnly && !isFreeProduct) return false;
              // Non-free tabs: hide for free users if they have a feature flag that's off
              if (!tab.freeOnly) {
                // First check role-based access
                // Collaborators only see specific tabs
                if (!hasFullTabAccess && !COLLABORATOR_ALLOWED_TABS.includes(tab.id)) {
                  return false;
                }
              }
              
              // Then check feature flag
              const featureFlagId = TAB_FEATURE_FLAGS[tab.id];
              if (featureFlagId && !isEnabled(featureFlagId)) {
                return false;
              }
              
              // Then check product-level navigation settings
              // Ideation module tabs
              if (['ideation', 'requests'].includes(tab.id)) {
                return navSettings.ideation_enabled !== false;
              }
              // Market module tabs
              if (tab.id === 'market') {
                return navSettings.market_enabled !== false;
              }
              // Planning module tabs
              if (['releases', 'kanban'].includes(tab.id)) {
                return navSettings.planning_enabled !== false;
              }
              // Build module tabs (Architecture/Architect, Delivery)
              if (['architect', 'delivery'].includes(tab.id)) {
                return navSettings.build_enabled !== false;
              }
              // Core tabs are always visible (if feature flag allows)
              return true;
            });
            
            return visibleTabs.map((tab, index) => (
              <React.Fragment key={tab.id}>
                {tab.id === 'upgrade' && index > 0 && (
                  <div className="flex items-center mx-1">
                    <div className={cn("h-5 w-px bg-gray-300 dark:bg-gray-600")} />
                  </div>
                )}
                <button
                  data-testid={`tab-${tab.id}`}
                  onClick={() => setActiveTab(tab.id)}
                  className={cn(
                    "flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-all",
                    tab.id === 'upgrade'
                      ? activeTab === 'upgrade'
                        ? "bg-amber-500 text-black shadow-sm"
                        : "text-amber-400 hover:bg-amber-500/10"
                      : activeTab === tab.id
                        ? "bg-blue-600 text-white shadow-sm"
                        : cn(colors.textMuted, "hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50")
                  )}
                >
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
                </button>
                {/* Visual Separator between groups */}
                {tab.separator && index < visibleTabs.length - 1 && (
                  <div className="flex items-center mx-1">
                    <div className={cn("h-5 w-px bg-gray-300 dark:bg-gray-600")} />
                  </div>
                )}
              </React.Fragment>
            ));
          })()}
        </div>
      </div>
      
      {/* Tab Content */}
      <div className={cn("flex-1 overflow-hidden", colors.bgCard)}>
        {activeTab === 'ideation' && (
          <IdeationTab productId={productId} product={product} canEdit={canCrudProducts} canUseAI={canUseAI} searchParams={searchParams} setSearchParams={setSearchParams} />
        )}
        {activeTab === 'requests' && (
          <RequestsTab product={product} canEdit={canCrudProducts} markStale={markStale} />
        )}
        {activeTab === 'strategy' && (
          <StrategyTab product={product} productId={productId} canEdit={canCrudProducts} canUseAI={canUseAI} refresh={refresh} />
        )}
        {activeTab === 'market' && (
          <MarketTab product={product} productId={productId} canEdit={canCrudProducts} canUseAI={canUseAI} refresh={refresh} markStale={markStale} />
        )}
        {activeTab === 'personas' && (
          <PersonasTab personas={personas} product={product} productId={productId} canEdit={canCrudProducts} canUseAI={canUseAI} refresh={refresh} allFeatures={allFeatures} allEpics={epics} searchNavTarget={searchNavTarget} onSearchNavHandled={() => setSearchNavTarget(null)} markStale={markStale} getCascadeTargets={getCascadeTargets} />
        )}
        {activeTab === 'goals' && (
          <GoalsTab goals={goals} productId={productId} canEdit={canCrudProducts} canUseAI={canUseAI} refresh={refresh} productContext={{ product_id: product.product_id, name: product.name, vision: product.high_level_vision }} searchNavTarget={searchNavTarget} onSearchNavHandled={() => setSearchNavTarget(null)} initiatives={initiatives} markStale={markStale} getCascadeTargets={getCascadeTargets} />
        )}
        {activeTab === 'initiatives' && (
          <InitiativesTab productId={productId} goals={goals} initiatives={initiatives} personas={personas} canEdit={canCrudProducts} canUseAI={canUseAI} refresh={refresh} searchNavTarget={searchNavTarget} onSearchNavHandled={() => setSearchNavTarget(null)} markStale={markStale} getCascadeTargets={getCascadeTargets} productContext={{ product_id: product.product_id, name: product.name, vision: product.high_level_vision, initiatives }} />
        )}
        {activeTab === 'epics' && (
          <EpicsTab epics={epics} goals={goals} initiatives={initiatives} productId={productId} canEdit={canCrudProducts} canUseAI={canUseAI} refresh={refresh} onSelectFeature={handleSelectFeature} onSelectEpic={setSelectedEpic} allFeatures={allFeatures} markStale={markStale} getCascadeTargets={getCascadeTargets} />
        )}
        {activeTab === 'features' && (
          <FeaturesTab features={allFeatures} epics={epics} productId={productId} canEdit={canCrudProducts} canUseAI={canUseAI} onSelectFeature={handleSelectFeature} refresh={refresh} releases={releases} personas={personas} teamMembers={teamMembers} markStale={markStale} getCascadeTargets={getCascadeTargets} onNavigateToTab={setActiveTab} />
        )}
        {activeTab === 'releases' && (
          <ReleasesTab features={allFeatures} epics={epics} productId={productId} canEdit={canCrudProducts} canUseAI={canUseAI} onRefresh={refreshData} personas={personas} teamMembers={teamMembers} goals={goals} productContext={{ product_id: product.product_id, name: product.name, vision: product.high_level_vision }} markStale={markStale} getCascadeTargets={getCascadeTargets} />
        )}
        {activeTab === 'kanban' && (
          <div className="flex flex-row h-full">
            <div className="flex-1 h-full overflow-hidden">
              <KanbanBoard
                selectedNode={null}
                productId={product.product_id}
                goals={goals}
                personas={personas}
                epics={epics}
                onRefresh={refreshData}
                onSelectFeature={handleSelectFeature}
              />
            </div>
            {canUseAI && <CollectionCopilotDrawer 
              context="kanban" 
              contextId={product.product_id} 
              title="Kanban Assistant"
              onRefresh={refreshData}
             
            />}
          </div>
        )}
        {activeTab === 'architect' && (
          <ArchitectTab 
            productId={productId} 
            product={product} 
            canEdit={canCrudProducts} 
            canUseAI={canUseAI} 
            onRefresh={refreshData}
            isOwner={isOwner}
          />
        )}
        {activeTab === 'delivery' && (
          <DeliveryTab 
            productId={productId} 
            product={product} 
            canEdit={canCrudProducts} 
            canUseAI={canUseAI} 
            onRefresh={refreshData}
            isOwner={isOwner}
          />
        )}
        {activeTab === 'upgrade' && isFreeProduct && (
          <div className="h-full overflow-y-auto">
            <UpgradeTab
              productId={productId}
              colors={colors}
            />
          </div>
        )}
      </div>
      
        {/* Feature Detail Modal */}
        <FeatureDetailModal
          isOpen={!!selectedFeature}
          feature={selectedFeature}
          onSave={(savedFeature) => {
            // Entity-level refresh with saved data (like Epic pattern)
            if (savedFeature?.feature_id) {
              refresh('entity', 'features', savedFeature);
            } else {
              refresh('collection', 'features');
            }
          }}
          onDelete={() => {
            // Collection-level refresh on delete
            setSelectedFeature(null);
            setSelectedEpicContext({ epic: null, siblingFeatures: [] });
            refresh('collection', 'features');
          }}
          onSplitComplete={() => {
            // Collection-level refresh on split (deletes 1, creates 2+)
            setSelectedFeature(null);
            setSelectedEpicContext({ epic: null, siblingFeatures: [] });
            refresh('collection', 'features');
          }}
          onClose={() => {
            setSelectedFeature(null);
            setSelectedEpicContext({ epic: null, siblingFeatures: [] });
          }}
          personas={personas}
          goals={goals}
          releases={releases}
          teamMembers={teamMembers}
          statuses={kanbanColumns}
          productContext={{
            product_id: product.product_id,
            name: product.name,
            vision: product.high_level_vision
          }}
          epicContext={{...selectedEpicContext, epics}}
        />

        {/* Epic Detail Modal */}
        <EpicDetailModal
          isOpen={!!selectedEpic}
          epic={selectedEpic}
          onSave={(savedEpic, changeInfo = {}) => {
            if (savedEpic?.epic_id) {
              refresh('entity', 'epics', savedEpic);
            } else if (changeInfo.dirtyCollections) {
              // Deferred stale flush from child modal saves — mark affected collections
              markStale(changeInfo.dirtyCollections);
              if (changeInfo.entityId) {
                refresh('entity', 'epics', changeInfo.entityId);
              }
            } else {
              refresh('collection', 'epics');
            }
            // Handle cascading updates for KPI subscription changes
            if (changeInfo.kpisChanged) {
              markStale(getCascadeTargets('epics', 'kpiChange'));
            }
            // Refresh features collection when cross-entity child updates were applied
            if (changeInfo.childUpdates?.success?.length > 0) {
              markStale(['features']);
            }
          }}
          onSaveAndClose={(savedEpic, changeInfo = {}) => {
            setSelectedEpic(null);
            if (savedEpic?.epic_id) {
              refresh('entity', 'epics', savedEpic);
            } else if (changeInfo.dirtyCollections) {
              markStale(changeInfo.dirtyCollections);
              if (changeInfo.entityId) {
                refresh('entity', 'epics', changeInfo.entityId);
              }
            } else {
              refresh('collection', 'epics');
            }
            // Handle cascading updates for KPI subscription changes
            if (changeInfo.kpisChanged) {
              markStale(getCascadeTargets('epics', 'kpiChange'));
            }
            // Refresh features collection when cross-entity child updates were applied
            if (changeInfo.childUpdates?.success?.length > 0) {
              markStale(['features']);
            }
          }}
          onClose={() => setSelectedEpic(null)}
          onDelete={() => {
            setSelectedEpic(null);
            refresh('collection', 'epics');
            // Epic deletion affects features
            markStale(getCascadeTargets('epics', 'delete'));
          }}
          onFeatureChange={() => {
            // Refresh features when features are added/deleted from epic
            refresh('collection', 'features');
          }}
          onKpiChange={() => {
            // KPI subscriptions changed - cascade to features
            markStale(getCascadeTargets('epics', 'kpiChange'));
          }}
          productContext={{
            product_id: product.product_id,
            name: product.name,
            vision: product.high_level_vision,
            initiatives: initiatives
          }}
          personas={personas}
          onSelectFeature={async (feature) => {
            // Open feature modal OVER epic modal (stacked)
            setStackedFeature(feature);
            // Set context for the stacked feature
            const epic = epics.find(e => e.epic_id === feature.epic_id);
            if (epic) {
              try {
                const res = await featuresApi.list(epic.epic_id);
                setStackedEpicContext({ epic, siblingFeatures: res.data || [] });
              } catch {
                setStackedEpicContext({ epic, siblingFeatures: [] });
              }
            }
          }}
          onChildFeatureUpdated={(patchFn) => { epicFeaturePatchRef.current = patchFn; }}
        />

        {/* Stacked Feature Modal (opens over Epic Modal) */}
        {stackedFeature && selectedEpic && (
          <div className="fixed inset-0 z-[60]">
            <FeatureDetailModal
              feature={stackedFeature}
              isOpen={!!stackedFeature}
              suppressChildModals={true}
              onSave={async (savedFeature) => {
                // Entity-level refresh with saved data
                if (savedFeature?.feature_id) {
                  refresh('entity', 'features', savedFeature);
                  // Patch the feature in the Epic modal's local state (targeted, no full reload)
                  if (epicFeaturePatchRef.current) epicFeaturePatchRef.current(savedFeature);
                } else {
                  await refresh('collection', 'features');
                }
                // Reload the epic's sibling features for context
                if (selectedEpic) {
                  try {
                    const res = await featuresApi.list(selectedEpic.epic_id);
                    setStackedEpicContext({ epic: selectedEpic, siblingFeatures: res.data || [] });
                  } catch {
                    // Ignore
                  }
                }
              }}
              onDelete={async () => {
                // Collection-level refresh on delete
                setStackedFeature(null);
                setStackedEpicContext({ epic: null, siblingFeatures: [] });
                await refresh('collection', 'features');
              }}
              onSplitComplete={async () => {
                // Collection-level refresh on split
                setStackedFeature(null);
                setStackedEpicContext({ epic: null, siblingFeatures: [] });
                await refresh('collection', 'features');
              }}
              onClose={() => {
                setStackedFeature(null);
                setStackedEpicContext({ epic: null, siblingFeatures: [] });
              }}
              personas={personas}
              releases={releases}
              productContext={{
                product_id: product.product_id,
                name: product.name,
                vision: product.high_level_vision
              }}
              epicContext={stackedEpicContext}
            />
          </div>
        )}
        
        {/* Product Settings Modal */}
        <ProductSettingsModal
          open={showProductSettings}
          onClose={() => setShowProductSettings(false)}
          productId={productId}
          product={product}
          teamMembers={teamMembers}
          onProductUpdate={refreshData}
        />
        
        {/* Entity Modals from Co-pilot links */}
        <EntityModals
          productId={productId}
          refresh={refresh}
          markStale={markStale}
          personas={personas}
          epics={epics}
          goals={goals}
          allFeatures={allFeatures}
          productContext={{ ...product, initiatives }}
          onSaveSegment={handleSaveSegment}
          onDeleteSegment={handleDeleteSegment}
          canEdit={canCrudProducts}
        />
      </div>
    </div>
    </EntityModalProvider>
  );
};


// ==================== IDEATION TAB ====================
const IdeationTab = ({ productId, product, canEdit, canUseAI, searchParams, setSearchParams }) => {
  const { getThemeColors } = useSafeMode();
  const { isEnabled } = useFeatureFlags();
  const colors = getThemeColors();
  
  // Get space from URL params
  const activeSpace = searchParams.get('space');
  
  // Navigate to a space using URL params (preserves tab=ideation)
  const navigateToSpace = (spaceId) => {
    setSearchParams({ tab: 'ideation', space: spaceId });
  };
  
  // Navigate back to main ideation view
  const navigateBack = () => {
    setSearchParams({ tab: 'ideation' });
  };

  // Main ideation tools - now use URL navigation, filtered by feature flags
  const ideationTools = [
    isEnabled(FEATURE_IDS.IDEATION_STACKRANKING) && {
      id: 'stack-ranking',
      name: 'Stack Ranking',
      description: 'Prioritize features, ideas, and problems with your team',
      icon: BarChart3,
      color: 'from-amber-500 to-orange-600',
      action: () => navigateToSpace('stack-ranking')
    },
    isEnabled(FEATURE_IDS.ORG_COLLABORATION) && {
      id: 'collaboration',
      name: 'Team Collaboration',
      description: 'Real-time whiteboard for brainstorming with your team',
      icon: Users,
      color: 'from-indigo-500 to-purple-600',
      action: () => navigateToSpace('collaboration')
    }
  ].filter(Boolean);

  // Customer Feedback tools
  const customerFeedbackTools = [
    isEnabled(FEATURE_IDS.IDEATION_INTERVIEWS) && {
      id: 'support-forms',
      name: 'Feedback Forms',
      description: 'Create shareable forms to collect bugs, features, and questions',
      icon: MessageSquareText,
      color: 'from-emerald-500 to-teal-600',
      action: () => navigateToSpace('support-forms')
    }
  ].filter(Boolean);

  // Deep Research categories - now navigate to spaces instead of opening modals
  const deepResearchCards = [
    isEnabled(FEATURE_IDS.IDEATION_RESEARCH) && {
      id: 'market-analysis',
      name: 'Market Analysis',
      description: 'Research market trends, size, and opportunities',
      icon: TrendingUp,
      color: 'from-blue-500 to-cyan-500',
      apiMethod: 'marketAnalysis'
    },
    isEnabled(FEATURE_IDS.IDEATION_RESEARCH) && {
      id: 'competitor-intel',
      name: 'Competitor Intelligence',
      description: 'Track competitor features, pricing, and strategies',
      icon: Target,
      color: 'from-red-500 to-pink-500',
      apiMethod: 'competitorIntel'
    },
    isEnabled(FEATURE_IDS.IDEATION_RESEARCH) && {
      id: 'tech-landscape',
      name: 'Technology Landscape',
      description: 'Explore emerging technologies and integrations',
      icon: Layers,
      color: 'from-purple-500 to-violet-500',
      apiMethod: 'techLandscape'
    }
  ].filter(Boolean);

  // Render collaboration spaces list
  if (activeSpace === 'collaboration') {
    const CollaborationSpaceList = React.lazy(() => import('../components/collaboration/CollaborationSpaceList'));
    return (
      <React.Suspense fallback={
        <div className="flex items-center justify-center h-full">
          <Loader2 className={cn("w-8 h-8 animate-spin", colors.textAccent)} />
        </div>
      }>
        <CollaborationSpaceList 
          productId={productId} 
          onBack={navigateBack} 
          canEdit={canEdit}
        />
      </React.Suspense>
    );
  }

  // Render stack ranking spaces list
  if (activeSpace === 'stack-ranking') {
    const StackRankingSpaceList = React.lazy(() => import('../components/stackranking/StackRankingSpaceList'));
    return (
      <React.Suspense fallback={
        <div className="flex items-center justify-center h-full">
          <Loader2 className={cn("w-8 h-8 animate-spin", colors.textAccent)} />
        </div>
      }>
        <StackRankingSpaceList 
          productId={productId} 
          onBack={navigateBack} 
          canEdit={canEdit}
        />
      </React.Suspense>
    );
  }

  // Render support forms space
  if (activeSpace === 'support-forms') {
    const SupportFormSpaceList = React.lazy(() => import('../components/support/SupportFormSpaceList'));
    return (
      <React.Suspense fallback={
        <div className="flex items-center justify-center h-full">
          <Loader2 className={cn("w-8 h-8 animate-spin", colors.textAccent)} />
        </div>
      }>
        <SupportFormSpaceList 
          productId={productId} 
          onBack={navigateBack} 
          canEdit={canEdit}
        />
      </React.Suspense>
    );
  }

  // Render Deep Research space pages
  if (activeSpace === 'market-analysis' || activeSpace === 'competitor-intel' || activeSpace === 'tech-landscape') {
    const DeepResearchSpaceComponent = React.lazy(() => import('../components/research/DeepResearchSpace'));
    return (
      <React.Suspense fallback={
        <div className="flex items-center justify-center h-full">
          <Loader2 className={cn("w-8 h-8 animate-spin", colors.textAccent)} />
        </div>
      }>
        <DeepResearchSpaceComponent
          productId={productId}
          product={product}
          researchType={deepResearchCards.find(c => c.id === activeSpace)}
          onBack={navigateBack}
          canEdit={canEdit}
          searchParams={searchParams}
          setSearchParams={setSearchParams}
        />
      </React.Suspense>
    );
  }

  // Main Ideation View
  return (
    <div className="flex flex-row h-full">
      {/* Main Content */}
      <div className="flex-1 p-6 overflow-y-auto">
        <div className="max-w-6xl mx-auto">
          {/* Ideation Tools Section - hide header if no tools */}
          {ideationTools.length > 0 && (
          <div className="mb-10">
            <h2 className={cn("text-2xl font-semibold mb-2", colors.textPrimary)}>Ideation Tools</h2>
            <p className={cn("text-sm mb-6", colors.textSecondary)}>
              Collaborative tools for prioritizing and brainstorming
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {ideationTools.map(tool => (
                <Card 
                  key={tool.id}
                  className={cn(
                    "p-6 border cursor-pointer transition-all hover:shadow-lg",
                    colors.bgCard, colors.borderPrimary
                  )}
                  onClick={tool.action}
                >
                  <div className="flex items-start gap-4">
                    <div className={cn("w-12 h-12 rounded-xl flex items-center justify-center bg-gradient-to-br", tool.color)}>
                      <tool.icon className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className={cn("text-lg font-semibold", colors.textPrimary)}>{tool.name}</h3>
                      <p className={cn("text-sm mt-1", colors.textSecondary)}>{tool.description}</p>
                    </div>
                    <ChevronRight className={cn("w-5 h-5", colors.textMuted)} />
                  </div>
                </Card>
              ))}
            </div>
          </div>
          )}

          {/* Customer Feedback Section - hide header if no tools */}
          {customerFeedbackTools.length > 0 && (
          <div className="mb-10">
            <h2 className={cn("text-2xl font-semibold mb-2", colors.textPrimary)}>Customer Feedback</h2>
            <p className={cn("text-sm mb-6", colors.textSecondary)}>
              Collect and manage feedback from your users
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {customerFeedbackTools.map(tool => (
                <Card 
                  key={tool.id}
                  className={cn(
                    "p-6 border cursor-pointer transition-all hover:shadow-lg",
                    colors.bgCard, colors.borderPrimary
                  )}
                  onClick={tool.action}
                  data-testid="support-forms-card"
                >
                  <div className="flex items-start gap-4">
                    <div className={cn("w-12 h-12 rounded-xl flex items-center justify-center bg-gradient-to-br", tool.color)}>
                      <tool.icon className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className={cn("text-lg font-semibold", colors.textPrimary)}>{tool.name}</h3>
                      <p className={cn("text-sm mt-1", colors.textSecondary)}>{tool.description}</p>
                    </div>
                    <ChevronRight className={cn("w-5 h-5", colors.textMuted)} />
                  </div>
                </Card>
              ))}
            </div>
          </div>
          )}

          {/* Deep Research Section - hide header if no cards */}
          {deepResearchCards.length > 0 && (
          <div>
            <h2 className={cn("text-2xl font-semibold mb-2", colors.textPrimary)}>Deep Research</h2>
            <p className={cn("text-sm mb-6", colors.textSecondary)}>
              AI-powered research tools for product discovery
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {deepResearchCards.map(card => (
                <Card 
                  key={card.id}
                  onClick={() => navigateToSpace(card.id)}
                  className={cn(
                    "p-5 border transition-all cursor-pointer hover:shadow-lg hover:scale-[1.02]",
                    colors.bgCard, colors.borderPrimary
                  )}
                >
                  <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center bg-gradient-to-br mb-3", card.color)}>
                    <card.icon className="w-5 h-5 text-white" />
                  </div>
                  <h3 className={cn("font-semibold", colors.textPrimary)}>{card.name}</h3>
                  <p className={cn("text-sm mt-1", colors.textSecondary)}>{card.description}</p>
                  <div className="flex items-center gap-1 mt-3">
                    <ChevronRight className={cn("w-4 h-4", colors.textMuted)} />
                    <span className={cn("text-xs", colors.textMuted)}>View Reports</span>
                  </div>
                </Card>
              ))}
            </div>
          </div>
          )}
        </div>
      </div>
    </div>
  );
};


// ==================== SHARED EDITABLE FIELD COMPONENT ====================
// Shared editable field for inline editing
const EditableFieldInline = ({ 
  field, 
  value, 
  placeholder, 
  multiline = false, 
  large = false, 
  isEditing, 
  editValue, 
  setEditValue, 
  onSave, 
  onCancel, 
  canEdit, 
  onStartEdit, 
  saving, 
  colors 
}) => {
  if (isEditing) {
    return (
      <div className="flex flex-col gap-2">
        {multiline ? (
          <Textarea
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            placeholder={placeholder}
            className={cn(large ? "min-h-[150px]" : "min-h-[80px]", colors.bgInput, colors.borderPrimary)}
            autoFocus
          />
        ) : (
          <Input
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            placeholder={placeholder}
            className={cn(colors.bgInput, colors.borderPrimary)}
            autoFocus
          />
        )}
        <div className="flex gap-2">
          <Button size="sm" onClick={() => onSave(field)} disabled={saving}>
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
            Save
          </Button>
          <Button size="sm" variant="ghost" onClick={onCancel}>
            <X className="w-3 h-3" /> Cancel
          </Button>
        </div>
      </div>
    );
  }
  
  // Handle object values by converting to string representation
  const displayValue = value && typeof value === 'object' 
    ? JSON.stringify(value, null, 2) 
    : value;
  
  return (
    <div className="group relative">
      <p className={cn(displayValue ? colors.textPrimary : colors.textMuted, "pr-8", large && "text-lg leading-relaxed", "whitespace-pre-wrap")}>
        {displayValue || placeholder}
      </p>
      {canEdit && (
        <button
          onClick={() => onStartEdit(field, displayValue)}
          className={cn("absolute top-0 right-0 opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded", colors.bgHover)}
        >
          <Pencil className="w-4 h-4 text-gray-400" />
        </button>
      )}
    </div>
  );
};

// Markdown editable field
const EditableMarkdownFieldInline = ({ 
  field, 
  value, 
  placeholder,
  label,
  onSave,
  canEdit, 
  saving, 
  colors,
  onRefresh
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleSave = async (fieldName, newValue) => {
    await onSave(fieldName, newValue);
    if (onRefresh) {
      await onRefresh();
    }
    setIsModalOpen(false);
  };
  
  // Handle object values by converting to JSON string or showing placeholder
  const displayValue = value && typeof value === 'object' 
    ? JSON.stringify(value, null, 2) 
    : value;
  
  return (
    <>
      <div className="group relative">
        {displayValue ? (
          <div className="prose prose-sm dark:prose-invert max-w-none pr-8">
            <ReactMarkdown remarkPlugins={[remarkBreaks]}>{displayValue}</ReactMarkdown>
          </div>
        ) : (
          <p className={cn(colors.textMuted, "pr-8")}>{placeholder}</p>
        )}
        {canEdit && (
          <button
            onClick={() => setIsModalOpen(true)}
            className={cn("absolute top-0 right-0 opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded", colors.bgHover)}
            data-testid={`edit-${field}-btn`}
          >
            <Pencil className="w-4 h-4 text-gray-400" />
          </button>
        )}
      </div>
      
      <MarkdownFieldEditModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSave}
        label={label || field.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
        fieldName={field}
        value={value}
        placeholder={placeholder}
      />
    </>
  );
};

// Market Trend Card - defined at module level to survive parent re-renders
const MarketTrendCard = ({ 
  fieldName, 
  value, 
  placeholder, 
  label, 
  icon: Icon, 
  iconBgClass, 
  iconClass,
  onSave,
  canEdit,
  colors
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleSave = async (name, newValue) => {
    await onSave(fieldName, newValue);
    setIsModalOpen(false);
  };

  // Handle object values by converting to JSON string
  const displayValue = value && typeof value === 'object' 
    ? JSON.stringify(value, null, 2) 
    : value;

  return (
    <Card className={cn("p-4 border group relative", colors.bgCard, colors.borderPrimary)}>
      <div className="flex items-center gap-2 mb-3">
        <div className={cn("w-8 h-8 rounded-full flex items-center justify-center", iconBgClass)}>
          <Icon className={cn("w-4 h-4", iconClass)} />
        </div>
        <h3 className={cn("font-semibold", colors.textPrimary)}>{label}</h3>
      </div>
      
      {displayValue ? (
        <div className="prose prose-sm dark:prose-invert max-w-none">
          <ReactMarkdown remarkPlugins={[remarkBreaks]}>{displayValue}</ReactMarkdown>
        </div>
      ) : (
        <p className={cn("text-sm", colors.textMuted)}>{placeholder}</p>
      )}

      {canEdit && (
        <button
          onClick={() => setIsModalOpen(true)}
          className={cn("absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded", colors.bgHover)}
          data-testid={`edit-${fieldName}-btn`}
        >
          <Pencil className="w-4 h-4 text-gray-400" />
        </button>
      )}
      
      <MarkdownFieldEditModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSave}
        label={label}
        fieldName={fieldName}
        value={value}
        placeholder={placeholder}
      />
    </Card>
  );
};


// ==================== STRATEGY TAB ====================
const StrategyTab = ({ product, productId, canEdit, canUseAI, refresh }) => {
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();
  
  // Inline editing states
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);
  
  // Handle inline edit save
  const handleSaveField = async (field) => {
    if (!editValue.trim() && editValue !== '') return;
    setSaving(true);
    try {
      await productsApi.update(productId, { [field]: editValue });
      toast.success('Updated successfully');
      await refresh('product');  // Refresh product data
    } catch (err) {
      toast.error('Failed to update');
    } finally {
      setSaving(false);
      setEditingField(null);
    }
  };

  // Handle direct field save (for modal-based editing)
  const handleDirectSaveField = async (field, value) => {
    setSaving(true);
    try {
      await productsApi.update(productId, { [field]: value });
      toast.success('Updated successfully');
      await refresh('product');
    } catch (err) {
      toast.error('Failed to update');
      throw err;
    } finally {
      setSaving(false);
    }
  };
  
  // Start editing a field
  const startEdit = (field, value) => {
    setEditingField(field);
    setEditValue(value || '');
  };
  
  // Cancel editing
  const cancelEdit = () => setEditingField(null);
  
  // Render editable field
  const renderEditableField = (field, value, placeholder, multiline = false, large = false) => (
    <EditableFieldInline
      field={field}
      value={value}
      placeholder={placeholder}
      multiline={multiline}
      large={large}
      isEditing={editingField === field}
      editValue={editValue}
      setEditValue={setEditValue}
      onSave={handleSaveField}
      onCancel={cancelEdit}
      canEdit={canEdit}
      onStartEdit={startEdit}
      saving={saving}
      colors={colors}
    />
  );
  
  // Render markdown editable field with modal
  const renderMarkdownField = (field, value, placeholder, label) => (
    <EditableMarkdownFieldInline
      field={field}
      value={value}
      placeholder={placeholder}
      label={label}
      onSave={handleDirectSaveField}
      canEdit={canEdit}
      saving={saving}
      colors={colors}
      onRefresh={() => refresh('product')}
    />
  );

  // Dismiss explainer state
  const [explainerDismissed, setExplainerDismissed] = useState(false);
  
  const isStrategyEmpty = !product.high_level_vision &&
    !product.value_proposition &&
    !product.target_audience &&
    !product.key_differentiators &&
    !product.strategic_pillars &&
    !product.revenue_model &&
    !product.pricing_strategy &&
    !product.north_star_metric;
  
  const showExplainer = isStrategyEmpty && !explainerDismissed;

  return (
    <div className="flex flex-row h-full">
      {/* Main Content - Strategy Report */}
      <div className="flex-1 p-6 overflow-y-auto">
        <div className="max-w-4xl mx-auto space-y-8">
          {showExplainer ? (
          <div className={cn("text-center py-12 px-6 rounded-lg border", colors.bgTertiary, colors.borderPrimary)} data-testid="strategy-empty-state">
            <Compass className={cn("w-12 h-12 mx-auto mb-4", colors.textMuted)} />
            <h3 className={cn("text-lg font-semibold mb-4", colors.textPrimary)}>No strategy defined yet</h3>
            <div className={cn("text-sm mb-6 max-w-2xl mx-auto text-left space-y-4", colors.textMuted)}>
              <p>
                <strong className={colors.textPrimary}>Codify Your North Star</strong>
                <br />
                Moving from reactive feature building to proactive product leadership requires a documented internal compass. In StackRanked, the Strategy tab defines the high-level logic of your business: your aspirational future state, your specific method of market differentiation, and the economic model that sustains your growth. By formalizing these pillars, you ensure that every tactical decision is anchored in a cohesive long-term plan.
              </p>
              <p>
                <strong className={colors.textPrimary}>The Role of Strategy in the StackRanked Process</strong>
                <br />
                Strategy serves as the primary filter for your AI Co-Pilots. These fields provide the high-level &ldquo;rules of the game&rdquo; that the system uses to evaluate everything from Idea backlogs to Release cycles. When you define your North Star Metric and Strategic Pillars, your AI assistants can rigorously assess whether a proposed Feature or Epic actually moves the needle on your core objectives. This prevents &ldquo;strategy drift,&rdquo; where teams focus on high-effort tasks that do not align with the company&apos;s stated vision.
              </p>
              <p>
                <strong className={colors.textPrimary}>Aligning Daily Execution with Long-Term Value</strong>
                <br />
                By defining your value proposition and key differentiators here, you program the system to prioritize work that reinforces your unique market position. Every Strategic Pillar you add refines the weighting of your Goals and Initiatives, giving you the clarity needed to say &ldquo;no&rdquo; to distractions and &ldquo;yes&rdquo; to the specific bets that capture value. This is how you transform a list of tasks into a structured path toward your product&apos;s aspirational future.
              </p>
            </div>
            {canEdit && (
              <Button className={cn(colors.accentGradient)} onClick={() => setExplainerDismissed(true)} data-testid="strategy-get-started-btn">
                Get Started
              </Button>
            )}
          </div>
          ) : (
          <>
          {/* Section 1: Vision */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center", colors.bgTertiary)}>
                <Compass className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <h2 className={cn("text-xl font-semibold", colors.textPrimary)}>Vision</h2>
                <p className={cn("text-sm", colors.textMuted)}>The aspirational future state</p>
              </div>
            </div>
            <Card className={cn("p-6 border", colors.bgCard, colors.borderPrimary)}>
              {renderMarkdownField(
                "high_level_vision", 
                product.high_level_vision, 
                "Define your product vision - the north star that guides all decisions.",
                "Vision"
              )}
            </Card>
          </section>

          {/* Section 2: Positioning */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center", colors.bgTertiary)}>
                <Target className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <h2 className={cn("text-xl font-semibold", colors.textPrimary)}>Positioning</h2>
                <p className={cn("text-sm", colors.textMuted)}>How we differentiate in the market</p>
              </div>
            </div>
            <Card className={cn("p-6 border space-y-6", colors.bgCard, colors.borderPrimary)}>
              <div>
                <h3 className={cn("text-sm font-medium mb-2 flex items-center gap-2", colors.textMuted)}>
                  <span className="w-2 h-2 rounded-full bg-blue-500" />
                  Value Proposition
                </h3>
                {renderMarkdownField(
                  "value_proposition", 
                  product.value_proposition, 
                  "What core promise do you make to customers?",
                  "Value Proposition"
                )}
              </div>
              <div>
                <h3 className={cn("text-sm font-medium mb-2 flex items-center gap-2", colors.textMuted)}>
                  <span className="w-2 h-2 rounded-full bg-green-500" />
                  Target Audience
                </h3>
                {renderMarkdownField(
                  "target_audience", 
                  product.target_audience, 
                  "Who are you building this for?",
                  "Target Audience"
                )}
              </div>
              <div>
                <h3 className={cn("text-sm font-medium mb-2 flex items-center gap-2", colors.textMuted)}>
                  <span className="w-2 h-2 rounded-full bg-purple-500" />
                  Key Differentiators
                </h3>
                {renderMarkdownField(
                  "key_differentiators", 
                  product.key_differentiators, 
                  "What makes you unique?",
                  "Key Differentiators"
                )}
              </div>
            </Card>
          </section>

          {/* Section 3: Strategic Pillars */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center", colors.bgTertiary)}>
                <Layers className="w-5 h-5 text-orange-500" />
              </div>
              <div>
                <h2 className={cn("text-xl font-semibold", colors.textPrimary)}>Strategic Pillars</h2>
                <p className={cn("text-sm", colors.textMuted)}>The 3-5 strategic bets driving the product</p>
              </div>
            </div>
            <Card className={cn("p-6 border", colors.bgCard, colors.borderPrimary)}>
              {renderMarkdownField(
                "strategic_pillars", 
                product.strategic_pillars, 
                "Define your strategic pillars - the key bets that inform goals and initiatives.",
                "Strategic Pillars"
              )}
            </Card>
          </section>

          {/* Section 4: Business Model */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center", colors.bgTertiary)}>
                <DollarSign className="w-5 h-5 text-emerald-500" />
              </div>
              <div>
                <h2 className={cn("text-xl font-semibold", colors.textPrimary)}>Business Model</h2>
                <p className={cn("text-sm", colors.textMuted)}>How we capture value</p>
              </div>
            </div>
            <Card className={cn("p-6 border space-y-6", colors.bgCard, colors.borderPrimary)}>
              <div>
                <h3 className={cn("text-sm font-medium mb-2 flex items-center gap-2", colors.textMuted)}>
                  <span className="w-2 h-2 rounded-full bg-emerald-500" />
                  Revenue Model
                </h3>
                {renderMarkdownField(
                  "revenue_model", 
                  product.revenue_model, 
                  "How do you make money?",
                  "Revenue Model"
                )}
              </div>
              <div>
                <h3 className={cn("text-sm font-medium mb-2 flex items-center gap-2", colors.textMuted)}>
                  <span className="w-2 h-2 rounded-full bg-teal-500" />
                  Pricing Strategy
                </h3>
                {renderMarkdownField(
                  "pricing_strategy", 
                  product.pricing_strategy, 
                  "Premium, competitive, or penetration?",
                  "Pricing Strategy"
                )}
              </div>
              <div>
                <h3 className={cn("text-sm font-medium mb-2 flex items-center gap-2", colors.textMuted)}>
                  <span className="w-2 h-2 rounded-full bg-cyan-500" />
                  North Star Metric
                </h3>
                {renderMarkdownField(
                  "north_star_metric", 
                  product.north_star_metric, 
                  "What single metric defines success?",
                  "North Star Metric"
                )}
              </div>
            </Card>
          </section>
          </>
          )}
        </div>
      </div>
      
      {/* AI Side Drawer */}
      {canUseAI && !showExplainer && <CollectionCopilotDrawer 
        context="strategy" 
        contextId={productId} 
        title="Strategy Advisor"
        onRefresh={() => refresh('product')}
       
      />}
    </div>
  );
};

// ==================== MARKET TAB ====================
const MarketTab = ({ product, productId, canEdit, canUseAI, refresh, markStale }) => {
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();
  
  // Inline editing states
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);
  
  // Competitor modal state
  const [showCompetitorModal, setShowCompetitorModal] = useState(false);
  const [editingCompetitor, setEditingCompetitor] = useState(null);
  const [competitorToDelete, setCompetitorToDelete] = useState(null);
  const [deletedCompetitors, setDeletedCompetitors] = useState([]); // For optimistic delete
  const [competitorForm, setCompetitorForm] = useState({
    name: '', description: '', strengths: '', weaknesses: '', url: ''
  });
  
  // Market segment modal state
  const [segmentDialogOpen, setSegmentDialogOpen] = useState(false);
  const [editingSegment, setEditingSegment] = useState(null);
  const [segmentToDelete, setSegmentToDelete] = useState(null);
  const [deletedSegments, setDeletedSegments] = useState([]); // For optimistic delete
  
  // Local handlers for segment modal (wrapper around parent handlers)
  const handleSaveSegmentLocal = async (segmentData) => {
    const segment = segmentData || editingSegment;
    if (!segment?.title?.trim()) {
      toast.error('Segment title is required');
      return;
    }
    
    try {
      const currentSegments = product.market_segments || [];
      const existingIndex = currentSegments.findIndex(s => s.segment_id === segment.segment_id);
      
      let updatedSegments;
      if (existingIndex >= 0) {
        updatedSegments = [...currentSegments];
        updatedSegments[existingIndex] = segment;
      } else {
        updatedSegments = [...currentSegments, segment];
      }
      
      await productsApi.update(productId, { market_segments: updatedSegments });
      await refresh('product');
      // Mark personas as stale since they display segment names
      markStale(['personas']);
      setSegmentDialogOpen(false);
      setEditingSegment(null);
      toast.success(existingIndex >= 0 ? 'Segment updated' : 'Segment added');
    } catch (err) {
      console.error('Failed to save segment:', err);
      toast.error('Failed to save segment');
      throw err;
    }
  };
  
  // Optimistic delete handler for segment card trash icon
  const handleDeleteSegmentOptimistic = async (segment) => {
    const segmentId = segment.segment_id;
    
    // Optimistic: hide from UI immediately
    setDeletedSegments(prev => [...prev, segmentId]);
    setSegmentToDelete(null);
    
    try {
      const updatedSegments = (product.market_segments || []).filter(s => s.segment_id !== segmentId);
      await productsApi.update(productId, { market_segments: updatedSegments });
      await refresh('product');
      // Mark personas as stale since deleted segment affects persona cards
      markStale(['personas']);
      toast.success('Segment deleted');
    } catch (err) {
      // Restore on failure
      setDeletedSegments(prev => prev.filter(id => id !== segmentId));
      console.error('Failed to delete segment:', err);
      toast.error('Failed to delete segment');
    }
  };
  
  // Handle inline edit save
  const handleSaveField = async (field) => {
    if (!editValue.trim() && editValue !== '') return;
    setSaving(true);
    try {
      await productsApi.update(productId, { [field]: editValue });
      toast.success('Updated successfully');
      await refresh('product');
    } catch (err) {
      toast.error('Failed to update');
    } finally {
      setSaving(false);
      setEditingField(null);
    }
  };

  // Handle direct field save (for modal-based editing)
  const handleDirectSaveField = async (field, value) => {
    setSaving(true);
    try {
      await productsApi.update(productId, { [field]: value });
      toast.success('Updated successfully');
      await refresh('product');
    } catch (err) {
      toast.error('Failed to update');
      throw err;
    } finally {
      setSaving(false);
    }
  };
  
  // Start editing a field
  const startEdit = (field, value) => {
    setEditingField(field);
    setEditValue(value || '');
  };
  
  // Handle saving a market trends field (now flat fields)
  const handleSaveMarketTrendsField = async (fieldName, value) => {
    setSaving(true);
    try {
      await productsApi.update(productId, { [fieldName]: value });
      toast.success('Updated successfully');
      await refresh('product');
    } catch (err) {
      toast.error('Failed to update');
      throw err;
    } finally {
      setSaving(false);
    }
  };
  
  // Handle competitor save
  const handleSaveCompetitor = async () => {
    if (!competitorForm.name.trim()) {
      toast.error('Competitor name is required');
      return;
    }
    setSaving(true);
    try {
      const currentCompetitors = product.key_competitors || [];
      let newCompetitors;
      
      if (editingCompetitor !== null) {
        // Update existing
        newCompetitors = currentCompetitors.map((c, i) => 
          i === editingCompetitor ? { ...competitorForm } : c
        );
      } else {
        // Add new
        newCompetitors = [...currentCompetitors, { ...competitorForm }];
      }
      
      await productsApi.update(productId, { key_competitors: newCompetitors });
      toast.success(editingCompetitor !== null ? 'Competitor updated' : 'Competitor added');
      await refresh('product');
      setShowCompetitorModal(false);
      setEditingCompetitor(null);
      setCompetitorForm({ name: '', description: '', strengths: '', weaknesses: '', url: '' });
    } catch (err) {
      toast.error('Failed to save competitor');
    } finally {
      setSaving(false);
    }
  };
  
  // Handle competitor delete with optimistic UI
  const handleDeleteCompetitorConfirmed = async () => {
    const index = competitorToDelete;
    setCompetitorToDelete(null);
    
    // Optimistic: hide from UI immediately
    setDeletedCompetitors(prev => [...prev, index]);
    
    try {
      const newCompetitors = (product.key_competitors || []).filter((_, i) => i !== index);
      await productsApi.update(productId, { key_competitors: newCompetitors });
      toast.success('Competitor deleted');
      await refresh('product');
    } catch (err) {
      // Restore on failure
      setDeletedCompetitors(prev => prev.filter(i => i !== index));
      toast.error('Failed to delete competitor');
    }
  };
  
  // Open competitor modal for editing
  const openEditCompetitor = (comp, index) => {
    setCompetitorForm({
      name: comp.name || '',
      description: comp.description || comp.notes || '',
      strengths: comp.strengths || '',
      weaknesses: comp.weaknesses || '',
      url: comp.url || ''
    });
    setEditingCompetitor(index);
    setShowCompetitorModal(true);
  };
  
  // Open competitor modal for adding
  const openAddCompetitor = () => {
    setCompetitorForm({ name: '', description: '', strengths: '', weaknesses: '', url: '' });
    setEditingCompetitor(null);
    setShowCompetitorModal(true);
  };
  
  // Cancel editing
  const cancelEdit = () => setEditingField(null);
  
  // Render editable field using shared component
  const renderEditableField = (field, value, placeholder, multiline = false) => (
    <EditableFieldInline
      field={field}
      value={value}
      placeholder={placeholder}
      multiline={multiline}
      isEditing={editingField === field}
      editValue={editValue}
      setEditValue={setEditValue}
      onSave={handleSaveField}
      onCancel={cancelEdit}
      canEdit={canEdit}
      onStartEdit={startEdit}
      saving={saving}
      colors={colors}
    />
  );

  // Render markdown editable field with modal
  const renderMarkdownField = (field, value, placeholder, label) => (
    <EditableMarkdownFieldInline
      field={field}
      value={value}
      placeholder={placeholder}
      label={label}
      onSave={handleDirectSaveField}
      canEdit={canEdit}
      saving={saving}
      colors={colors}
      onRefresh={() => refresh('product')}
    />
  );

  // Render market trends field with modal (unused, kept for reference)
  const MarketTrendsField = ({ fieldName, value, placeholder, label }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);

    const handleSave = async (name, newValue) => {
      await handleSaveMarketTrendsField(fieldName, newValue);
      setIsModalOpen(false);
    };

    return (
      <>
        {value ? (
          <div className="prose prose-sm dark:prose-invert max-w-none">
            <ReactMarkdown remarkPlugins={[remarkBreaks]}>{value}</ReactMarkdown>
          </div>
        ) : (
          <p className={cn("text-sm", colors.textMuted)}>{placeholder}</p>
        )}
        
        <MarkdownFieldEditModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSave={handleSave}
          label={label}
          fieldName={fieldName}
          value={value}
          placeholder={placeholder}
        />
      </>
    );
  };

  // Dismiss explainer state
  const [explainerDismissed, setExplainerDismissed] = useState(false);
  
  const isMarketEmpty = !product.competitive_positioning &&
    (!product.market_segments || product.market_segments.length === 0) &&
    !product.market_size_tam &&
    !product.market_size_sam &&
    !product.market_size_som &&
    (!product.key_competitors || product.key_competitors.length === 0) &&
    !product.market_opportunities &&
    !product.market_weaknesses &&
    !product.market_threats;
  
  const showExplainer = isMarketEmpty && !explainerDismissed;

  return (
    <div className="flex flex-row h-full">
      {/* Main Content */}
      <div className="flex-1 p-6 overflow-y-auto">
        <div className="max-w-5xl mx-auto space-y-8">
          {showExplainer ? (
          <div className={cn("text-center py-12 px-6 rounded-lg border", colors.bgTertiary, colors.borderPrimary)} data-testid="market-empty-state">
            <Globe className={cn("w-12 h-12 mx-auto mb-4", colors.textMuted)} />
            <h3 className={cn("text-lg font-semibold mb-4", colors.textPrimary)}>No market data defined yet</h3>
            <div className={cn("text-sm mb-6 max-w-2xl mx-auto text-left space-y-4", colors.textMuted)}>
              <p>
                <strong className={colors.textPrimary}>Define the Market Environment</strong>
                <br />
                Broaden your assessment beyond internal performance metrics. In StackRanked, the Market data set establishes the external conditions that determine product success - from the specific industry segments you target to the total addressable market (TAM) you aim to capture. By documenting your competitive positioning and identifying the specific factors within your industry, you ensure that every strategic decision accounts for the external realities of your business.
              </p>
              <p>
                <strong className={colors.textPrimary}>The more comprehensive your Market Profile, the more precise StackRanked becomes.</strong>
                <br />
                These entries are not static records; they provide the objective benchmarks for your AI Co-Pilots. When you provide data on market size, competitor activities, and industry trends, your AI assistants can evaluate your Goals, Initiatives, and Epics against external risks and opportunities. This turns market research into a functional component of the prioritization process, allowing the system to identify which initiatives result in the highest market impact and competitive differentiation.
              </p>
              <p>
                <strong className={colors.textPrimary}>This is how you align execution with market complexity.</strong>
                <br />
                By moving beyond general observations, you program the system to identify risks and value propositions across the entire market context. Every market segment you define and every competitor you track improves the accuracy of the ranking process. This provides the clarity required to ensure your team is focused on the work that addresses the most significant market needs and mitigates the most critical external threats.
              </p>
            </div>
            {canEdit && (
              <Button className={cn(colors.accentGradient)} onClick={() => setExplainerDismissed(true)} data-testid="market-get-started-btn">
                Get Started
              </Button>
            )}
          </div>
          ) : (
          <>
          {/* Competitive Positioning */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center", colors.bgTertiary)}>
                <Target className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <h2 className={cn("text-xl font-semibold", colors.textPrimary)}>Competitive Positioning</h2>
                <p className={cn("text-sm", colors.textMuted)}>Where we stand vs competitors</p>
              </div>
            </div>
            <Card className={cn("p-6 border", colors.bgCard, colors.borderPrimary)}>
              {renderMarkdownField(
                "competitive_positioning", 
                product.competitive_positioning, 
                "Define your competitive stance and positioning in the market.",
                "Competitive Positioning"
              )}
            </Card>
          </section>

          {/* Market Segments */}
          <section>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center", colors.bgTertiary)}>
                  <Globe className="w-5 h-5 text-blue-500" />
                </div>
                <div>
                  <h2 className={cn("text-xl font-semibold", colors.textPrimary)}>Market Segments</h2>
                  <p className={cn("text-sm", colors.textMuted)}>Industry and vertical focus areas</p>
                </div>
              </div>
              {canEdit && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setEditingSegment({ segment_id: `seg_${Date.now()}`, title: '', description: '' });
                    setSegmentDialogOpen(true);
                  }}
                  className={cn("gap-1", colors.borderPrimary)}
                >
                  <Plus className="w-4 h-4" />
                  Add Segment
                </Button>
              )}
            </div>
            
            {product.market_segments && product.market_segments.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {product.market_segments
                  .filter(segment => !deletedSegments.includes(segment.segment_id))
                  .map((segment, index) => (
                  <Card key={segment.segment_id || index} className={cn("p-4 border group relative", colors.bgCard, colors.borderPrimary)}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className={cn("font-semibold", colors.textPrimary)}>{segment.title || 'Untitled Segment'}</h3>
                        <div className={cn("text-sm mt-1 prose prose-sm dark:prose-invert max-w-none", colors.textSecondary)}>
                          <ReactMarkdown remarkPlugins={[remarkBreaks]}>{segment.description || 'No description'}</ReactMarkdown>
                        </div>
                      </div>
                      {canEdit && (
                        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => {
                              setEditingSegment(segment);
                              setSegmentDialogOpen(true);
                            }}
                            className={cn("p-1.5 rounded", colors.bgHover)}
                          >
                            <Edit3 className="w-4 h-4 text-gray-400" />
                          </button>
                          <button
                            onClick={() => setSegmentToDelete(segment)}
                            className={cn("p-1.5 rounded", colors.bgHover)}
                          >
                            <Trash2 className="w-4 h-4 text-red-400" />
                          </button>
                        </div>
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className={cn("p-6 border", colors.bgCard, colors.borderPrimary)}>
                <div className="text-center">
                  <Globe className={cn("w-10 h-10 mx-auto mb-2", colors.textMuted)} />
                  <p className={colors.textMuted}>No market segments defined yet</p>
                  {canEdit && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setEditingSegment({ segment_id: `seg_${Date.now()}`, title: '', description: '' });
                        setSegmentDialogOpen(true);
                      }}
                      className="mt-3"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add your first segment
                    </Button>
                  )}
                </div>
              </Card>
            )}
          </section>

          {/* Market Size */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center", colors.bgTertiary)}>
                <BarChart3 className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <h2 className={cn("text-xl font-semibold", colors.textPrimary)}>Market Size</h2>
                <p className={cn("text-sm", colors.textMuted)}>TAM / SAM / SOM analysis</p>
              </div>
            </div>
            
            {/* Market Size Cards */}
            <div className="space-y-4">
              {/* TAM */}
              <Card className={cn("p-4 border", colors.bgCard, colors.borderPrimary)}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-blue-500" />
                    <h3 className={cn("font-medium", colors.textPrimary)}>TAM</h3>
                  </div>
                  <span className={cn("text-xs", colors.textMuted)}>Total Addressable Market</span>
                </div>
                {renderMarkdownField("market_size_tam", product.market_size_tam, "e.g., $50B", "TAM - Total Addressable Market")}
              </Card>
              
              {/* SAM */}
              <Card className={cn("p-4 border", colors.bgCard, colors.borderPrimary)}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-purple-500" />
                    <h3 className={cn("font-medium", colors.textPrimary)}>SAM</h3>
                  </div>
                  <span className={cn("text-xs", colors.textMuted)}>Serviceable Addressable Market</span>
                </div>
                {renderMarkdownField("market_size_sam", product.market_size_sam, "e.g., $10B", "SAM - Serviceable Addressable Market")}
              </Card>
              
              {/* SOM */}
              <Card className={cn("p-4 border", colors.bgCard, colors.borderPrimary)}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-emerald-500" />
                    <h3 className={cn("font-medium", colors.textPrimary)}>SOM</h3>
                  </div>
                  <span className={cn("text-xs", colors.textMuted)}>Serviceable Obtainable Market</span>
                </div>
                {renderMarkdownField("market_size_som", product.market_size_som, "e.g., $500M", "SOM - Serviceable Obtainable Market")}
              </Card>
            </div>
          </section>

          {/* Key Competitors */}
          <section>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center", colors.bgTertiary)}>
                  <Building2 className="w-5 h-5 text-orange-500" />
                </div>
                <div>
                  <h2 className={cn("text-xl font-semibold", colors.textPrimary)}>Key Competitors</h2>
                  <p className={cn("text-sm", colors.textMuted)}>Competitive landscape</p>
                </div>
              </div>
              {canEdit && (
                <Button variant="outline" size="sm" className={cn("gap-2", colors.borderPrimary)} onClick={openAddCompetitor}>
                  <Plus className="w-4 h-4" />
                  Add Competitor
                </Button>
              )}
            </div>
            {product.key_competitors && product.key_competitors.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {product.key_competitors
                  .map((comp, i) => ({ comp, originalIndex: i }))
                  .filter(({ originalIndex }) => !deletedCompetitors.includes(originalIndex))
                  .map(({ comp, originalIndex }) => (
                  <Card key={originalIndex} className={cn("p-5 border group relative", colors.bgCard, colors.borderPrimary)}>
                    {/* Header */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center", colors.bgTertiary)}>
                          <Building2 className="w-5 h-5 text-orange-500" />
                        </div>
                        <div>
                          <h3 className={cn("font-semibold", colors.textPrimary)}>{comp.name}</h3>
                          {comp.url && (
                            <a href={comp.url} target="_blank" rel="noopener noreferrer" className="text-blue-500 text-xs hover:underline flex items-center gap-1">
                              <ExternalLink className="w-3 h-3" />
                              Visit website
                            </a>
                          )}
                        </div>
                      </div>
                      {canEdit && (
                        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button onClick={() => openEditCompetitor(comp, originalIndex)} className={cn("p-1.5 rounded", colors.bgHover)}>
                            <Edit3 className="w-4 h-4 text-gray-400" />
                          </button>
                          <button onClick={() => setCompetitorToDelete(originalIndex)} className={cn("p-1.5 rounded", colors.bgHover)}>
                            <Trash2 className="w-4 h-4 text-red-400" />
                          </button>
                        </div>
                      )}
                    </div>
                    
                    {/* Description */}
                    {(comp.description || comp.notes) && (
                      <p className={cn("text-sm mb-4", colors.textSecondary)}>
                        {comp.description || comp.notes}
                      </p>
                    )}
                    
                    {/* Strengths & Weaknesses - Always show if either exists */}
                    {(comp.strengths || comp.weaknesses) && (
                      <div className="grid grid-cols-2 gap-3">
                        <div className={cn("p-3 rounded-lg", colors.bgTertiary)}>
                          <div className="flex items-center gap-2 mb-2">
                            <ThumbsUp className="w-3.5 h-3.5 text-green-500" />
                            <span className={cn("text-xs font-medium", colors.textMuted)}>Strengths</span>
                          </div>
                          <p className={cn("text-xs", colors.textSecondary)}>{comp.strengths || 'Not specified'}</p>
                        </div>
                        <div className={cn("p-3 rounded-lg", colors.bgTertiary)}>
                          <div className="flex items-center gap-2 mb-2">
                            <ThumbsDown className="w-3.5 h-3.5 text-red-500" />
                            <span className={cn("text-xs font-medium", colors.textMuted)}>Weaknesses</span>
                          </div>
                          <p className={cn("text-xs", colors.textSecondary)}>{comp.weaknesses || 'Not specified'}</p>
                        </div>
                      </div>
                    )}
                    
                    {/* Legacy feature_comparison display */}
                    {comp.feature_comparison && !comp.strengths && !comp.weaknesses && (
                      <div className={cn("text-xs p-3 rounded mt-3", colors.bgTertiary)}>
                        <span className={colors.textMuted}>Analysis: </span>
                        <span className={colors.textSecondary}>{comp.feature_comparison}</span>
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            ) : (
              <Card className={cn("p-8 text-center border", colors.bgCard, colors.borderPrimary)}>
                <Building2 className={cn("w-12 h-12 mx-auto mb-3", colors.textMuted)} />
                <p className={colors.textMuted}>No competitors added yet</p>
                <p className={cn("text-sm mt-1 mb-4", colors.textMuted)}>Ask the AI to help identify key competitors</p>
                {canEdit && (
                  <Button variant="outline" onClick={openAddCompetitor}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Your First Competitor
                  </Button>
                )}
              </Card>
            )}
          </section>

          {/* Market Trends */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center", colors.bgTertiary)}>
                <TrendingUp className="w-5 h-5 text-purple-500" />
              </div>
              <div>
                <h2 className={cn("text-xl font-semibold", colors.textPrimary)}>Market Trends</h2>
                <p className={cn("text-sm", colors.textMuted)}>Opportunities, weaknesses, and threats in the market</p>
              </div>
            </div>
            
            {/* Structured full-width layout for market trends */}
            <div className="space-y-4">
              <MarketTrendCard
                fieldName="market_opportunities"
                value={product.market_opportunities}
                placeholder="Identify growth opportunities..."
                label="Opportunities"
                icon={TrendingUp}
                iconBgClass="bg-emerald-100 dark:bg-emerald-900/30"
                iconClass="text-emerald-600 dark:text-emerald-400"
                onSave={handleSaveMarketTrendsField}
                canEdit={canEdit}
                colors={colors}
              />
              
              <MarketTrendCard
                fieldName="market_weaknesses"
                value={product.market_weaknesses}
                placeholder="Identify internal weaknesses..."
                label="Weaknesses"
                icon={AlertTriangle}
                iconBgClass="bg-amber-100 dark:bg-amber-900/30"
                iconClass="text-amber-600 dark:text-amber-400"
                onSave={handleSaveMarketTrendsField}
                canEdit={canEdit}
                colors={colors}
              />
              
              <MarketTrendCard
                fieldName="market_threats"
                value={product.market_threats}
                placeholder="Identify external threats..."
                label="Threats"
                icon={AlertTriangle}
                iconBgClass="bg-red-100 dark:bg-red-900/30"
                iconClass="text-red-600 dark:text-red-400"
                onSave={handleSaveMarketTrendsField}
                canEdit={canEdit}
                colors={colors}
              />
            </div>
          </section>
          </>
          )}
        </div>
      </div>
      
      {/* AI Side Drawer */}
      {canUseAI && !showExplainer && <CollectionCopilotDrawer 
        context="market" 
        contextId={productId} 
        title="Market Analyst"
        onRefresh={() => refresh('product')}
       
      />}
      
      {/* Competitor Modal */}
      {showCompetitorModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className={cn("w-full max-w-4xl h-[700px] p-6 mx-4 flex flex-col", colors.bgCard, colors.borderPrimary)}>
            <h3 className={cn("text-lg font-semibold mb-4", colors.textPrimary)}>
              {editingCompetitor !== null ? 'Edit Competitor' : 'Add Competitor'}
            </h3>
            
            <div className="flex-1 flex flex-col space-y-4 overflow-hidden">
              <div>
                <label className={cn("text-sm font-medium", colors.textSecondary)}>Name *</label>
                <Input
                  value={competitorForm.name}
                  onChange={(e) => setCompetitorForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Acme Corp"
                  className={cn("mt-1", colors.bgInput, colors.borderPrimary)}
                />
              </div>
              
              <div>
                <label className={cn("text-sm font-medium", colors.textSecondary)}>Description</label>
                <Textarea
                  value={competitorForm.description}
                  onChange={(e) => setCompetitorForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Brief description of this competitor"
                  className={cn("mt-1 min-h-[80px]", colors.bgInput, colors.borderPrimary)}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4 flex-1 min-h-0">
                <div className="flex flex-col">
                  <label className={cn("text-sm font-medium flex items-center gap-1", colors.textSecondary)}>
                    <ThumbsUp className="w-3.5 h-3.5 text-green-500" />
                    Strengths
                  </label>
                  <Textarea
                    value={competitorForm.strengths}
                    onChange={(e) => setCompetitorForm(prev => ({ ...prev, strengths: e.target.value }))}
                    placeholder="What they do well"
                    className={cn("mt-1 flex-1 min-h-[180px]", colors.bgInput, colors.borderPrimary)}
                  />
                </div>
                <div className="flex flex-col">
                  <label className={cn("text-sm font-medium flex items-center gap-1", colors.textSecondary)}>
                    <ThumbsDown className="w-3.5 h-3.5 text-red-500" />
                    Weaknesses
                  </label>
                  <Textarea
                    value={competitorForm.weaknesses}
                    onChange={(e) => setCompetitorForm(prev => ({ ...prev, weaknesses: e.target.value }))}
                    placeholder="Their weak points"
                    className={cn("mt-1 flex-1 min-h-[180px]", colors.bgInput, colors.borderPrimary)}
                  />
                </div>
              </div>
              
              <div>
                <label className={cn("text-sm font-medium", colors.textSecondary)}>Website URL</label>
                <Input
                  value={competitorForm.url}
                  onChange={(e) => setCompetitorForm(prev => ({ ...prev, url: e.target.value }))}
                  placeholder="https://competitor.com"
                  className={cn("mt-1", colors.bgInput, colors.borderPrimary)}
                />
              </div>
            </div>
            
            <div className="flex justify-end gap-3 pt-4 mt-auto border-t border-gray-200 dark:border-gray-700">
              <Button variant="outline" onClick={() => { setShowCompetitorModal(false); setEditingCompetitor(null); }}>
                Cancel
              </Button>
              <Button onClick={handleSaveCompetitor} disabled={saving} className={colors.accentGradient}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {editingCompetitor !== null ? 'Update' : 'Add'} Competitor
              </Button>
            </div>
          </Card>
        </div>
      )}
      
      {/* Market Segment Modal */}
      <SegmentDetailModal
        isOpen={segmentDialogOpen}
        segment={editingSegment}
        productId={productId}
        onSave={handleSaveSegmentLocal}
        onClose={() => { setSegmentDialogOpen(false); setEditingSegment(null); }}
        canEdit={canEdit}
        zIndex={60}
      />
      
      {/* Delete Segment Confirmation Modal */}
      <Dialog open={!!segmentToDelete} onOpenChange={() => setSegmentToDelete(null)}>
        <DialogContent className="max-w-md bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-gray-900 dark:text-gray-100">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Delete Market Segment?
            </DialogTitle>
            <DialogDescription className="text-gray-600 dark:text-gray-400">
              Are you sure you want to delete "{segmentToDelete?.title}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => setSegmentToDelete(null)}
            >
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={() => handleDeleteSegmentOptimistic(segmentToDelete)}
            >
              Delete Segment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Delete Competitor Confirmation Modal */}
      <Dialog open={competitorToDelete !== null} onOpenChange={() => setCompetitorToDelete(null)}>
        <DialogContent className="max-w-md bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-gray-900 dark:text-gray-100">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Delete Competitor?
            </DialogTitle>
            <DialogDescription className="text-gray-600 dark:text-gray-400">
              Are you sure you want to delete "{product.key_competitors?.[competitorToDelete]?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => setCompetitorToDelete(null)}
            >
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={handleDeleteCompetitorConfirmed}
            >
              Delete Competitor
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== ADD PERSONA MODAL ====================
const AddPersonaModal = ({ isOpen, onClose, onSave, productId }) => {
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    role: '',
    goals: '',
    pain_points: '',
    context: ''
  });

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData({ name: '', role: '', goals: '', pain_points: '', context: '' });
    }
  }, [isOpen]);

  const handleCreate = async () => {
    if (!formData.name.trim()) {
      toast.error('Persona name is required');
      return;
    }
    
    setSaving(true);
    try {
      await personasApi.create(productId, formData);
      toast.success('Persona created!');
      onSave();
      onClose();
    } catch (err) {
      console.error('Failed to create persona:', err);
      toast.error(err.response?.data?.detail || 'Failed to create persona');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={cn(colors.bgCard, "max-w-lg overflow-hidden")}>
        <DialogHeader>
          <DialogTitle className={cn("flex items-center gap-2", colors.textPrimary)}>
            <Users className="w-5 h-5 text-green-500" />
            Add Persona
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-5 py-4">
          <div>
            <label className={cn("text-sm font-medium", colors.textSecondary)}>Name *</label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Enterprise Admin, Power User"
              className={cn("mt-1.5", colors.bgInput, colors.borderPrimary)}
              data-testid="persona-name-input"
            />
          </div>
          
          <div>
            <label className={cn("text-sm font-medium", colors.textSecondary)}>Role</label>
            <Input
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              placeholder="e.g., IT Manager at mid-sized company"
              className={cn("mt-1.5", colors.bgInput, colors.borderPrimary)}
              data-testid="persona-role-input"
            />
          </div>
          
          <div>
            <label className={cn("text-sm font-medium", colors.textSecondary)}>Goals</label>
            <Textarea
              value={formData.goals}
              onChange={(e) => setFormData({ ...formData, goals: e.target.value })}
              placeholder="What does this persona want to achieve?"
              rows={2}
              className={cn("mt-1.5", colors.bgInput, colors.borderPrimary)}
            />
          </div>
          
          <div>
            <label className={cn("text-sm font-medium", colors.textSecondary)}>Pain Points</label>
            <Textarea
              value={formData.pain_points}
              onChange={(e) => setFormData({ ...formData, pain_points: e.target.value })}
              placeholder="What frustrations or challenges do they face?"
              rows={2}
              className={cn("mt-1.5", colors.bgInput, colors.borderPrimary)}
            />
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button 
            onClick={handleCreate}
            disabled={!formData.name.trim() || saving}
            className={cn(colors.accentGradient)}
            data-testid="create-persona-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
            Create Persona
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ==================== PERSONAS TAB ====================
const PersonasTab = ({ personas, product, productId, canEdit, canUseAI, refresh, allFeatures = [], allEpics = [], searchNavTarget, onSearchNavHandled, markStale, getCascadeTargets }) => {
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();
  const [selectedPersona, setSelectedPersona] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [lastHandledNavId, setLastHandledNavId] = useState(null);

  // Handle search navigation 
  useEffect(() => {
    if (searchNavTarget?.type === 'persona' && searchNavTarget.id !== lastHandledNavId) {
      const persona = personas.find(p => p.persona_id === searchNavTarget.id);
      if (persona) {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setSelectedPersona(persona);
        setIsDetailModalOpen(true);
      }
      setLastHandledNavId(searchNavTarget.id);
      onSearchNavHandled?.();
    }
  }, [searchNavTarget, personas, lastHandledNavId, onSearchNavHandled]);

  const handlePersonaClick = (persona) => {
    setSelectedPersona(persona);
    setIsDetailModalOpen(true);
  };

  const handleModalClose = () => {
    setIsDetailModalOpen(false);
    setSelectedPersona(null);
  };

  return (
    <div className="flex flex-row h-full">
      {/* Main Content */}
      <div className="flex-1 p-6 overflow-y-auto">
        <div className="max-w-6xl mx-auto">
          {personas.length > 0 && (
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center", colors.bgTertiary)}>
                <Users className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <h2 className={cn("text-xl font-semibold", colors.textPrimary)}>
                  Personas
                  <span className={cn("ml-2 text-sm font-normal", colors.textMuted)}>({personas.length})</span>
                </h2>
                <p className={cn("text-sm", colors.textMuted)}>Who you are building for</p>
              </div>
            </div>
            {canEdit && (
              <Button 
                className={cn("gap-2", colors.accentGradient)}
                onClick={() => setShowAddModal(true)}
                data-testid="add-persona-btn"
              >
                <Plus className="w-4 h-4" />
                Add Persona
              </Button>
            )}
          </div>
          )}

          {personas.length === 0 ? (
            <div className={cn("text-center py-12 px-6 rounded-lg border", colors.bgTertiary, colors.borderPrimary)}>
              <Users className={cn("w-12 h-12 mx-auto mb-4", colors.textMuted)} />
              <h3 className={cn("text-lg font-semibold mb-4", colors.textPrimary)}>No personas defined yet</h3>
              <div className={cn("text-sm mb-6 max-w-2xl mx-auto text-left space-y-4", colors.textMuted)}>
                <p>
                  <strong className={colors.textPrimary}>Build the Full Round Table</strong>
                  <br />
                  Broaden your thinking beyond the &ldquo;end user.&rdquo; In StackRanked, a powerful persona set covers the entire customer chain - from the Executive who signs the check and the Administrator who manages the account, to the diverse Users who each face unique friction points. By defining every stakeholder who touches your product, you ensure that no buyer is ignored and no operational hurdle is a surprise.
                </p>
                <p>
                  <strong className={colors.textPrimary}>The stronger your &ldquo;Round Table,&rdquo; the more powerful StackRanked becomes.</strong>
                  <br />
                  These personas aren&apos;t just static profiles; they are the active advisors for your AI Co-Pilots. When you have a deep, inclusive customer chain defined, your AI assistants can rigorously stress-test your Goals, Initiatives, and Epics against the reality of every person involved in the buying and using process. This transforms your co-pilot from a generic helper into a specialized team that understands the complex politics and needs of a real organization.
                </p>
                <p>
                  <strong className={colors.textPrimary}>This is how you align execution with market complexity.</strong>
                  <br />
                  By moving beyond the typical &ldquo;three or four&rdquo; profiles, you program the system to identify risks and value propositions across the whole customer journey. Every update or new persona you add refines your Virtual Focus Group, giving you the clarity needed to get the right things done for the people who actually determine your product&apos;s success.
                </p>
              </div>
              {canEdit && (
                <Button className={cn(colors.accentGradient)} onClick={() => setShowAddModal(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Your First Persona
                </Button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {personas.map(persona => {
                // Count epics and features that reference this persona
                const epicCount = allEpics.filter(e => e.persona_ids?.includes(persona.persona_id)).length;
                const featureCount = allFeatures.filter(f => f.persona_ids?.includes(persona.persona_id)).length;
                
                return (
                  <Card 
                    key={persona.persona_id} 
                    className={cn("border cursor-pointer transition-all hover:shadow-md hover:ring-2 hover:ring-purple-500/30 overflow-hidden flex flex-col", colors.bgCard, colors.borderPrimary)}
                    onClick={() => handlePersonaClick(persona)}
                    data-testid={`persona-card-${persona.persona_id}`}
                  >
                    <div className="p-6 flex-1">
                      <div className="flex items-start gap-4">
                        <div className={cn("w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0", colors.bgTertiary)}>
                          <Users className="w-7 h-7 text-green-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className={cn("text-lg font-semibold", colors.textPrimary)}>{persona.name}</h3>
                          <p className={cn("text-sm mb-3", colors.textSecondary)}>{persona.role}</p>
                          
                          {persona.goals && (
                            <div className="mb-3">
                              <h4 className={cn("text-xs font-medium mb-1", colors.textMuted)}>Goals</h4>
                              <p className={cn("text-sm line-clamp-2", colors.textSecondary)}>{persona.goals}</p>
                            </div>
                          )}
                          
                          {persona.pain_points && (
                            <div className="mb-3">
                              <h4 className={cn("text-xs font-medium mb-1", colors.textMuted)}>Pain Points</h4>
                              <p className={cn("text-sm line-clamp-2", colors.textSecondary)}>{persona.pain_points}</p>
                            </div>
                          )}
                          
                          {persona.context && (
                            <div>
                              <h4 className={cn("text-xs font-medium mb-1", colors.textMuted)}>Context</h4>
                              <p className={cn("text-sm line-clamp-2", colors.textSecondary)}>{persona.context}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Footer Section */}
                    <div className={cn("px-6 py-3 border-t flex items-center justify-between", colors.borderPrimary, colors.bgTertiary)}>
                      <div className="flex items-center">
                        <span className={cn("text-xs", colors.textMuted)}>Epics</span>
                        <span className={cn("text-xs font-medium ml-1", epicCount > 0 ? colors.textPrimary : colors.textMuted)}>{epicCount}</span>
                      </div>
                      <div className="flex items-center">
                        {(() => {
                          const segmentTitle = persona.segment_id && product.market_segments
                            ? product.market_segments.find(s => s.segment_id === persona.segment_id)?.title
                            : null;
                          return segmentTitle ? (
                            <span className={cn("text-xs font-medium px-2 py-0.5 rounded-full", colors.bgCard, colors.textSecondary)}>
                              {segmentTitle}
                            </span>
                          ) : (
                            <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400">
                              No Segment
                            </span>
                          );
                        })()}
                      </div>
                      <div className="flex items-center">
                        <span className={cn("text-xs", colors.textMuted)}>Features</span>
                        <span className={cn("text-xs font-medium ml-1", featureCount > 0 ? colors.textPrimary : colors.textMuted)}>{featureCount}</span>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </div>
      
      {/* AI Side Drawer */}
      {canUseAI && personas.length > 0 && <CollectionCopilotDrawer 
        context="personas" 
        contextId={productId} 
        title="Personas Expert"
        onRefresh={() => refresh('collection', 'personas')}
       
      />}
      
      {/* Persona Detail Modal with AI Co-pilot */}
      <PersonaDetailModal
        isOpen={isDetailModalOpen}
        persona={selectedPersona}
        onUpdate={() => refresh('collection', 'personas')}
        onClose={handleModalClose}
        productContext={product || { product_id: productId }}
        allFeatures={allFeatures}
        allEpics={allEpics}
      />
      
      {/* Add Persona Modal */}
      <AddPersonaModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSave={() => refresh('collection', 'personas')}
        productId={productId}
      />
    </div>
  );
};


// ==================== GOAL CARD COMPONENT ====================
// Format value based on metric format
const formatGoalMetricValue = (value, format) => {
  if (value === null || value === undefined) return '-';
  const num = parseFloat(value);
  if (isNaN(num)) return value;
  
  switch (format) {
    case 'currency':
      return `$${num.toLocaleString()}`;
    case 'percent':
      return `${num}%`;
    case 'time':
      if (num >= 60) {
        const hours = Math.floor(num / 60);
        const mins = Math.round(num % 60);
        return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
      }
      return `${num}m`;
    default:
      return num.toLocaleString();
  }
};

/**
 * InitiativeCard - Unified card design following Goals pattern
 * 
 * Layout:
 * - Header: Icon | Title
 * - Tags: [Status] [Target Date] [Blocked badge]
 * - Description: 2-line clamp
 * - Metrics: Goals, Epics, Features, Progress (with dotted lines)
 * - Footer: Follow Type indicator | Owner
 */
const InitiativeCard = ({ 
  initiative, 
  colors, 
  onSelect,
  goals = []
}) => {
  // Get status badge color
  const getStatusBadge = (status) => {
    const statusMap = {
      draft: { color: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300', label: 'Draft' },
      planning: { color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400', label: 'Planning' },
      in_progress: { color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400', label: 'In Progress' },
      shipped: { color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400', label: 'Shipped' },
      paused: { color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400', label: 'Paused' }
    };
    return statusMap[status] || statusMap.draft;
  };

  // Calculate goal count from linked goals
  const getGoalCount = () => {
    const goalIds = initiative.goal_ids || (initiative.goal_id ? [initiative.goal_id] : []);
    return goalIds.length;
  };

  // Calculate KPI stats from linked goals
  const getKpiStats = () => {
    const subscribedCount = initiative.subscribed_kpi_ids?.length || 0;
    const linkedGoalIds = initiative.goal_ids || (initiative.goal_id ? [initiative.goal_id] : []);
    const availableCount = goals
      .filter(g => linkedGoalIds.includes(g.goal_id))
      .reduce((sum, g) => sum + (g.kpis?.length || 0), 0);
    return { subscribed: subscribedCount, available: availableCount };
  };

  // Get tracking status based on progress and blockers
  const getTrackingStatus = () => {
    if (initiative.is_blocked) return 'blocked';
    if (initiative.child_blocker_count > 0) return 'at-risk';
    if (initiative.status === 'shipped') return 'completed';
    const progress = initiative.computed_progress || 0;
    if (progress >= 100) return 'completed';
    if (progress >= 50) return 'on-track';
    if (progress > 0) return 'in-progress';
    return 'neutral';
  };

  // Get confidence-based color for days display
  const getConfidenceColor = (confidence, days) => {
    if (days <= 3) return "text-red-600 dark:text-red-400";
    if (days <= 7) return "text-amber-600 dark:text-amber-400";
    
    switch (confidence) {
      case 'high':
        return "text-green-600 dark:text-green-400";
      case 'medium':
        return "text-amber-600 dark:text-amber-400";
      case 'low':
        return "text-gray-500 dark:text-gray-400";
      default:
        return colors.textMuted;
    }
  };

  // Metrics
  const goalCount = getGoalCount();
  const kpiStats = getKpiStats();
  const epicStats = initiative.epic_stats || { total: 0, completed: 0 };
  const featureStats = initiative.feature_stats || { total: 0, done: 0 };
  const progress = initiative.computed_progress || 0;
  const isBlocked = initiative.is_blocked;
  const hasChildBlockers = initiative.child_blocker_count > 0;
  
  const statusBadge = getStatusBadge(initiative.status);
  const trackingStatus = getTrackingStatus();
  const followType = initiative.follow_type || 'epics';

  // Tracking status icon
  const TrackingIcon = () => {
    switch (trackingStatus) {
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'on-track':
        return <TrendingUp className="w-4 h-4 text-blue-500" />;
      case 'in-progress':
        return <Clock className="w-4 h-4 text-amber-500" />;
      case 'at-risk':
        return <AlertTriangle className="w-4 h-4 text-amber-500" />;
      case 'blocked':
        return <AlertTriangle className="w-4 h-4 text-red-500" />;
      default:
        return <Clock className="w-4 h-4 text-gray-400" />;
    }
  };

  return (
    <Card 
      className={cn(
        "border cursor-pointer transition-all hover:shadow-lg hover:ring-2 hover:ring-purple-500/30 overflow-hidden flex flex-col",
        colors.bgCard, 
        isBlocked 
          ? "border-red-500 dark:border-red-500 ring-1 ring-red-500/50" 
          : colors.borderPrimary
      )}
      onClick={() => onSelect(initiative)}
      data-testid={`initiative-card-${initiative.initiative_id}`}
    >
      {/* Header Section */}
      <div className="p-4 pb-2 flex-shrink-0">
        <div className="flex items-start gap-3">
          <div className={cn(
            "w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0",
            isBlocked ? "bg-red-100 dark:bg-red-900/30" : colors.bgTertiary
          )}>
            {isBlocked ? (
              <AlertTriangle className="w-5 h-5 text-red-500" />
            ) : (
              <Rocket className={cn("w-5 h-5", colors.textAccent)} />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <h3 className={cn("font-semibold text-base leading-tight line-clamp-1", colors.textPrimary)}>
              {initiative.title}
            </h3>
            {/* Tags Row */}
            <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
              {/* Follow Type Badge - first */}
              <span className={cn(
                "px-2 py-0.5 rounded-full text-xs font-medium",
                followType === 'tags' 
                  ? "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400"
                  : "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400"
              )}>
                {followType === 'tags' ? 'Tags' : 'Epics'}
              </span>
              {isBlocked && (
                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
                  Blocked
                </span>
              )}
              {!isBlocked && hasChildBlockers && (
                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                  ⚠️ {initiative.child_blocker_count}
                </span>
              )}
              <span className={cn("px-2 py-0.5 rounded-full text-xs", statusBadge.color)}>
                {statusBadge.label}
              </span>
              {/* Owner Avatar - after status badge */}
              {initiative.owner && (
                <div 
                  className={cn(
                    "w-5 h-5 rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0",
                    "bg-gradient-to-br from-purple-500 to-blue-600 text-white"
                  )}
                  title={`Owner: ${initiative.owner.name || initiative.owner.email}`}
                >
                  {(initiative.owner.name || initiative.owner.email || '?')[0].toUpperCase()}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Description Section */}
      <div className={cn("px-4 h-12 flex-shrink-0", colors.textSecondary)}>
        <p className="text-sm line-clamp-2">
          {initiative.description || 'No description provided'}
        </p>
      </div>
      
      {/* Metrics Section */}
      <div className={cn("px-4 py-3 flex-1 border-t space-y-1.5", colors.borderPrimary)}>
        {/* Goals */}
        <div className="flex items-center justify-between">
          <span className={cn("text-xs", colors.textMuted)}>Goals</span>
          <div className="flex-1 mx-2 border-b border-dotted border-gray-300 dark:border-gray-600" />
          <span className={cn("text-xs font-medium", goalCount > 0 ? colors.textPrimary : colors.textMuted)}>
            {goalCount}
          </span>
        </div>
        
        {/* KPIs */}
        <div className="flex items-center justify-between">
          <span className={cn("text-xs", colors.textMuted)}>KPIs</span>
          <div className="flex-1 mx-2 border-b border-dotted border-gray-300 dark:border-gray-600" />
          <span className="text-xs font-medium">
            <span className={kpiStats.subscribed > 0 ? "text-purple-600 dark:text-purple-400" : colors.textMuted}>
              {kpiStats.subscribed}
            </span>
            <span className={colors.textMuted}>/{kpiStats.available}</span>
          </span>
        </div>
        
        {/* Tags or Epics - based on follow_type */}
        {followType === 'tags' ? (
          <div className="flex items-center justify-between">
            <span className={cn("text-xs", colors.textMuted)}>Tags</span>
            <div className="flex-1 mx-2 border-b border-dotted border-gray-300 dark:border-gray-600" />
            <span className={cn("text-xs font-medium", (initiative.tag_ids?.length || 0) > 0 ? "text-indigo-600 dark:text-indigo-400" : colors.textMuted)}>
              {initiative.tag_ids?.length || 0}
            </span>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <span className={cn("text-xs", colors.textMuted)}>Epics</span>
            <div className="flex-1 mx-2 border-b border-dotted border-gray-300 dark:border-gray-600" />
            <span className={cn("text-xs font-medium", (initiative.epic_count || 0) > 0 ? "text-purple-600 dark:text-purple-400" : colors.textMuted)}>
              {initiative.epic_count || 0}
            </span>
          </div>
        )}
        
        {/* Features */}
        <div className="flex items-center justify-between">
          <span className={cn("text-xs", colors.textMuted)}>Features</span>
          <div className="flex-1 mx-2 border-b border-dotted border-gray-300 dark:border-gray-600" />
          <span className={cn("text-xs font-medium", featureStats.total > 0 ? colors.textPrimary : colors.textMuted)}>
            {featureStats.total}
          </span>
        </div>
        
        {/* Progress */}
        <div className="flex items-center justify-between">
          <span className={cn("text-xs", colors.textMuted)}>Progress</span>
          <div className="flex-1 mx-2 border-b border-dotted border-gray-300 dark:border-gray-600" />
          <span className={cn(
            "text-xs font-medium",
            progress >= 100 ? "text-green-600 dark:text-green-400" :
            progress >= 50 ? "text-blue-600 dark:text-blue-400" :
            colors.textMuted
          )}>
            {progress}%
          </span>
        </div>
      </div>
      
      {/* Footer Section */}
      <div className={cn(
        "px-4 py-3 border-t flex-shrink-0 mt-auto h-[52px] flex items-center justify-between",
        colors.borderPrimary, 
        colors.bgTertiary
      )}>
        <div className="flex items-center gap-2 min-w-0">
          {followType === 'tags' ? (
            <>
              <Tag className={cn("w-3.5 h-3.5 flex-shrink-0", colors.textMuted)} />
              <span className={cn("text-xs", colors.textSecondary)}>Tag-based</span>
            </>
          ) : (
            <>
              <Target className={cn("w-3.5 h-3.5 flex-shrink-0", colors.textMuted)} />
              <span className={cn("text-xs truncate", colors.textSecondary)}>
                {initiative.goal_title || 'No Goals'}
              </span>
            </>
          )}
        </div>
        {/* Days Remaining - bottom right */}
        {initiative.target_date && initiative.status !== 'shipped' && initiative.status !== 'paused' && 
         initiative.days_remaining !== null && initiative.days_remaining !== undefined ? (
          <span className={cn(
            "text-xs font-medium flex items-center gap-1",
            getConfidenceColor(initiative.date_confidence, initiative.days_remaining)
          )}>
            <Clock className="w-3.5 h-3.5" />
            {initiative.days_remaining}d
          </span>
        ) : (
          <span className={cn("text-xs", colors.textMuted)}>
            {initiative.status === 'shipped' ? 'Shipped' : initiative.status === 'paused' ? 'Paused' : ''}
          </span>
        )}
      </div>
    </Card>
  );
};

/**
 * FeatureCard - Unified card design following Goals pattern
 * 
 * Layout:
 * - Header: Icon (AlertTriangle if blocked) | Title
 * - Tags: [Blocked] [Priority] [Release/Parking Lot] [Status]
 * - Description: 2-line clamp
 * - Metrics: Personas, KPIs, Requirements, Level of Effort (with dotted lines)
 * - Footer: Epic Name | (future: New Note indicator)
 */
const FeatureCard = ({ 
  feature, 
  colors, 
  onSelect, 
  getEpicName, 
  releases = [],
  personaMap = {},
  isArchived = false 
}) => {
  // Get priority badge color
  const getPriorityBadge = (priority) => {
    const priorityMap = {
      Critical: { color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', label: 'Critical' },
      High: { color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400', label: 'High' },
      Medium: { color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400', label: 'Medium' },
      Low: { color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400', label: 'Low' }
    };
    return priorityMap[priority] || priorityMap.Medium;
  };

  // Get status badge color
  const getStatusBadge = (status) => {
    const statusMap = {
      Backlog: { color: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300', label: 'Backlog' },
      Todo: { color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400', label: 'Todo' },
      'In Progress': { color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400', label: 'In Progress' },
      Review: { color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400', label: 'Review' },
      Done: { color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400', label: 'Done' }
    };
    return statusMap[status] || statusMap.Backlog;
  };

  // Get release name
  const getReleaseName = (releaseId) => {
    if (!releaseId) return 'Parking Lot';
    const release = releases.find(r => r.release_id === releaseId);
    return release?.name || 'Unknown';
  };

  // Count metrics
  const personaCount = feature.persona_ids?.length || 0;
  const kpiSubscribedCount = feature.kpi_alignments?.length || 0;
  const kpiAvailableCount = feature.available_kpi_count ?? 0; // From backend (same pattern as Epics)
  const requirementCount = feature.requirements?.length || 0;
  const effortSize = feature.effort_size || '-';
  
  const priorityBadge = getPriorityBadge(feature.priority_level);
  const statusBadge = getStatusBadge(feature.status);
  const epicName = getEpicName(feature.epic_id);
  const releaseName = getReleaseName(feature.release_id);
  const isBlocked = feature.is_blocked;

  return (
    <Card 
      className={cn(
        "border cursor-pointer transition-all hover:shadow-lg hover:ring-2 hover:ring-blue-500/30 overflow-hidden flex flex-col",
        colors.bgCard, 
        isBlocked 
          ? "border-red-500 dark:border-red-500 ring-1 ring-red-500/50" 
          : colors.borderPrimary,
        isArchived && "opacity-70 hover:opacity-100"
      )}
      onClick={() => onSelect(feature)}
    >
      {/* Header Section */}
      <div className="p-4 pb-2 flex-shrink-0">
        <div className="flex items-start gap-3">
          <div className={cn(
            "w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0",
            isBlocked ? "bg-red-100 dark:bg-red-900/30" : colors.bgTertiary
          )}>
            {isBlocked ? (
              <AlertTriangle className="w-5 h-5 text-red-500" />
            ) : (
              <LayoutList className={cn("w-5 h-5", colors.textAccent)} />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <h3 className={cn("font-semibold text-base leading-tight line-clamp-1", colors.textPrimary)}>
              {feature.title}
            </h3>
            {/* Tags Row */}
            <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
              {isBlocked && (
                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
                  Blocked
                </span>
              )}
              {feature.priority_level && (
                <span className={cn("px-2 py-0.5 rounded-full text-xs max-w-[10ch] truncate", priorityBadge.color)}>
                  {priorityBadge.label}
                </span>
              )}
              <span className={cn(
                "px-2 py-0.5 rounded-full text-xs max-w-[10ch] truncate",
                feature.release_id 
                  ? "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400"
                  : "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400"
              )}>
                {releaseName}
              </span>
              <span className={cn("px-2 py-0.5 rounded-full text-xs max-w-[10ch] truncate", statusBadge.color)}>
                {statusBadge.label}
              </span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Description Section */}
      <div className={cn("px-4 h-12 flex-shrink-0", colors.textSecondary)}>
        <p className="text-sm line-clamp-2">
          {feature.description || 'No description provided'}
        </p>
      </div>
      
      {/* Metrics Section */}
      <div className={cn("px-4 py-3 flex-1 border-t space-y-1.5", colors.borderPrimary)}>
        {/* Personas */}
        <div className="flex items-center justify-between">
          <span className={cn("text-xs", colors.textMuted)}>Personas</span>
          <div className="flex-1 mx-2 border-b border-dotted border-gray-300 dark:border-gray-600" />
          <span className={cn("text-xs font-medium", personaCount > 0 ? colors.textPrimary : colors.textMuted)}>
            {personaCount}
          </span>
        </div>
        
        {/* KPIs */}
        <div className="flex items-center justify-between">
          <span className={cn("text-xs", colors.textMuted)}>KPIs</span>
          <div className="flex-1 mx-2 border-b border-dotted border-gray-300 dark:border-gray-600" />
          <span className="text-xs font-medium">
            <span className={kpiSubscribedCount > 0 ? "text-green-600 dark:text-green-400" : "text-red-500 dark:text-red-400"}>
              {kpiSubscribedCount}
            </span>
            <span className={colors.textMuted}>/{kpiAvailableCount}</span>
          </span>
        </div>
        
        {/* Requirements */}
        <div className="flex items-center justify-between">
          <span className={cn("text-xs", colors.textMuted)}>Requirements</span>
          <div className="flex-1 mx-2 border-b border-dotted border-gray-300 dark:border-gray-600" />
          <span className={cn("text-xs font-medium", requirementCount > 0 ? colors.textPrimary : colors.textMuted)}>
            {requirementCount}
          </span>
        </div>
        
        {/* Level of Effort */}
        <div className="flex items-center justify-between">
          <span className={cn("text-xs", colors.textMuted)}>Level of Effort</span>
          <div className="flex-1 mx-2 border-b border-dotted border-gray-300 dark:border-gray-600" />
          <span className={cn(
            "text-xs font-medium px-1.5 py-0.5 rounded",
            effortSize !== '-' 
              ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400" 
              : colors.textMuted
          )}>
            {effortSize}
          </span>
        </div>
      </div>
      
      {/* Footer Section */}
      <div className={cn(
        "px-4 py-3 border-t flex-shrink-0 mt-auto h-[52px] flex items-center justify-between",
        colors.borderPrimary, 
        colors.bgTertiary
      )}>
        <div className="flex items-center gap-2 min-w-0">
          <Layers className={cn("w-3.5 h-3.5 flex-shrink-0", colors.textMuted)} />
          <span className={cn("text-xs truncate", colors.textSecondary)}>
            {epicName}
          </span>
        </div>
        {/* Future: New Note indicator could go here */}
      </div>
    </Card>
  );
};

/**
 * EpicCard - Unified card design following Goals/Features pattern
 * 
 * Layout:
 * - Header: Icon | Name
 * - Tags: Target Date && Status
 * - Description: 2-line clamp
 * - Metrics: Personas, KPIs (Ns/Na), Features, Features Complete (with dotted lines)
 * - Footer: Initiative Name | On Track Icon
 */
const EpicCard = ({ 
  epic, 
  colors, 
  onSelect, 
  goals = [],
  initiatives = [],
  features = []
}) => {
  // Get status badge color
  const getStatusBadge = (status) => {
    const statusMap = {
      draft: { color: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300', label: 'Draft' },
      planning: { color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400', label: 'Planning' },
      in_progress: { color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400', label: 'In Progress' },
      shipped: { color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400', label: 'Shipped' },
      paused: { color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400', label: 'Paused' }
    };
    // Fallback to draft for unknown/legacy statuses (e.g., "active", "completed", "abandoned")
    return statusMap[status] || statusMap.draft;
  };

  // Get initiative name from the epic's direct initiative_id link
  // Note: Epics link directly to initiatives via initiative_id, NOT through goals
  const getInitiativeName = () => {
    if (!epic.initiative_id) return 'No Initiative';
    const initiative = initiatives.find(i => i.initiative_id === epic.initiative_id);
    return initiative?.title || 'Unknown Initiative';
  };

  // Calculate KPI metrics - subscribed vs available from parent initiative
  // Check if epic is on track (all features in good status)
  const getTrackingStatus = () => {
    if (features.length === 0) return 'neutral';
    const completedCount = features.filter(f => f.status === 'Done').length;
    const blockedCount = features.filter(f => f.is_blocked).length;
    
    if (blockedCount > 0) return 'at-risk';
    if (completedCount === features.length) return 'completed';
    if (completedCount > 0) return 'on-track';
    return 'neutral';
  };

  // Count metrics
  const personaCount = epic.persona_ids?.length || 0;
  const kpiSubscribedCount = epic.subscribed_kpi_ids?.length || 0;
  const kpiAvailableCount = epic.available_kpi_count ?? 0; // From backend (filtered count)
  const featureCount = features.length;
  const featuresCompleteCount = features.filter(f => f.status === 'Done').length;
  
  const statusBadge = getStatusBadge(epic.status);
  const initiativeName = getInitiativeName();
  const trackingStatus = getTrackingStatus();

  // Tracking status icon
  const TrackingIcon = () => {
    switch (trackingStatus) {
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'on-track':
        return <TrendingUp className="w-4 h-4 text-blue-500" />;
      case 'at-risk':
        return <AlertTriangle className="w-4 h-4 text-red-500" />;
      default:
        return <Clock className="w-4 h-4 text-gray-400" />;
    }
  };

  return (
    <Card 
      className={cn(
        "border cursor-pointer transition-all hover:shadow-lg hover:ring-2 hover:ring-blue-500/30 overflow-hidden flex flex-col",
        colors.bgCard, 
        colors.borderPrimary
      )}
      onClick={() => onSelect(epic)}
    >
      {/* Header Section */}
      <div className="p-4 pb-2 flex-shrink-0">
        <div className="flex items-start gap-3">
          <div className={cn(
            "w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0",
            colors.bgTertiary
          )}>
            <Layers className={cn("w-5 h-5", colors.textAccent)} />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className={cn("font-semibold text-base leading-tight line-clamp-1", colors.textPrimary)}>
              {epic.title}
            </h3>
            {/* Tags Row */}
            <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
              {epic.target_date && (
                <span className="px-2 py-0.5 rounded-full text-xs bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400">
                  {new Date(epic.target_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </span>
              )}
              <span className={cn("px-2 py-0.5 rounded-full text-xs", statusBadge.color)}>
                {statusBadge.label}
              </span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Description Section */}
      <div className={cn("px-4 h-12 flex-shrink-0", colors.textSecondary)}>
        <p className="text-sm line-clamp-2">
          {epic.description || 'No description provided'}
        </p>
      </div>
      
      {/* Metrics Section */}
      <div className={cn("px-4 py-3 flex-1 border-t space-y-1.5", colors.borderPrimary)}>
        {/* Personas */}
        <div className="flex items-center justify-between">
          <span className={cn("text-xs", colors.textMuted)}>Personas</span>
          <div className="flex-1 mx-2 border-b border-dotted border-gray-300 dark:border-gray-600" />
          <span className={cn("text-xs font-medium", personaCount > 0 ? colors.textPrimary : colors.textMuted)}>
            {personaCount}
          </span>
        </div>
        
        {/* KPIs */}
        <div className="flex items-center justify-between">
          <span className={cn("text-xs", colors.textMuted)}>KPIs</span>
          <div className="flex-1 mx-2 border-b border-dotted border-gray-300 dark:border-gray-600" />
          <span className="text-xs font-medium">
            <span className={kpiSubscribedCount > 0 ? "text-green-600 dark:text-green-400" : colors.textMuted}>
              {kpiSubscribedCount}
            </span>
            {kpiAvailableCount > 0 && (
              <span className={colors.textMuted}>/{kpiAvailableCount}</span>
            )}
          </span>
        </div>
        
        {/* Features */}
        <div className="flex items-center justify-between">
          <span className={cn("text-xs", colors.textMuted)}>Features</span>
          <div className="flex-1 mx-2 border-b border-dotted border-gray-300 dark:border-gray-600" />
          <span className={cn("text-xs font-medium", featureCount > 0 ? colors.textPrimary : colors.textMuted)}>
            {featureCount}
          </span>
        </div>
        
        {/* Features Complete */}
        <div className="flex items-center justify-between">
          <span className={cn("text-xs", colors.textMuted)}>Features Complete</span>
          <div className="flex-1 mx-2 border-b border-dotted border-gray-300 dark:border-gray-600" />
          <span className={cn(
            "text-xs font-medium",
            featuresCompleteCount === featureCount && featureCount > 0
              ? "text-green-600 dark:text-green-400"
              : featuresCompleteCount > 0 
                ? colors.textPrimary 
                : colors.textMuted
          )}>
            {featuresCompleteCount}
          </span>
        </div>
      </div>
      
      {/* Footer Section */}
      <div className={cn(
        "px-4 py-3 border-t flex-shrink-0 mt-auto h-[52px] flex items-center justify-between",
        colors.borderPrimary, 
        colors.bgTertiary
      )}>
        <div className="flex items-center gap-2 min-w-0">
          <Compass className={cn("w-3.5 h-3.5 flex-shrink-0", colors.textMuted)} />
          <span className={cn("text-xs truncate", colors.textSecondary)}>
            {initiativeName}
          </span>
        </div>
        <TrackingIcon />
      </div>
    </Card>
  );
};

const GoalCard = ({ goal, colors, onSelect, isArchived = false, initiatives = [] }) => {
  // Get investment badge config
  const getInvestmentBadge = (metrics) => {
    const badge = metrics?.investment_badge || 'low';
    const pct = metrics?.investment_pct || 0;
    
    const badges = {
      high: { icon: '🔥', label: 'High Bet', color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' },
      medium: { icon: '💡', label: 'Medium', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
      low: { icon: '🌱', label: 'Low', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' }
    };
    
    return { ...badges[badge], pct };
  };

  // Status badge color
  const getStatusBadge = (status) => {
    const statusMap = {
      draft: { color: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300', label: 'Draft' },
      active: { color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400', label: 'Active' },
      achieved: { color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400', label: 'Achieved' },
      abandoned: { color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', label: 'Abandoned' }
    };
    return statusMap[status] || statusMap.draft;
  };

  const metrics = goal.computed_metrics;
  const investmentBadge = getInvestmentBadge(metrics);
  const executionProgress = metrics?.execution_progress || 0;
  const blockedCount = metrics?.blocked_count || 0;
  const statusBadge = getStatusBadge(goal.status);
  const kpisComputed = metrics?.kpis_computed || [];
  const overallStatus = metrics?.kpi_status || 'not_set';
  
  // Count initiatives that reference this goal
  const initiativeCount = initiatives.filter(i => 
    i.goal_id === goal.goal_id || i.goal_ids?.includes(goal.goal_id)
  ).length;
  
  return (
    <Card 
      className={cn(
        "border cursor-pointer transition-all hover:shadow-lg hover:ring-2 hover:ring-purple-500/30 overflow-hidden flex flex-col",
        colors.bgCard, 
        colors.borderPrimary,
        isArchived && "opacity-70 hover:opacity-100"
      )}
      onClick={() => onSelect(goal)}
    >
      {/* Header Section - Fixed height */}
      <div className="p-4 flex-shrink-0">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0 flex-1">
            <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0", colors.bgTertiary)}>
              <Target className={cn("w-5 h-5", colors.textAccent)} />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className={cn("font-semibold text-base leading-tight line-clamp-1", colors.textPrimary)}>{goal.title}</h3>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <span className={cn("text-xs px-2 py-0.5 rounded-full", statusBadge.color)}>
                  {statusBadge.label}
                </span>
                <span className={cn("px-2 py-0.5 rounded-full text-xs font-medium", investmentBadge.color)}>
                  {investmentBadge.icon} {investmentBadge.pct}%
                </span>
                {/* On Track / Off Track badge - only show for active goals */}
                {goal.status === 'active' && overallStatus !== 'not_set' && (
                  <span className={cn(
                    "text-xs px-2 py-0.5 rounded-full flex items-center gap-1",
                    overallStatus === 'on_track' 
                      ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" 
                      : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
                  )}>
                    <TrendingUp className={cn("w-3 h-3", overallStatus === 'off_track' && "rotate-180")} />
                    {overallStatus === 'on_track' ? 'On Track' : 'Off Track'}
                  </span>
                )}
                {goal.is_shifted && (
                  <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                    ⚠️ Shifted
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Description Section - Fixed height with line clamp */}
      <div className={cn("px-4 h-12 flex-shrink-0", colors.textSecondary)}>
        <p className="text-sm line-clamp-2">
          {goal.description || 'No description provided'}
        </p>
      </div>
      
      {/* Progress Section - Flexible height to fill space */}
      <div className={cn("px-4 py-3 flex-1 border-t", colors.borderPrimary)}>
        {/* Execution Progress */}
        <div className="mb-2">
          <div className="flex items-center justify-between mb-1">
            <span className={cn("text-xs font-medium", colors.textSecondary)}>Execution</span>
            <span className={cn("text-xs", colors.textMuted)}>{executionProgress}%</span>
          </div>
          <div className="w-full h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-full transition-all"
              style={{ width: `${executionProgress}%` }}
            />
          </div>
        </div>
        
        {/* KPI Progress Bars (up to 3) - Show for all goals but only fill progress for Active */}
        {kpisComputed.slice(0, 3).map((kpi, idx) => {
          const isActive = goal.status === 'active';
          return (
            <div key={kpi.kpi_id || idx} className="mb-1 last:mb-0">
              <div className="flex items-center justify-between mb-0.5">
                <span className={cn("text-xs truncate max-w-[60%]", colors.textMuted)}>{kpi.metric_name}</span>
                {isActive ? (
                  <span className={cn("text-xs", colors.textMuted)}>
                    {formatGoalMetricValue(kpi.current_value, kpi.metric_format)} / {formatGoalMetricValue(kpi.target_value, kpi.metric_format)}
                  </span>
                ) : (
                  <span className={cn("text-xs", colors.textMuted)}>
                    {formatGoalMetricValue(kpi.target_value, kpi.metric_format)}
                  </span>
                )}
              </div>
              <div className="w-full h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                {isActive && (
                  <div 
                    className={cn(
                      "h-full rounded-full transition-all",
                      kpi.status === 'on_track' ? "bg-green-500" : "bg-red-500"
                    )}
                    style={{ width: `${Math.min(100, kpi.progress || 0)}%` }}
                  />
                )}
              </div>
            </div>
          );
        })}
        
        {blockedCount > 0 && (
          <div className="flex items-center gap-1 mt-2 text-amber-600 dark:text-amber-400">
            <AlertTriangle className="w-3 h-3" />
            <span className="text-xs">{blockedCount} blocked</span>
          </div>
        )}
      </div>
      
      {/* Footer Section - Always at bottom, aligned across cards */}
      <div className={cn("px-4 py-3 border-t flex-shrink-0 mt-auto h-[52px] flex items-center justify-between", colors.borderPrimary, colors.bgTertiary)}>
        <span className={cn("text-xs", colors.textSecondary)}>
          {goal.kpis?.length || 0} KPI{(goal.kpis?.length || 0) !== 1 ? 's' : ''}
        </span>
        <span className={cn("text-xs", initiativeCount > 0 ? colors.textPrimary : colors.textMuted)}>
          Initiatives {initiativeCount}
        </span>
      </div>
    </Card>
  );
};


// ==================== ADD GOAL MODAL (Quick Entry) ====================
const AddGoalModal = ({ isOpen, onClose, onSave, productId }) => {
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    status: 'draft'
  });

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData({ title: '', description: '', status: 'draft' });
    }
  }, [isOpen]);

  const handleCreate = async () => {
    if (!formData.title.trim()) {
      toast.error('Goal title is required');
      return;
    }
    
    setSaving(true);
    try {
      await goalsApi.create(productId, formData);
      toast.success('Goal created!');
      if (onSave) await onSave();
      onClose();
    } catch (err) {
      console.error('Failed to create goal:', err);
      // Handle FastAPI validation errors (detail can be string or array)
      const detail = err.response?.data?.detail;
      const errorMsg = typeof detail === 'string' 
        ? detail 
        : Array.isArray(detail) 
          ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ')
          : 'Failed to create goal';
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={cn(colors.bgCard, "max-w-lg overflow-hidden")}>
        <DialogHeader>
          <DialogTitle className={cn("flex items-center gap-2", colors.textPrimary)}>
            <Target className="w-5 h-5 text-amber-500" />
            Add Goal
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-5 py-4">
          <div>
            <label className={cn("text-sm font-medium", colors.textSecondary)}>Title *</label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="e.g., Increase user retention, Reduce churn"
              className={cn("mt-1.5", colors.bgInput, colors.borderPrimary)}
              data-testid="goal-title-input"
            />
          </div>
          
          <div>
            <label className={cn("text-sm font-medium", colors.textSecondary)}>Description</label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Describe the strategic objective and why it matters..."
              rows={5}
              className={cn("mt-1.5", colors.bgInput, colors.borderPrimary)}
            />
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button 
            onClick={handleCreate}
            disabled={!formData.title.trim() || saving}
            className={cn(
              colors.accentGradient,
              (!formData.title.trim() && !saving) && "opacity-50 cursor-not-allowed"
            )}
            data-testid="create-goal-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
            Create Goal
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


// ==================== ADD INITIATIVE MODAL (Quick Entry) ====================
const AddInitiativeModal = ({ isOpen, onClose, onSave, productId, goals = [] }) => {
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    goal_id: '',
    status: 'draft'
  });

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData({ 
        title: '', 
        description: '', 
        goal_id: goals[0]?.goal_id || '',
        status: 'draft'
      });
    }
  }, [isOpen, goals]);

  const handleCreate = async () => {
    if (!formData.title.trim()) {
      toast.error('Initiative title is required');
      return;
    }
    if (!formData.goal_id) {
      toast.error('Please select a goal');
      return;
    }
    
    setSaving(true);
    try {
      await initiativesApi.create(formData.goal_id, formData);
      toast.success('Initiative created!');
      if (onSave) await onSave();
      onClose();
    } catch (err) {
      console.error('Failed to create initiative:', err);
      toast.error(err.response?.data?.detail || 'Failed to create initiative');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={cn(colors.bgCard, "max-w-lg overflow-hidden")}>
        <DialogHeader>
          <DialogTitle className={cn("flex items-center gap-2", colors.textPrimary)}>
            <Rocket className="w-5 h-5 text-indigo-500" />
            Add Initiative
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-5 py-4 max-w-full">
          <div className="min-w-0">
            <label className={cn("text-sm font-medium", colors.textSecondary)}>Title *</label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="e.g., Q1 Mobile App Redesign"
              className={cn("mt-1.5", colors.bgInput, colors.borderPrimary)}
              data-testid="initiative-title-input"
            />
          </div>
          
          <div className="min-w-0">
            <label className={cn("text-sm font-medium", colors.textSecondary)}>Goal *</label>
            <Select 
              value={formData.goal_id} 
              onValueChange={(value) => setFormData({ ...formData, goal_id: value })}
            >
              <SelectTrigger className={cn("mt-1.5 w-full overflow-hidden", colors.bgInput, colors.borderPrimary)}>
                <SelectValue placeholder="Select a goal..." />
              </SelectTrigger>
              <SelectContent className={cn(colors.bgCard, colors.borderPrimary)}>
                {goals.length === 0 ? (
                  <SelectItem value="no-goals" disabled>No goals available - create one first</SelectItem>
                ) : (
                  goals.map(goal => (
                    <SelectItem key={goal.goal_id} value={goal.goal_id}>
                      {goal.title}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
            {goals.length === 0 && (
              <p className={cn("text-xs mt-1.5", colors.textMuted)}>
                You need to create a goal before adding initiatives.
              </p>
            )}
          </div>
          
          <div className="min-w-0">
            <label className={cn("text-sm font-medium", colors.textSecondary)}>Description</label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Describe what this initiative aims to achieve..."
              rows={4}
              className={cn("mt-1.5", colors.bgInput, colors.borderPrimary)}
            />
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button 
            onClick={handleCreate}
            disabled={!formData.title.trim() || !formData.goal_id || saving}
            className={cn(
              colors.accentGradient,
              ((!formData.title.trim() || !formData.goal_id) && !saving) && "opacity-50 cursor-not-allowed"
            )}
            data-testid="create-initiative-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
            Create Initiative
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


// ==================== GOALS TAB ====================
const GoalsTab = ({ goals, productId, canEdit, canUseAI, refresh, productContext, searchNavTarget, onSearchNavHandled, initiatives = [], markStale, getCascadeTargets }) => {
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAchieved, setShowAchieved] = useState(false);
  const [showAbandoned, setShowAbandoned] = useState(false);
  const [lastHandledNavId, setLastHandledNavId] = useState(null);

  // Handle search navigation
  useEffect(() => {
    if (searchNavTarget?.type === 'goal' && searchNavTarget.id !== lastHandledNavId) {
      const goal = goals.find(g => g.goal_id === searchNavTarget.id);
      if (goal) {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setSelectedGoal(goal);
      }
      setLastHandledNavId(searchNavTarget.id);
      onSearchNavHandled?.();
    }
  }, [searchNavTarget, goals, lastHandledNavId, onSearchNavHandled]);

  // Filter goals by search and status
  const filterBySearch = (g) => 
    g.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    g.description?.toLowerCase().includes(searchQuery.toLowerCase());

  // Separate goals by status
  const activeGoals = goals.filter(g => 
    filterBySearch(g) && 
    (g.status !== 'achieved' && g.status !== 'abandoned') &&
    (statusFilter === 'all' || statusFilter === g.status)
  );
  
  const achievedGoals = goals.filter(g => 
    filterBySearch(g) && g.status === 'achieved'
  );
  
  const abandonedGoals = goals.filter(g => 
    filterBySearch(g) && g.status === 'abandoned'
  );

  // Auto-expand sections when filtering to those statuses
  React.useEffect(() => {
    if (statusFilter === 'achieved') {
      setShowAchieved(true);
    } else if (statusFilter === 'abandoned') {
      setShowAbandoned(true);
    }
  }, [statusFilter]);

  return (
    <div className="flex flex-row h-full">
      {/* Main Content */}
      <div className="flex-1 p-6 overflow-y-auto">
        <div className="max-w-6xl mx-auto">
          {/* Header with filters */}
          {goals.length > 0 && (
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center", colors.bgTertiary)}>
                <Target className={cn("w-5 h-5", colors.textAccent)} />
              </div>
              <div>
                <h2 className={cn("text-xl font-semibold", colors.textPrimary)}>
                  Goals
                  <span className={cn("ml-2 text-sm font-normal", colors.textMuted)}>({goals.length})</span>
                </h2>
                <p className={cn("text-sm", colors.textMuted)}>Strategic objectives and key results</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {/* Search Input */}
              <div className="relative">
                <Search className={cn("w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2", colors.textMuted)} />
                <Input 
                  placeholder="Search goals..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={cn("pl-9 w-72", colors.bgInput, colors.borderPrimary)}
                />
              </div>
              
              {/* Status Filter */}
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className={cn("w-36", colors.bgInput, colors.borderPrimary)}>
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent className={cn(colors.bgCard, colors.borderPrimary)}>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="achieved">Achieved</SelectItem>
                  <SelectItem value="abandoned">Abandoned</SelectItem>
                </SelectContent>
              </Select>
              
              {canEdit && (
                <Button 
                  className={cn("gap-2", colors.accentGradient)}
                  onClick={() => setShowCreateModal(true)}
                >
                  <Plus className="w-4 h-4" />
                  Add Goal
                </Button>
              )}
            </div>
          </div>
          )}
          
          {/* Active Goals Grid - 2 columns */}
          {(statusFilter === 'all' || (statusFilter !== 'achieved' && statusFilter !== 'abandoned')) && (
            <>
              {activeGoals.length === 0 && achievedGoals.length === 0 && abandonedGoals.length === 0 ? (
                <div className={cn("text-center py-12 px-6 rounded-lg border", colors.bgTertiary, colors.borderPrimary)}>
                  <Target className={cn("w-12 h-12 mx-auto mb-4", colors.textMuted)} />
                  <h3 className={cn("text-lg font-semibold mb-4", colors.textPrimary)}>No goals defined yet</h3>
                  <div className={cn("text-sm mb-6 max-w-2xl mx-auto text-left space-y-4", colors.textMuted)}>
                    <p>
                      <strong className={colors.textPrimary}>Define Winning. Drive Agreement. Move Fast.</strong>
                      <br />
                      Goals are your specific, measurable commitments to the business. They go beyond vague vision statements by anchoring your strategy to concrete KPIs, fixed Target Dates, and clear Investment Levels. This defines exactly what &ldquo;winning&rdquo; looks like before the first line of code is written.
                    </p>
                    <p>
                      <strong className={colors.textPrimary}>Great goals drive the alignment required to move at speed.</strong>
                      <br />
                      Ambiguity is the enemy of execution; once a team truly agrees on the target, they can stop debating and start shipping. By establishing clear goals upfront, you eliminate the friction of shifting priorities and empower your team to focus entirely on delivery.
                    </p>
                    <p>
                      <strong className={colors.textPrimary}>We track the Goal Stability to help the organization &ldquo;guess better&rdquo; every quarter.</strong>
                      <br />
                      We know targets evolve due to executive pivots, collaboration, and new market data. By measuring how and when these shifts happen, StackRanked helps you identify planning patterns and improve your forecasting maturity - turning the art of the &ldquo;guess&rdquo; into a repeatable, predictable science.
                    </p>
                  </div>
                  {canEdit && (
                    <Button 
                      className={cn(colors.accentGradient)}
                      onClick={() => setShowCreateModal(true)}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Create Your First Goal
                    </Button>
                  )}
                </div>
              ) : activeGoals.length === 0 ? (
                <div className={cn("text-center py-8 mb-6", colors.textMuted)}>
                  <p className="text-sm">No active goals found{searchQuery && ` matching "${searchQuery}"`}</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8 items-stretch">
                  {activeGoals.map(goal => (
                    <GoalCard key={goal.goal_id} goal={goal} colors={colors} onSelect={setSelectedGoal} initiatives={initiatives} />
                  ))}
                </div>
              )}
            </>
          )}
          
          {/* Achieved Goals Section - Collapsible */}
          {achievedGoals.length > 0 && (statusFilter === 'all' || statusFilter === 'achieved') && (
            <div className="mt-8">
              <button
                onClick={() => setShowAchieved(!showAchieved)}
                className={cn(
                  "flex items-center gap-2 text-sm font-medium mb-4 transition-colors",
                  colors.textSecondary,
                  "hover:text-gray-900 dark:hover:text-gray-100"
                )}
              >
                {showAchieved ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                <Trophy className="w-4 h-4 text-green-500" />
                Goals Achieved ({achievedGoals.length})
              </button>
              
              {showAchieved && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch">
                  {achievedGoals.map(goal => (
                    <GoalCard key={goal.goal_id} goal={goal} colors={colors} onSelect={setSelectedGoal} isArchived initiatives={initiatives} />
                  ))}
                </div>
              )}
            </div>
          )}
          
          {/* Abandoned Goals Section - Collapsible */}
          {abandonedGoals.length > 0 && (statusFilter === 'all' || statusFilter === 'abandoned') && (
            <div className="mt-8">
              <button
                onClick={() => setShowAbandoned(!showAbandoned)}
                className={cn(
                  "flex items-center gap-2 text-sm font-medium mb-4 transition-colors",
                  colors.textSecondary,
                  "hover:text-gray-900 dark:hover:text-gray-100"
                )}
              >
                {showAbandoned ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                <Archive className="w-4 h-4 text-gray-500" />
                Goals Abandoned ({abandonedGoals.length})
              </button>
              
              {showAbandoned && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch">
                  {abandonedGoals.map(goal => (
                    <GoalCard key={goal.goal_id} goal={goal} colors={colors} onSelect={setSelectedGoal} isArchived initiatives={initiatives} />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* AI Side Drawer */}
      {canUseAI && goals.length > 0 && <CollectionCopilotDrawer 
        context="goals" 
        contextId={productId} 
        title="Goals Assistant"
        onRefresh={() => refresh('collection', 'goals')}
       
      />}
      
      {/* Goal Detail Modal (Edit mode) */}
      <GoalDetailModal
        isOpen={!!selectedGoal}
        onClose={() => setSelectedGoal(null)}
        goal={selectedGoal}
        productContext={productContext}
        onSave={(savedGoal, changeInfo = {}) => {
          if (savedGoal?.goal_id) {
            // Entity-level refresh with optimistic data
            refresh('entity', 'goals', savedGoal);
          } else {
            // Fallback to collection refresh
            refresh('collection', 'goals');
          }
          // Handle cascading updates for KPI changes
          if (changeInfo.kpisChanged) {
            markStale(getCascadeTargets('goals', 'kpiChange'));
          }
        }}
        onSaveAndClose={(savedGoal, changeInfo = {}) => {
          setSelectedGoal(null);
          if (savedGoal?.goal_id) {
            refresh('entity', 'goals', savedGoal);
          } else {
            refresh('collection', 'goals');
          }
          // Handle cascading updates for KPI changes
          if (changeInfo.kpisChanged) {
            markStale(getCascadeTargets('goals', 'kpiChange'));
          }
        }}
        onDelete={() => {
          setSelectedGoal(null);
          refresh('collection', 'goals');
          // Goal deletion affects initiatives
          markStale(getCascadeTargets('goals', 'delete'));
        }}
        onInitiativeChange={() => {
          // Refresh initiatives when linking/unlinking
          refresh('collection', 'initiatives');
        }}
        onKpiChange={() => {
          // KPI added/deleted/modified - cascade to downstream collections
          markStale(getCascadeTargets('goals', 'kpiChange'));
        }}
        mode="edit"
      />
      
      {/* Add Goal Modal (Quick Entry) */}
      <AddGoalModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSave={() => refresh('collection', 'goals')}
        productId={productId}
      />
    </div>
  );
};


// ==================== INITIATIVES TAB ====================
const InitiativesTab = ({ productId, goals, initiatives = [], personas = [], canEdit, canUseAI, refresh, searchNavTarget, onSearchNavHandled, markStale, getCascadeTargets, productContext = {} }) => {
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();
  
  // Modal state
  const [selectedInitiative, setSelectedInitiative] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [lastHandledNavId, setLastHandledNavId] = useState(null);
  
  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [goalFilter, setGoalFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState([]);
  
  // Available statuses for initiatives
  const INITIATIVE_STATUSES = [
    { value: 'draft', label: 'Draft', color: 'bg-gray-400' },
    { value: 'planning', label: 'Planning', color: 'bg-blue-400' },
    { value: 'in_progress', label: 'In Progress', color: 'bg-amber-400' },
    { value: 'shipped', label: 'Shipped', color: 'bg-green-400' },
    { value: 'paused', label: 'Paused', color: 'bg-purple-400' },
  ];
  
  // Handle search navigation
  useEffect(() => {
    if (searchNavTarget?.type === 'initiative' && searchNavTarget.id !== lastHandledNavId) {
      const initiative = initiatives.find(i => i.initiative_id === searchNavTarget.id);
      if (initiative) {
        setSelectedInitiative(initiative);
      } else {
        // Fetch directly if not in list
        initiativesApi.get(searchNavTarget.id).then(res => {
          if (res.data) setSelectedInitiative(res.data);
        }).catch(console.error);
      }
      setLastHandledNavId(searchNavTarget.id);
      onSearchNavHandled?.();
    }
  }, [searchNavTarget, initiatives, lastHandledNavId, onSearchNavHandled]);
  
  // Filter initiatives
  const filteredInitiatives = initiatives.filter(initiative => {
    const matchesSearch = initiative.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          initiative.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesGoal = goalFilter === 'all' || 
                        initiative.goal_id === goalFilter ||
                        initiative.goal_ids?.includes(goalFilter);
    const matchesStatus = statusFilter.length === 0 || statusFilter.includes(initiative.status);
    return matchesSearch && matchesGoal && matchesStatus;
  });
  
  // Check if any filters are active
  const hasActiveFilters = searchQuery || goalFilter !== 'all' || statusFilter.length > 0;
  
  return (
    <div className="flex flex-row h-full">
      {/* Main Content */}
      <div className="flex-1 p-6 overflow-y-auto">
        <div className="max-w-6xl mx-auto">
          {initiatives.length > 0 && (
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className={cn("text-2xl font-semibold", colors.textPrimary)}>Initiatives</h2>
              <p className={cn("text-sm mt-1", colors.textSecondary)}>
                Strategic bets and major workstreams
              </p>
            </div>
            <div className="flex items-center gap-3">
              {/* Search Input */}
              <div className="relative">
                <Search className={cn("w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2", colors.textMuted)} />
                <Input 
                  placeholder="Search initiatives..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={cn("pl-9 w-56", colors.bgInput, colors.borderPrimary)}
                  data-testid="initiatives-search-input"
                />
              </div>
              
              {/* Goal Filter */}
              <Select value={goalFilter} onValueChange={setGoalFilter}>
                <SelectTrigger className={cn("w-40", colors.bgInput, colors.borderPrimary)} data-testid="initiatives-goal-filter">
                  <SelectValue placeholder="Filter by goal" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Goals</SelectItem>
                  {goals.map(g => (
                    <SelectItem key={g.goal_id} value={g.goal_id}>{g.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {/* Status Filter (Multi-select) */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="outline" 
                    className={cn("gap-2", colors.bgInput, colors.borderPrimary)}
                    data-testid="initiatives-status-filter"
                  >
                    <Filter className="w-4 h-4" />
                    Status {statusFilter.length > 0 && `(${statusFilter.length})`}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className={cn("w-48", colors.bgCard)}>
                  {INITIATIVE_STATUSES.map(status => (
                    <DropdownMenuCheckboxItem
                      key={status.value}
                      checked={statusFilter.includes(status.value)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setStatusFilter(prev => [...prev, status.value]);
                        } else {
                          setStatusFilter(prev => prev.filter(s => s !== status.value));
                        }
                      }}
                    >
                      <span className={cn("w-2 h-2 rounded-full mr-2", status.color)} />
                      {status.label}
                    </DropdownMenuCheckboxItem>
                  ))}
                  {statusFilter.length > 0 && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => setStatusFilter([])}>
                        Clear all
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
              
              {/* Clear Filters */}
              {hasActiveFilters && (
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => {
                    setSearchQuery('');
                    setGoalFilter('all');
                    setStatusFilter([]);
                  }}
                  className={cn("text-xs px-2", colors.textMuted)}
                >
                  Clear
                </Button>
              )}
              
              {canEdit && (
                <Button 
                  className={cn("gap-2", colors.accentGradient)}
                  onClick={() => setShowCreateModal(true)}
                >
                  <Plus className="w-4 h-4" />
                  Add Initiative
                </Button>
              )}
            </div>
          </div>
          )}
          
          {initiatives.length === 0 ? (
            <div className={cn("text-center py-12 px-6 rounded-lg border", colors.bgTertiary, colors.borderPrimary)}>
              <Rocket className={cn("w-12 h-12 mx-auto mb-4", colors.textMuted)} />
              <h3 className={cn("text-lg font-semibold mb-4", colors.textPrimary)}>No initiatives yet</h3>
              <div className={cn("text-sm mb-6 max-w-2xl mx-auto text-left space-y-4", colors.textMuted)}>
                <p>
                  <strong className={colors.textPrimary}>Initiatives are where you define how you will achieve your Goal KPIs.</strong>
                  <br />
                  You can build them structurally (by grouping Epics) or dynamically (by tracking Tags across features). Link them to specific KPI targets to measure exactly how much value you are delivering against your goals.
                </p>
                <p>
                  <strong className={colors.textPrimary}>For your roadmap, Initiatives act as your market commitments.</strong>
                  <br />
                  They abstract the complexity of engineering tasks into understandable milestones, ensuring stakeholders focus on the value you are shipping, rather than the code you are writing.
                </p>
                <p>
                  <strong className={colors.textPrimary}>Ultimately, this anchors your execution data to your strategic intent.</strong>
                  <br />
                  By bridging the gap between Epics and KPIs, Initiatives create a live translation layer for your organization - ensuring that every feature you ship is no longer just a completed ticket, but visible progress toward a market-facing promise.
                </p>
              </div>
              {canEdit && (
                <Button onClick={() => setShowCreateModal(true)} className={cn(colors.accentGradient)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Initiative
                </Button>
              )}
            </div>
          ) : filteredInitiatives.length === 0 ? (
            <div className={cn("text-center py-12 px-6 rounded-lg border", colors.bgTertiary, colors.borderPrimary)}>
              <Search className={cn("w-12 h-12 mx-auto mb-4", colors.textMuted)} />
              <h3 className={cn("text-lg font-semibold mb-2", colors.textPrimary)}>No matching initiatives</h3>
              <p className={cn("text-sm mb-4", colors.textMuted)}>
                Try adjusting your search or filters
              </p>
              <Button 
                variant="outline" 
                onClick={() => {
                  setSearchQuery('');
                  setGoalFilter('all');
                  setStatusFilter([]);
                }}
              >
                Clear Filters
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredInitiatives.map(initiative => (
                <InitiativeCard
                  key={initiative.initiative_id}
                  initiative={initiative}
                  colors={colors}
                  onSelect={setSelectedInitiative}
                  goals={goals}
                />
              ))}
            </div>
          )}
        </div>
      </div>
      
      {/* AI Bottom Panel */}
      {canUseAI && initiatives.length > 0 && <CollectionCopilotDrawer 
        context="initiatives" 
        contextId={productId} 
        title="Initiative Planner"
        onRefresh={() => refresh('collection', 'initiatives')}
      />}
      
      {/* Edit Modal */}
      {selectedInitiative && (
        <InitiativeDetailModal
          isOpen={!!selectedInitiative}
          onClose={() => setSelectedInitiative(null)}
          initiative={selectedInitiative}
          goals={goals}
          personas={personas}
          productContext={productContext}
          mode="edit"
          onSave={(savedInitiative, changeInfo = {}) => {
            if (savedInitiative?.initiative_id) {
              // Entity-level refresh with optimistic data
              refresh('entity', 'initiatives', savedInitiative);
            } else if (changeInfo.dirtyCollections) {
              // Child modal closed with dirty collections — mark them stale
              markStale(changeInfo.dirtyCollections);
              // Targeted refresh of this initiative to get updated computed counts
              if (changeInfo.entityId) {
                refresh('entity', 'initiatives', changeInfo.entityId);
              }
            } else {
              // Fallback to collection refresh
              refresh('collection', 'initiatives');
            }
            // Handle cascading updates for KPI subscription changes
            if (changeInfo.kpisChanged) {
              markStale(getCascadeTargets('initiatives', 'kpiChange'));
            }
            // Handle cascading updates for follow_type changes (epics were unlinked)
            if (changeInfo.followTypeChanged && changeInfo.previousFollowType === 'epics') {
              markStale(['epics']);
            }
          }}
          onSaveAndClose={(savedInitiative, changeInfo = {}) => {
            setSelectedInitiative(null);
            if (savedInitiative?.initiative_id) {
              refresh('entity', 'initiatives', savedInitiative);
            } else if (changeInfo.dirtyCollections) {
              markStale(changeInfo.dirtyCollections);
              if (changeInfo.entityId) {
                refresh('entity', 'initiatives', changeInfo.entityId);
              }
            } else {
              refresh('collection', 'initiatives');
            }
            // Handle cascading updates for KPI subscription changes
            if (changeInfo.kpisChanged) {
              markStale(getCascadeTargets('initiatives', 'kpiChange'));
            }
            // Handle cascading updates for follow_type changes (epics were unlinked)
            if (changeInfo.followTypeChanged && changeInfo.previousFollowType === 'epics') {
              markStale(['epics']);
            }
          }}
          onDelete={() => {
            setSelectedInitiative(null);
            refresh('collection', 'initiatives');
            // Initiative deletion affects epics
            markStale(getCascadeTargets('initiatives', 'delete'));
          }}
          onGoalChange={() => {
            // Refresh goals when goals are linked/unlinked from initiative
            refresh('collection', 'goals');
          }}
          onKpiChange={() => {
            // KPI subscriptions changed - cascade to downstream collections
            markStale(getCascadeTargets('initiatives', 'kpiChange'));
          }}
        />
      )}
      
      {/* Add Initiative Modal (Quick Entry) */}
      <AddInitiativeModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSave={() => refresh('collection', 'initiatives')}
        productId={productId}
        goals={goals}
      />
    </div>
  );
};


// ==================== ADD EPIC MODAL ====================
const AddEpicModal = ({ isOpen, onClose, onSave, productId, initiatives = [] }) => {
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    initiative_id: '',
    status: 'draft'
  });

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData({ 
        title: '', 
        description: '', 
        initiative_id: '',
        status: 'draft'
      });
    }
  }, [isOpen]);

  const handleCreate = async () => {
    if (!formData.title.trim()) {
      toast.error('Epic title is required');
      return;
    }
    
    setSaving(true);
    try {
      await epicsApi.createForProduct(productId, formData);
      toast.success('Epic created!');
      onSave();
      onClose();
    } catch (err) {
      console.error('Failed to create epic:', err);
      toast.error(err.response?.data?.detail || 'Failed to create epic');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={cn(colors.bgCard, "max-w-lg")}>
        <DialogHeader>
          <DialogTitle className={cn("flex items-center gap-2", colors.textPrimary)}>
            <Layers className="w-5 h-5 text-purple-500" />
            Add Epic
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-5 py-4">
          <div>
            <label className={cn("text-sm font-medium", colors.textSecondary)}>Title *</label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="e.g., User Authentication, Payment Processing"
              className={cn("mt-1.5", colors.bgInput, colors.borderPrimary)}
              data-testid="epic-title-input"
            />
          </div>
          
          <div>
            <label className={cn("text-sm font-medium", colors.textSecondary)}>Description</label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Describe the scope and purpose of this epic..."
              rows={4}
              className={cn("mt-1.5", colors.bgInput, colors.borderPrimary)}
            />
          </div>
          
          {initiatives.length > 0 && (
            <div>
              <label className={cn("text-sm font-medium", colors.textSecondary)}>Initiative (Optional)</label>
              <Select 
                value={formData.initiative_id || 'none'} 
                onValueChange={(value) => setFormData({ ...formData, initiative_id: value === 'none' ? '' : value })}
              >
                <SelectTrigger className={cn("mt-1.5", colors.bgInput, colors.borderPrimary)}>
                  <SelectValue placeholder="Select an initiative..." />
                </SelectTrigger>
                <SelectContent className={cn(colors.bgCard, colors.borderPrimary)}>
                  <SelectItem value="none">No Initiative</SelectItem>
                  {initiatives.map(init => (
                    <SelectItem key={init.initiative_id} value={init.initiative_id}>
                      {init.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button 
            onClick={handleCreate}
            disabled={!formData.title.trim() || saving}
            className={cn(
              colors.accentGradient,
              (!formData.title.trim() && !saving) && "opacity-50 cursor-not-allowed"
            )}
            data-testid="create-epic-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
            Create Epic
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ==================== EPICS TAB ====================
const EpicsTab = ({ epics, goals, initiatives = [], productId, canEdit, canUseAI, refresh, onSelectFeature, onSelectEpic, allFeatures = [], markStale, getCascadeTargets }) => {
  const { getThemeColors } = useSafeMode();
  const { isEnabled } = useFeatureFlags();
  const colors = getThemeColors();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [initiativeFilter, setInitiativeFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  
  // Section visibility state
  const [showActive, setShowActive] = useState(true);
  const [showArchived, setShowArchived] = useState(false);

  // Valid status options for filtering (same as dropdown)
  const STATUS_OPTIONS = [
    { value: 'draft', label: 'Draft' },
    { value: 'planning', label: 'Planning' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'shipped', label: 'Shipped' },
    { value: 'paused', label: 'Paused' },
  ];

  // Normalize status for filtering (handle legacy statuses)
  const normalizeStatus = (status) => {
    const validStatuses = STATUS_OPTIONS.map(o => o.value);
    return validStatuses.includes(status) ? status : 'draft';
  };

  // Separate epics into active and archived
  const { activeEpics, archivedEpics } = useMemo(() => {
    return {
      activeEpics: epics.filter(e => e.status !== 'archived'),
      archivedEpics: epics.filter(e => e.status === 'archived')
    };
  }, [epics]);

  // Derive features per epic from allFeatures (no local state needed)
  const epicFeatures = useMemo(() => {
    const featuresMap = {};
    for (const epic of epics) {
      featuresMap[epic.epic_id] = allFeatures.filter(f => f.epic_id === epic.epic_id);
    }
    return featuresMap;
  }, [epics, allFeatures]);

  // Get initiative for an epic (via direct initiative_id link)
  const getEpicInitiativeId = (epic) => {
    return epic.initiative_id || null;
  };

  // Filter active epics
  const filteredActiveEpics = activeEpics.filter(e => {
    const matchesSearch = e.title?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || normalizeStatus(e.status) === statusFilter;
    const matchesInitiative = initiativeFilter === 'all' || 
      (initiativeFilter === 'none' ? !e.initiative_id : e.initiative_id === initiativeFilter);
    return matchesSearch && matchesStatus && matchesInitiative;
  });

  // Filter archived epics
  const filteredArchivedEpics = archivedEpics.filter(e => {
    const matchesSearch = e.title?.toLowerCase().includes(searchQuery.toLowerCase());
    // Archived epics don't need status filter (they're all archived)
    const matchesInitiative = initiativeFilter === 'all' || 
      (initiativeFilter === 'none' ? !e.initiative_id : e.initiative_id === initiativeFilter);
    return matchesSearch && matchesInitiative;
  });

  return (
    <div className="flex flex-row h-full">
      {/* Main Content */}
      <div className="flex-1 p-6 overflow-y-auto">
        <div className="max-w-6xl mx-auto">
          {epics.length > 0 && (
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className={cn("text-2xl font-semibold", colors.textPrimary)}>Epics</h2>
              <p className={cn("text-sm mt-1", colors.textSecondary)}>
                {activeEpics.length} Active Epic{activeEpics.length !== 1 ? 's' : ''}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className={cn("w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2", colors.textMuted)} />
                <Input 
                  placeholder="Search epics..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={cn("pl-9 w-64", colors.bgInput, colors.borderPrimary)}
                />
              </div>
              {isEnabled(FEATURE_IDS.TAB_INITIATIVES) && (
              <Select value={initiativeFilter} onValueChange={setInitiativeFilter}>
                <SelectTrigger className={cn("w-44", colors.bgInput, colors.borderPrimary)}>
                  <SelectValue placeholder="Filter by initiative" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Initiatives</SelectItem>
                  <SelectItem value="none">No Initiative</SelectItem>
                  {initiatives.map(i => (
                    <SelectItem key={i.initiative_id} value={i.initiative_id}>{i.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              )}
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className={cn("w-40", colors.bgInput, colors.borderPrimary)}>
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  {STATUS_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {canEdit && (
                <Button 
                  className={cn("gap-2", colors.accentGradient)}
                  onClick={() => setShowAddModal(true)}
                  data-testid="add-epic-btn"
                >
                  <Plus className="w-4 h-4" />
                  Add Epic
                </Button>
              )}
            </div>
          </div>
          )}
          
          {/* Active Epics Section */}
          {(filteredActiveEpics.length > 0 || activeEpics.length === 0) && (
            <div>
              {epics.length > 0 && (
              <button
                onClick={() => setShowActive(!showActive)}
                className={cn(
                  "flex items-center gap-2 text-sm font-medium mb-4 transition-colors",
                  colors.textSecondary,
                  "hover:text-gray-900 dark:hover:text-gray-100"
                )}
                data-testid="active-epics-toggle"
              >
                {showActive ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                <Layers className="w-4 h-4 text-indigo-500" />
                Active Epics ({filteredActiveEpics.length})
              </button>
              )}
              
              {(showActive || epics.length === 0) && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredActiveEpics.length === 0 ? (
                    activeEpics.length === 0 ? (
                      <div className={cn("col-span-3 text-center py-12 px-6 rounded-lg border", colors.bgTertiary, colors.borderPrimary)}>
                        <Layers className={cn("w-12 h-12 mx-auto mb-4", colors.textMuted)} />
                        <h3 className={cn("text-lg font-semibold mb-4", colors.textPrimary)}>No epics defined yet</h3>
                        <div className={cn("text-sm mb-6 max-w-2xl mx-auto text-left space-y-4", colors.textMuted)}>
                          <p>
                            <strong className={colors.textPrimary}>Define Your Delivery Vehicles.</strong>
                            <br />
                            Epics are the tactical engines of your roadmap. While Initiatives set the market promise, Epics define the concrete scope required to fulfill it. In StackRanked, an Epic is strictly focused: it belongs to a single Initiative, ensuring that every significant body of work has a clear strategic owner and never floats without a purpose.
                          </p>
                          <p>
                            <strong className={colors.textPrimary}>Crucially, this is where you lock scope to value.</strong>
                            <br />
                            By linking an Epic to specific KPI Targets inherited from its parent Initiative, you draw a direct line between the features your team builds and the business outcomes you measure. This transforms your backlog from a list of &ldquo;things to do&rdquo; into a calculated plan to hit your numbers.
                          </p>
                          <p>
                            <strong className={colors.textPrimary}>Don&apos;t just fill them with tickets - design them for your Personas.</strong>
                            <br />
                            A well-crafted Epic Problem Statement allows your AI Co-Pilot to rigorously test your scope against your &ldquo;Virtual Advisory Board,&rdquo; ensuring the solution you are planning actually solves the pain points of the users you serve.
                          </p>
                        </div>
                        {canEdit && (
                          <Button className={cn(colors.accentGradient)} onClick={() => setShowAddModal(true)}>
                            <Plus className="w-4 h-4 mr-2" />
                            Create Your First Epic
                          </Button>
                        )}
                      </div>
                    ) : (
                      <Card className={cn("col-span-3 p-12 text-center border", colors.bgCard, colors.borderPrimary)}>
                        <Layers className={cn("w-12 h-12 mx-auto mb-3", colors.textMuted)} />
                        <p className={colors.textMuted}>No active epics found matching your filter</p>
                      </Card>
                    )
                  ) : (
                    filteredActiveEpics.map((epic) => {
                      const features = epicFeatures[epic.epic_id] || [];
                      return (
                        <EpicCard
                          key={epic.epic_id}
                          epic={epic}
                          colors={colors}
                          onSelect={onSelectEpic}
                          goals={goals}
                          initiatives={initiatives}
                          features={features}
                        />
                      );
                    })
                  )}
                </div>
              )}
            </div>
          )}
          
          {/* Archived Epics Section */}
          {filteredArchivedEpics.length > 0 && (
            <div className="mt-8">
              <button
                onClick={() => setShowArchived(!showArchived)}
                className={cn(
                  "flex items-center gap-2 text-sm font-medium mb-4 transition-colors",
                  colors.textSecondary,
                  "hover:text-gray-900 dark:hover:text-gray-100"
                )}
                data-testid="archived-epics-toggle"
              >
                {showArchived ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                <Archive className="w-4 h-4" />
                Archived Epics ({filteredArchivedEpics.length})
              </button>
              
              {showArchived && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredArchivedEpics.map((epic) => {
                    const features = epicFeatures[epic.epic_id] || [];
                    
                    // Resolve epic IDs in archived_reason to titles
                    const resolveArchivedReason = (reason) => {
                      if (!reason) return null;
                      // Match epic_xxx pattern and replace with epic title
                      return reason.replace(/epic_([a-f0-9]+)/gi, (match, id) => {
                        const targetEpic = epics.find(e => e.epic_id === match || e.epic_id === `epic_${id}`);
                        return targetEpic ? `"${targetEpic.title}"` : match;
                      });
                    };
                    
                    const displayReason = resolveArchivedReason(epic.archived_reason);
                    
                    return (
                      <Card
                        key={epic.epic_id}
                        onClick={() => onSelectEpic(epic)}
                        className={cn(
                          "p-4 border cursor-pointer transition-all hover:shadow-md opacity-70 hover:opacity-100",
                          colors.bgCard,
                          colors.borderPrimary
                        )}
                        data-testid={`archived-epic-card-${epic.epic_id}`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-2">
                              <Archive className={cn("w-4 h-4", colors.textMuted)} />
                              <h4 className={cn("font-medium truncate", colors.textPrimary)}>
                                {epic.title}
                              </h4>
                            </div>
                            <p className={cn("text-xs", colors.textMuted)}>
                              Archived {epic.archived_at ? new Date(epic.archived_at).toLocaleDateString() : ''}
                            </p>
                            {displayReason && (
                              <p className={cn("text-xs mt-1 italic", colors.textSecondary)}>
                                {displayReason}
                              </p>
                            )}
                            <div className="flex items-center gap-2 mt-2">
                              <span className={cn("text-xs", colors.textMuted)}>
                                {features.length} feature{features.length !== 1 ? 's' : ''}
                              </span>
                            </div>
                          </div>
                        </div>
                      </Card>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* AI Bottom Panel */}
      {canUseAI && epics.length > 0 && <CollectionCopilotDrawer 
        context="epics" 
        contextId={productId} 
        title="Epic Analyzer"
        onRefresh={() => refresh('collection', 'epics')}
       
      />}
      
      {/* Add Epic Modal */}
      <AddEpicModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSave={() => refresh('collection', 'epics')}
        productId={productId}
        initiatives={initiatives}
      />
    </div>
  );
};


// ==================== ADD FEATURE MODAL ====================
const AddFeatureModal = ({ isOpen, onClose, onSave, productId, epics = [] }) => {
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    epic_id: '',
    execution_type: 'engineering',
    status: 'Backlog',
    priority_rank: 0
  });

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData({ 
        title: '', 
        description: '', 
        epic_id: epics[0]?.epic_id || '',
        execution_type: 'engineering',
        status: 'Backlog',
        priority_rank: 0
      });
    }
  }, [isOpen, epics]);

  const handleCreate = async () => {
    if (!formData.title.trim()) {
      toast.error('Feature title is required');
      return;
    }
    if (!formData.epic_id) {
      toast.error('Please select an epic');
      return;
    }
    
    setSaving(true);
    try {
      await featuresApi.create(formData.epic_id, formData);
      toast.success('Feature created!');
      onSave();
      onClose();
    } catch (err) {
      console.error('Failed to create feature:', err);
      toast.error(err.response?.data?.detail || 'Failed to create feature');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={cn(colors.bgCard, "max-w-lg overflow-hidden")}>
        <DialogHeader>
          <DialogTitle className={cn("flex items-center gap-2", colors.textPrimary)}>
            <LayoutList className="w-5 h-5 text-blue-500" />
            Add Feature
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-5 py-4 max-w-full">
          <div className="min-w-0">
            <label className={cn("text-sm font-medium", colors.textSecondary)}>Title *</label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="e.g., User Login, Dashboard Analytics"
              className={cn("mt-1.5", colors.bgInput, colors.borderPrimary)}
              data-testid="feature-title-input"
            />
          </div>
          
          <div className="min-w-0">
            <label className={cn("text-sm font-medium", colors.textSecondary)}>Epic *</label>
            <Select 
              value={formData.epic_id} 
              onValueChange={(value) => setFormData({ ...formData, epic_id: value })}
            >
              <SelectTrigger className={cn("mt-1.5 w-full overflow-hidden", colors.bgInput, colors.borderPrimary)}>
                <SelectValue placeholder="Select an epic..." />
              </SelectTrigger>
              <SelectContent className={cn(colors.bgCard, colors.borderPrimary)}>
                {epics.length === 0 ? (
                  <SelectItem value="no-epics" disabled>No epics available - create one first</SelectItem>
                ) : (
                  epics.map(epic => (
                    <SelectItem key={epic.epic_id} value={epic.epic_id}>
                      {epic.title}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
            {epics.length === 0 && (
              <p className={cn("text-xs mt-1.5", colors.textMuted)}>
                You need to create an epic before adding features.
              </p>
            )}
          </div>

          <div className="min-w-0">
            <label className={cn("text-sm font-medium", colors.textSecondary)}>Execution Type</label>
            <Select 
              value={formData.execution_type} 
              onValueChange={(value) => setFormData({ ...formData, execution_type: value })}
            >
              <SelectTrigger className={cn("mt-1.5 w-full", colors.bgInput, colors.borderPrimary)} data-testid="execution-type-create-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className={cn(colors.bgCard, colors.borderPrimary)}>
                <SelectItem value="engineering">Engineering / Development</SelectItem>
                <SelectItem value="strategic">Strategic / Operational</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="min-w-0">
            <label className={cn("text-sm font-medium", colors.textSecondary)}>Description</label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Describe what this feature does..."
              rows={4}
              className={cn("mt-1.5", colors.bgInput, colors.borderPrimary)}
            />
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button 
            onClick={handleCreate}
            disabled={!formData.title.trim() || !formData.epic_id || saving}
            className={cn(
              colors.accentGradient,
              ((!formData.title.trim() || !formData.epic_id) && !saving) && "opacity-50 cursor-not-allowed"
            )}
            data-testid="create-feature-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
            Create Feature
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ==================== FEATURES TAB ====================
const FeaturesTab = ({ features, epics, productId, canEdit, canUseAI, onSelectFeature, refresh, releases = [], personas = [], teamMembers = [], markStale, getCascadeTargets, onNavigateToTab }) => {
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();
  const [searchQuery, setSearchQuery] = useState('');
  const [epicFilter, setEpicFilter] = useState('all');
  const [sortBy, setSortBy] = useState('priority');
  const [tagFilter, setTagFilter] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [tagsLoaded, setTagsLoaded] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  
  // Split feature modal state (triggered by collection copilot)
  const [splitModalOpen, setSplitModalOpen] = useState(false);
  const [splitFeature, setSplitFeature] = useState(null);
  const [splitData, setSplitData] = useState(null);
  
  // Build linkableEntities from props (data flows down from ProductDetailPage)
  // Filter out archived epics from dropdowns
  const linkableEntities = useMemo(() => ({
    epics: (epics || []).filter(e => e.status !== 'archived'),
    releases: releases || [],
    personas: personas || [],
    tags: availableTags || [],
    teamMembers: teamMembers || [],
  }), [epics, releases, personas, availableTags, teamMembers]);
  
  // Archived features state
  const [archivedFeatures, setArchivedFeatures] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [restoringId, setRestoringId] = useState(null);

  // Load archived features
  useEffect(() => {
    const loadArchivedFeatures = async () => {
      if (!productId) return;
      try {
        const res = await featuresApi.listArchived(productId);
        setArchivedFeatures(res.data || []);
      } catch (err) {
        console.error('Failed to load archived features:', err);
      }
    };
    loadArchivedFeatures();
  }, [productId]);
  
  // Restore archived feature
  const handleRestore = async (featureId, featureTitle) => {
    setRestoringId(featureId);
    try {
      await featuresApi.restore(featureId);
      toast.success('Feature restored', { description: `${featureTitle} has been restored to Backlog` });
      // Refresh both lists
      const res = await featuresApi.listArchived(productId);
      setArchivedFeatures(res.data || []);
      refresh('collection', 'features');
    } catch (err) {
      toast.error('Failed to restore feature');
    } finally {
      setRestoringId(null);
    }
  };

  // Handle split item from collection copilot
  // Receives already-transformed payload from UnifiedActionReview: { feature_1: {...}, feature_2: {...}, source_id, ... }
  const handleSplitItem = useCallback((splitPayload) => {
    const sourceId = splitPayload.source_id;
    if (!sourceId) {
      toast.error('Split failed: No source feature ID provided');
      return;
    }
    
    // Find feature from in-memory array
    const feature = features.find(f => f.feature_id === sourceId);
    if (!feature) {
      toast.error('Split failed: Feature not found');
      return;
    }
    
    // Payload is already transformed by UnifiedActionReview's handleOpenSplit
    // Just pass through to the modal (same pattern as FeatureDetail.jsx)
    setSplitFeature(feature);
    setSplitData(splitPayload);
    setSplitModalOpen(true);
  }, [features]);

  // Load tags for this product
  useEffect(() => {
    const loadTags = async () => {
      if (!productId) return;
      try {
        const { productTagsApi } = await import('../services/api');
        const res = await productTagsApi.list(productId);
        setAvailableTags(res.data.tags || []);
        setTagsLoaded(true);
      } catch (err) {
        console.error('Failed to load tags:', err);
      }
    };
    loadTags();
  }, [productId]);

  // Create a map of tag_id to tag object for quick lookup
  const tagMap = React.useMemo(() => {
    const map = {};
    availableTags.forEach(tag => {
      map[tag.tag_id] = tag;
    });
    return map;
  }, [availableTags]);

  // Built-in filter tags (effort, parking lot, release)
  const BUILTIN_FILTERS = useMemo(() => [
    { tag_id: '__effort_xs', name: 'Effort: XS', color: '#6b7280', isBuiltin: true, filterType: 'effort', value: 'XS' },
    { tag_id: '__effort_s', name: 'Effort: S', color: '#6b7280', isBuiltin: true, filterType: 'effort', value: 'S' },
    { tag_id: '__effort_m', name: 'Effort: M', color: '#6b7280', isBuiltin: true, filterType: 'effort', value: 'M' },
    { tag_id: '__effort_l', name: 'Effort: L', color: '#6b7280', isBuiltin: true, filterType: 'effort', value: 'L' },
    { tag_id: '__effort_xl', name: 'Effort: XL', color: '#6b7280', isBuiltin: true, filterType: 'effort', value: 'XL' },
    { tag_id: '__in_parking_lot', name: 'In Parking Lot', color: '#9ca3af', isBuiltin: true, filterType: 'release', value: null },
    { tag_id: '__in_release', name: 'In Release', color: '#6366f1', isBuiltin: true, filterType: 'release', value: 'assigned' },
  ], []);

  // Combine built-in filters with dynamic tags
  const allFilterTags = useMemo(() => {
    return [...BUILTIN_FILTERS, ...availableTags];
  }, [BUILTIN_FILTERS, availableTags]);

  // Helper to check if a feature matches the selected filters
  const matchesFilters = useCallback((feature) => {
    if (tagFilter.length === 0) return true;
    
    // Separate built-in filters from regular tag filters
    const builtinFilters = tagFilter.filter(id => id.startsWith('__'));
    const regularTagFilters = tagFilter.filter(id => !id.startsWith('__'));
    
    // Check built-in filters (OR within same type, AND across types)
    let matchesBuiltin = true;
    
    // Group built-in filters by type
    const effortFilters = builtinFilters.filter(id => id.startsWith('__effort_'));
    const releaseFilters = builtinFilters.filter(id => id.startsWith('__in_'));
    
    // Effort filters - match if ANY selected effort matches
    if (effortFilters.length > 0) {
      const selectedEfforts = effortFilters.map(id => {
        const filter = BUILTIN_FILTERS.find(f => f.tag_id === id);
        return filter?.value;
      });
      matchesBuiltin = matchesBuiltin && selectedEfforts.includes(feature.effort_size);
    }
    
    // Release filters - match if ANY selected release filter matches
    if (releaseFilters.length > 0) {
      const inParkingLot = !feature.release_id;
      const inRelease = !!feature.release_id;
      
      const matchesParkingLot = releaseFilters.includes('__in_parking_lot') && inParkingLot;
      const matchesInRelease = releaseFilters.includes('__in_release') && inRelease;
      
      matchesBuiltin = matchesBuiltin && (matchesParkingLot || matchesInRelease);
    }
    
    // Regular tag filters - match if ANY selected tag is on the feature
    const matchesRegularTags = regularTagFilters.length === 0 || 
      (feature.tag_ids && regularTagFilters.some(tagId => feature.tag_ids.includes(tagId)));
    
    return matchesBuiltin && matchesRegularTags;
  }, [tagFilter, BUILTIN_FILTERS]);

  // Separate features into categories: active, code complete, production
  const { activeFeatures, codeCompleteFeatures, productionFeatures } = useMemo(() => {
    return {
      // Active: not Done, not in_production
      activeFeatures: features.filter(f => !f.in_production && f.status !== 'Done'),
      // Code Complete: status is Done but not yet in production
      codeCompleteFeatures: features.filter(f => !f.in_production && f.status === 'Done'),
      // In Production: in_production flag is true
      productionFeatures: features.filter(f => f.in_production)
    };
  }, [features]);

  // State for showing collapsed sections
  const [showActive, setShowActive] = useState(true); // Default open
  const [showCodeComplete, setShowCodeComplete] = useState(false);
  const [showProduction, setShowProduction] = useState(false);

  const filteredFeatures = activeFeatures
    .filter(f => {
      const matchesSearch = f.title?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesEpic = epicFilter === 'all' || f.epic_id === epicFilter;
      const matchesTagFilters = matchesFilters(f);
      return matchesSearch && matchesEpic && matchesTagFilters;
    })
    .sort((a, b) => {
      if (sortBy === 'blocked') {
        // Blocked first, then by priority
        if (a.is_blocked && !b.is_blocked) return -1;
        if (!a.is_blocked && b.is_blocked) return 1;
        return (a.priority_rank || 0) - (b.priority_rank || 0);
      }
      if (sortBy === 'priority') return (a.priority_rank || 0) - (b.priority_rank || 0);
      if (sortBy === 'status') return (a.status || '').localeCompare(b.status || '');
      if (sortBy === 'title') return (a.title || '').localeCompare(b.title || '');
      return 0;
    });

  // Filter production features as well (for search/filter within production section)
  const filteredProductionFeatures = productionFeatures
    .filter(f => {
      const matchesSearch = searchQuery === '' || f.title?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesEpic = epicFilter === 'all' || f.epic_id === epicFilter;
      return matchesSearch && matchesEpic;
    })
    .sort((a, b) => (a.title || '').localeCompare(b.title || ''));

  // Filter code complete features
  const filteredCodeCompleteFeatures = codeCompleteFeatures
    .filter(f => {
      const matchesSearch = searchQuery === '' || f.title?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesEpic = epicFilter === 'all' || f.epic_id === epicFilter;
      return matchesSearch && matchesEpic;
    })
    .sort((a, b) => (a.title || '').localeCompare(b.title || ''));

  const getEpicName = (epicId) => epics.find(e => e.epic_id === epicId)?.title || 'Unknown';

  const STATUS_COLORS = {
    'Done': 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    'In Progress': 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    'Review': 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
    'Todo': 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    'Backlog': 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400',
  };

  // Import TagFilter and TagBadge dynamically to avoid circular deps
  const { TagFilter, TagBadge } = React.useMemo(() => {
    try {
      return require('../components/tags');
    } catch (e) {
      return { TagFilter: () => null, TagBadge: () => null };
    }
  }, []);

  return (
    <div className="flex flex-row h-full">
      {/* Main Content */}
      <div className="flex-1 p-6 overflow-y-auto">
        <div className="max-w-6xl mx-auto">
          {/* Feature Usage Banner */}
          <UsageBanner 
            limitType="features" 
            showAtPercentage={50}
            className="mb-6"
          />
          
          {features.length > 0 && (
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className={cn("text-2xl font-semibold", colors.textPrimary)}>Features</h2>
              <p className={cn("text-sm mt-1", colors.textSecondary)}>
                {activeFeatures.length} Active Feature{activeFeatures.length !== 1 ? 's' : ''}
              </p>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="relative">
                <Search className={cn("w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2", colors.textMuted)} />
                <Input 
                  placeholder="Search features..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={cn("pl-9 w-64", colors.bgInput, colors.borderPrimary)}
                />
              </div>
              <Select value={epicFilter} onValueChange={setEpicFilter}>
                <SelectTrigger className={cn("w-40", colors.bgInput, colors.borderPrimary)}>
                  <SelectValue placeholder="Filter by epic" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Epics</SelectItem>
                  {epics.map(e => (
                    <SelectItem key={e.epic_id} value={e.epic_id}>{e.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {tagsLoaded && (
                <TagFilter
                  availableTags={allFilterTags}
                  selectedTagIds={tagFilter}
                  onFilterChange={setTagFilter}
                />
              )}
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className={cn("w-32", colors.bgInput, colors.borderPrimary)}>
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="priority">Priority</SelectItem>
                  <SelectItem value="status">Status</SelectItem>
                  <SelectItem value="title">Title</SelectItem>
                  <SelectItem value="blocked">Blocked</SelectItem>
                </SelectContent>
              </Select>
              {canEdit && (
                <Button 
                  className={cn("gap-2", colors.accentGradient)}
                  onClick={() => setShowAddModal(true)}
                  data-testid="add-feature-btn"
                >
                  <Plus className="w-4 h-4" />
                  Add Feature
                </Button>
              )}
            </div>
          </div>
          )}
          
          {/* Active Features Section */}
          {(filteredFeatures.length > 0 || features.length === 0) && (
            <div>
              {features.length > 0 && (
              <button
                onClick={() => setShowActive(!showActive)}
                className={cn(
                  "flex items-center gap-2 text-sm font-medium mb-4 transition-colors",
                  colors.textSecondary,
                  "hover:text-gray-900 dark:hover:text-gray-100"
                )}
              >
                {showActive ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                <LayoutList className="w-4 h-4 text-indigo-500" />
                Active Features ({filteredFeatures.length})
              </button>
              )}
              
              {(showActive || features.length === 0) && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredFeatures.length === 0 ? (
                    features.length === 0 ? (
                      <div className={cn("col-span-3 text-center py-12 px-6 rounded-lg border", colors.bgTertiary, colors.borderPrimary)} data-testid="features-empty-state">
                        <LayoutList className={cn("w-12 h-12 mx-auto mb-4", colors.textMuted)} />
                        <h3 className={cn("text-lg font-semibold mb-4", colors.textPrimary)}>No features defined yet</h3>
                        <div className={cn("text-sm mb-6 max-w-2xl mx-auto text-left space-y-4", colors.textMuted)}>
                          <p>
                            <strong className={colors.textPrimary}>Define the Work. Prove the Worth.</strong>
                            <br />
                            Features are the concrete building blocks of your product. But in StackRanked, a feature cannot exist just for the sake of code - it must be an active contributor to your strategy. If a feature isn&apos;t explicitly linked to a KPI (inherited from its Epic or Tags), the system considers it &ldquo;orphaned.&rdquo; We ask you to justify exactly how each feature moves the needle, ensuring your team never wastes cycles on work that doesn&apos;t matter.
                          </p>
                          <p>
                            <strong className={colors.textPrimary}>This depth implies discipline, not drudgery.</strong>
                            <br />
                            By the time you reach this stage, your AI Co-Pilot is context-aware and ready to do the heavy lifting. It can draft your strategic alignment justification, suggest &ldquo;Shall&rdquo; statements for your Requirements, and define acceptance criteria - turning what used to be data entry into a quick review process.
                          </p>
                          <p>
                            <strong className={colors.textPrimary}>Finally, this is your execution command center.</strong>
                            <br />
                            Stop chasing decisions through Slack threads and emails. Use Notes to contextualize communication right where the work happens. As engineering builds, the Dev Log will automatically track Git commits and MCP technical decisions, giving you a pristine audit trail from the first requirement to the final release.
                          </p>
                        </div>
                        {canEdit && epics.filter(e => e.status !== 'archived').length > 0 ? (
                          <Button className={cn(colors.accentGradient)} onClick={() => setShowAddModal(true)} data-testid="create-first-feature-btn">
                            <Plus className="w-4 h-4 mr-2" />
                            Create Your First Feature
                          </Button>
                        ) : canEdit ? (
                          <div className="space-y-3">
                            <p className={cn("text-sm", colors.textSecondary)}>
                              Features belong to Epics. Create an Epic first to start adding features.
                            </p>
                            <Button 
                              variant="outline"
                              className={cn("border", colors.borderPrimary)}
                              onClick={() => onNavigateToTab?.('epics')}
                              data-testid="go-to-epics-btn"
                            >
                              <Layers className="w-4 h-4 mr-2" />
                              Go to Epics
                            </Button>
                          </div>
                        ) : null}
                      </div>
                    ) : (
                      <Card className={cn("col-span-3 p-12 text-center border", colors.bgCard, colors.borderPrimary)}>
                        <LayoutList className={cn("w-12 h-12 mx-auto mb-3", colors.textMuted)} />
                        <p className={colors.textMuted}>No active features matching your filter</p>
                      </Card>
                    )
                  ) : (
                    filteredFeatures.map(feature => (
                      <FeatureCard
                        key={feature.feature_id}
                        feature={feature}
                        colors={colors}
                        onSelect={onSelectFeature}
                        getEpicName={getEpicName}
                        releases={releases}
                      />
                    ))
                  )}
                </div>
              )}
            </div>
          )}
          
          {/* Code Complete Features Section */}
          {filteredCodeCompleteFeatures.length > 0 && (
            <div className="mt-8">
              <button
                onClick={() => setShowCodeComplete(!showCodeComplete)}
                className={cn(
                  "flex items-center gap-2 text-sm font-medium mb-4 transition-colors",
                  colors.textSecondary,
                  "hover:text-gray-900 dark:hover:text-gray-100"
                )}
              >
                {showCodeComplete ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                <CheckCircle className="w-4 h-4 text-blue-500" />
                Code Complete ({filteredCodeCompleteFeatures.length})
              </button>
              
              {showCodeComplete && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                  {filteredCodeCompleteFeatures.map((feature) => (
                    <Card
                      key={feature.feature_id}
                      onClick={() => onSelectFeature(feature)}
                      className={cn(
                        "p-3 border cursor-pointer transition-all hover:shadow-md hover:ring-1 hover:ring-blue-500/30",
                        colors.bgCard,
                        "border-blue-200 dark:border-blue-800/50 bg-blue-50/30 dark:bg-blue-900/10"
                      )}
                    >
                      <div className="flex items-start gap-3">
                        <div className={cn(
                          "w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0",
                          "bg-blue-100 dark:bg-blue-900/30"
                        )}>
                          <CheckCircle className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className={cn("font-medium text-sm truncate", colors.textPrimary)}>
                            {feature.title}
                          </h4>
                          <div className="flex items-center gap-2 mt-1">
                            <span className={cn("text-xs", colors.textMuted)}>
                              {getEpicName(feature.epic_id)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          )}
          
          {/* In Production Features Section */}
          {filteredProductionFeatures.length > 0 && (
            <div className="mt-8">
              <button
                onClick={() => setShowProduction(!showProduction)}
                className={cn(
                  "flex items-center gap-2 text-sm font-medium mb-4 transition-colors",
                  colors.textSecondary,
                  "hover:text-gray-900 dark:hover:text-gray-100"
                )}
              >
                {showProduction ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                <Rocket className="w-4 h-4" />
                In Production ({filteredProductionFeatures.length})
              </button>
              
              {showProduction && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                  {filteredProductionFeatures.map((feature) => (
                    <Card
                      key={feature.feature_id}
                      onClick={() => onSelectFeature(feature)}
                      className={cn(
                        "p-3 border cursor-pointer transition-all hover:shadow-md hover:ring-1 hover:ring-green-500/30",
                        colors.bgCard,
                        "border-green-200 dark:border-green-800/50 bg-green-50/30 dark:bg-green-900/10"
                      )}
                    >
                      <div className="flex items-start gap-3">
                        <div className={cn(
                          "w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0",
                          "bg-green-100 dark:bg-green-900/30"
                        )}>
                          <Rocket className="w-4 h-4 text-green-600 dark:text-green-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className={cn("font-medium text-sm truncate", colors.textPrimary)}>
                            {feature.title}
                          </h4>
                          <div className="flex items-center gap-2 mt-1">
                            <span className={cn("text-xs", colors.textMuted)}>
                              {getEpicName(feature.epic_id)}
                            </span>
                            {feature.deployed_at && (
                              <span className="text-xs text-green-600 dark:text-green-400">
                                • {new Date(feature.deployed_at).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          )}
          
          {/* Archived Features Section */}
          {archivedFeatures.length > 0 && (
            <div className="mt-8">
              <button
                onClick={() => setShowArchived(!showArchived)}
                className={cn(
                  "flex items-center gap-2 text-sm font-medium mb-4 transition-colors",
                  colors.textSecondary,
                  "hover:text-gray-900 dark:hover:text-gray-100"
                )}
              >
                {showArchived ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                <Archive className="w-4 h-4" />
                Archived Features ({archivedFeatures.length})
              </button>
              
              {showArchived && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {archivedFeatures.map((feature) => (
                    <Card
                      key={feature.feature_id}
                      className={cn(
                        "p-4 border opacity-70 hover:opacity-100 transition-opacity",
                        colors.bgCard,
                        colors.borderPrimary
                      )}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <h4 className={cn("font-medium truncate", colors.textPrimary)}>
                            {feature.title}
                          </h4>
                          <p className={cn("text-xs mt-1", colors.textMuted)}>
                            Archived {feature.archived_at ? new Date(feature.archived_at).toLocaleDateString() : ''}
                          </p>
                          {feature.archived_reason && (
                            <p className={cn("text-xs mt-1 italic", colors.textSecondary)}>
                              {feature.archived_reason}
                            </p>
                          )}
                        </div>
                        {canEdit && (
                          <Button
                            variant="outline"
                            size="sm"
                            className={cn("ml-4 shrink-0", colors.borderPrimary)}
                            onClick={() => handleRestore(feature.feature_id, feature.title)}
                            disabled={restoringId === feature.feature_id}
                          >
                            {restoringId === feature.feature_id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <RotateCcw className="w-3 h-3 mr-1" />
                                Restore
                              </>
                            )}
                          </Button>
                        )}
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* AI Bottom Panel */}
      {canUseAI && features.length > 0 && <CollectionCopilotDrawer 
        context="features" 
        contextId={productId} 
        title="Feature Advisor"
        onRefresh={() => refresh('collection', 'features')}
        onSplitItem={handleSplitItem}
        relatedData={{ 
          features, 
          epics,
          releases,
          personas,
          tags: availableTags,
          teamMembers,
        }}
      />}
      
      {/* Split Feature Modal (triggered by collection copilot) */}
      <SplitFeatureModal
        isOpen={splitModalOpen}
        originalFeature={splitFeature}
        splitData={splitData}
        productId={productId}
        linkableEntities={linkableEntities}
        onClose={() => {
          setSplitModalOpen(false);
          setSplitFeature(null);
          setSplitData(null);
        }}
        onSplitComplete={(newFeatures) => {
          setSplitModalOpen(false);
          setSplitFeature(null);
          setSplitData(null);
          refresh('collection', 'features');
          toast.success('Feature split successfully!');
        }}
      />
      
      {/* Add Feature Modal */}
      <AddFeatureModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSave={() => refresh('collection', 'features')}
        productId={productId}
        epics={linkableEntities.epics}
      />
    </div>
  );
};


// ==================== RELEASES TAB ====================
const ReleasesTab = ({ features, epics, productId, canEdit, canUseAI, onRefresh, personas = [], teamMembers = [], goals = [], productContext = {}, markStale, getCascadeTargets }) => {
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();
  const [searchParams, setSearchParams] = useSearchParams();
  const [epicFilter, setEpicFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [tagFilter, setTagFilter] = useState([]);
  const [executionTypeFilter, setExecutionTypeFilter] = useState('all');
  const [availableTags, setAvailableTags] = useState([]);
  const [releases, setReleases] = useState([]);
  const [loadingReleases, setLoadingReleases] = useState(true);
  const [draggedFeature, setDraggedFeature] = useState(null);
  const [dragOverRelease, setDragOverRelease] = useState(null);
  
  // Optimistic UI: Track pending moves and local feature overrides
  const [pendingMoves, setPendingMoves] = useState(new Set()); // Set of feature_ids being moved
  const [optimisticOverrides, setOptimisticOverrides] = useState({}); // { featureId: newReleaseId }
  
  // Ref for scrolling to current release
  const releasesContainerRef = useRef(null);
  const releaseRefs = useRef({});
  
  // Track if initial auto-scroll has happened (prevents re-scroll on every releases refresh)
  const hasInitialScrolled = useRef(false);
  
  // Track highlighted release from URL param (for visual highlight effect)
  const [highlightedReleaseId, setHighlightedReleaseId] = useState(null);
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [showProductionModal, setShowProductionModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [selectedRelease, setSelectedRelease] = useState(null);
  const [closePreview, setClosePreview] = useState(null);
  const [productionPreview, setProductionPreview] = useState(null);
  
  // Feature edit modal state
  const [selectedFeature, setSelectedFeature] = useState(null);
  
  // Apply optimistic overrides to features for rendering
  const effectiveFeatures = React.useMemo(() => {
    return features.map(f => {
      if (optimisticOverrides[f.feature_id] !== undefined) {
        return { ...f, release_id: optimisticOverrides[f.feature_id] };
      }
      return f;
    });
  }, [features, optimisticOverrides]);
  
  // Create tagMap from availableTags for quick lookups
  const tagMap = React.useMemo(() => {
    const map = {};
    availableTags.forEach(tag => {
      map[tag.tag_id] = tag;
    });
    return map;
  }, [availableTags]);
  
  // Import TagBadge dynamically to avoid circular deps
  const TagBadge = React.useMemo(() => {
    try {
      return require('../components/tags').TagBadge;
    } catch (e) {
      return () => null;
    }
  }, []);
  
  // Load tags for filtering
  useEffect(() => {
    const loadTags = async () => {
      try {
        const { productTagsApi } = await import('../services/api');
        const res = await productTagsApi.list(productId);
        setAvailableTags(res.data.tags || []);
      } catch (err) {
        console.error('Failed to load tags:', err);
      }
    };
    loadTags();
  }, [productId]);
  
  // Load releases function - can be called to refresh
  const loadReleases = useCallback(async () => {
    try {
      setLoadingReleases(true);
      const res = await releasesApi.list(productId);
      setReleases(res.data || []);
    } catch (err) {
      console.error('Error loading releases:', err);
    } finally {
      setLoadingReleases(false);
    }
  }, [productId]);
  
  // Load releases on mount and when productId changes
  useEffect(() => {
    loadReleases();
  }, [loadReleases]);
  
  // ==========================================================================
  // RELEASE SCROLL BEHAVIOR
  // ==========================================================================
  // Two scroll triggers:
  // 1. Initial load: Auto-scroll to "current" release (runs ONCE on mount)
  // 2. URL param: Scroll to specific release via ?highlight=release_xxx
  //
  // IMPORTANT: We use hasInitialScrolled ref to prevent re-scrolling on every
  // releases state update (which happens on drag-drop, close, start, etc.)
  // ==========================================================================
  
  // Extract highlight param for dependency tracking
  const highlightParam = searchParams.get('highlight');
  
  // Handle URL param ?highlight=release_xxx - scroll to and highlight specific release
  useEffect(() => {
    if (!highlightParam || loadingReleases || releases.length === 0) return;
    
    // Check if this release exists
    const releaseExists = releases.some(r => r.release_id === highlightParam);
    if (!releaseExists) {
      console.warn(`Release ${highlightParam} not found for highlighting`);
      return;
    }
    
    // Set highlight state for visual effect
    setHighlightedReleaseId(highlightParam);
    
    // Clear the URL param after handling (so refresh doesn't re-highlight)
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('highlight');
    setSearchParams(newParams, { replace: true });
    
    // Scroll to the highlighted release
    setTimeout(() => {
      if (releaseRefs.current[highlightParam]) {
        releaseRefs.current[highlightParam].scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center'  // Center the highlighted release
        });
      }
    }, 150);
    
    // Clear highlight after 3 seconds (visual pulse effect duration)
    setTimeout(() => {
      setHighlightedReleaseId(null);
    }, 3000);
    
    // Mark as scrolled so initial auto-scroll doesn't override
    hasInitialScrolled.current = true;
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightParam, loadingReleases, releases]);
  
  // Initial auto-scroll to the "current" release (runs ONCE on first load)
  useEffect(() => {
    // Skip if already scrolled (either from URL param or previous load)
    if (hasInitialScrolled.current) return;
    if (loadingReleases || releases.length === 0) return;
    
    // Mark as scrolled BEFORE doing anything (prevents race conditions)
    hasInitialScrolled.current = true;
    
    // Find the current release (today falls within its date range) or the next upcoming one
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Sort releases by start_date
    const sortedReleases = [...releases].sort((a, b) => 
      new Date(a.start_date) - new Date(b.start_date)
    );
    
    // Find current or next release index
    let currentIndex = sortedReleases.findIndex(r => {
      const start = new Date(r.start_date);
      const end = new Date(r.end_date);
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);
      return today >= start && today <= end;
    });
    
    // If no current release, find the next upcoming one
    if (currentIndex === -1) {
      currentIndex = sortedReleases.findIndex(r => {
        const start = new Date(r.start_date);
        start.setHours(0, 0, 0, 0);
        return start > today;
      });
    }
    
    // Scroll to the release BEFORE the current one (so current is visible)
    const scrollToIndex = Math.max(0, currentIndex - 1);
    const targetReleaseId = sortedReleases[scrollToIndex]?.release_id;
    
    if (targetReleaseId && releaseRefs.current[targetReleaseId] && releasesContainerRef.current) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        releaseRefs.current[targetReleaseId]?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'start'
        });
      }, 100);
    }
  }, [loadingReleases, releases]);
  
  // Status order for sorting: Done at TOP, Backlog at BOTTOM (near Close Release button)
  // Backlog items are most likely to be moved if release closes early
  const STATUS_ORDER = {
    'Done': 1,
    'Review': 2,
    'In Progress': 3,
    'Todo': 4,
    'Backlog': 5
  };
  
  // Sort features by: 1) Kanban progress (Done at top, Backlog at bottom), 2) priority_rank, 3) creation date
  const sortFeaturesByProgress = (featuresList) => {
    return [...featuresList].sort((a, b) => {
      // 1) Sort by status (Done at top, Backlog at bottom near Close Release)
      // Custom statuses (like "Design") treated as mid-level (3)
      const statusA = STATUS_ORDER[a.status] || 3;
      const statusB = STATUS_ORDER[b.status] || 3;
      if (statusA !== statusB) return statusA - statusB;
      
      // 2) Sort by priority_rank within same status (lower rank = higher priority = top)
      const rankA = a.priority_rank ?? Infinity;
      const rankB = b.priority_rank ?? Infinity;
      if (rankA !== rankB) return rankA - rankB;
      
      // 3) Sort by creation date (older first)
      const dateA = a.created_at ? new Date(a.created_at) : new Date(0);
      const dateB = b.created_at ? new Date(b.created_at) : new Date(0);
      return dateA - dateB;
    });
  };
  
  // Filter function to apply to any feature list (must be defined before use)
  const filterFeature = (f) => {
    // Keyword search (title or description)
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesTitle = f.title?.toLowerCase().includes(query);
      const matchesDesc = f.description?.toLowerCase().includes(query);
      if (!matchesTitle && !matchesDesc) return false;
    }
    
    // Execution type filter
    if (executionTypeFilter !== 'all') {
      const featureType = f.execution_type || 'engineering';
      if (featureType !== executionTypeFilter) return false;
    }
    
    // Epic filter
    if (epicFilter !== 'all' && f.epic_id !== epicFilter) return false;
    
    // Tag filter
    if (tagFilter.length > 0) {
      if (!f.tag_ids || !tagFilter.some(tagId => f.tag_ids.includes(tagId))) return false;
    }
    
    return true;
  };
  
  // Filter parking lot features (no release_id assigned) - use effectiveFeatures for optimistic UI
  const parkingLotFeatures = sortFeaturesByProgress(
    effectiveFeatures.filter(f => !f.release_id && filterFeature(f))
  );
  
  // Get features for a specific release (sorted by progress, with filters) - use effectiveFeatures
  const getReleaseFeatures = (releaseId) => {
    return sortFeaturesByProgress(
      effectiveFeatures.filter(f => f.release_id === releaseId && filterFeature(f))
    );
  };
  
  // Check if a feature is currently being moved (pending)
  const isFeaturePending = (featureId) => pendingMoves.has(featureId);
  
  // Drag and drop handlers
  const handleDragStart = (e, feature) => {
    // Don't allow dragging features that are pending
    if (isFeaturePending(feature.feature_id)) {
      e.preventDefault();
      return;
    }
    setDraggedFeature(feature);
    e.dataTransfer.effectAllowed = 'move';
  };
  
  const handleDragOver = (e, releaseId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverRelease(releaseId);
  };
  
  const handleDragLeave = () => {
    setDragOverRelease(null);
  };
  
  const handleDrop = async (e, targetReleaseId) => {
    e.preventDefault();
    setDragOverRelease(null);
    
    if (!draggedFeature || !canEdit) return;
    
    const featureId = draggedFeature.feature_id;
    const sourceReleaseId = draggedFeature.release_id; // Original location (null for parking lot)
    const targetId = targetReleaseId === 'parking-lot' ? null : targetReleaseId;
    
    // Don't do anything if dropping in same location
    if (sourceReleaseId === targetId) {
      setDraggedFeature(null);
      return;
    }
    
    // STEP 1: Optimistic UI - immediately show card in target location
    setPendingMoves(prev => new Set([...prev, featureId]));
    setOptimisticOverrides(prev => ({ ...prev, [featureId]: targetId }));
    setDraggedFeature(null); // Clear drag state immediately
    
    // STEP 2: Make API call in background
    try {
      if (targetReleaseId === 'parking-lot') {
        // Move to parking lot
        if (sourceReleaseId) {
          await releasesApi.unassignFeature(sourceReleaseId, featureId);
        }
      } else {
        // Move to release
        await releasesApi.assignFeature(targetReleaseId, featureId);
      }
      
      // STEP 3: Success - refresh data and clear pending state
      await onRefresh();
      const releasesRes = await releasesApi.list(productId);
      setReleases(releasesRes.data || []);
      
      // Clear optimistic state after refresh (data is now authoritative)
      setOptimisticOverrides(prev => {
        const updated = { ...prev };
        delete updated[featureId];
        return updated;
      });
    } catch (err) {
      console.error('Error moving feature:', err);
      
      // STEP 4: Failure - revert optimistic update
      setOptimisticOverrides(prev => {
        const updated = { ...prev };
        delete updated[featureId];
        return updated;
      });
      
      // Show error toast
      toast.error('Failed to move feature. Please try again.');
    } finally {
      // Always clear pending state
      setPendingMoves(prev => {
        const updated = new Set(prev);
        updated.delete(featureId);
        return updated;
      });
    }
  };
  
  // Handle close release (Stage 1: Close Sprint)
  const handleCloseRelease = async (release) => {
    try {
      const preview = await releasesApi.closePreview(release.release_id);
      setClosePreview(preview.data);
      setSelectedRelease(release);
      setShowCloseModal(true);
    } catch (err) {
      console.error('Error getting close preview:', err);
    }
  };
  
  const confirmCloseRelease = async (moveToNext) => {
    try {
      // Make the API call
      await releasesApi.close(selectedRelease.release_id, moveToNext);
      
      // Show success message immediately
      toast.success('Sprint closed successfully!');
      
      // Close modal and clear state immediately (don't wait for refresh)
      setShowCloseModal(false);
      setClosePreview(null);
      setSelectedRelease(null);
      
      // Refresh data in the background
      try {
        await onRefresh();
        const releasesRes = await releasesApi.list(productId);
        setReleases(releasesRes.data || []);
      } catch (refreshErr) {
        console.error('Error refreshing data:', refreshErr);
        // Don't throw - the close succeeded, refresh is just a bonus
      }
    } catch (err) {
      console.error('Error closing release:', err);
      throw err; // Re-throw so modal can handle it
    }
  };
  
  // Handle promote to production (Stage 2)
  const handlePromoteToProduction = async (release) => {
    try {
      const preview = await releasesApi.productionPreview(release.release_id);
      
      // If all features are already in production, skip the modal and directly finalize the release
      if (preview.data.all_already_in_production && preview.data.can_promote) {
        // Directly call the API with empty array to mark release as released
        setSelectedRelease(release);
        await releasesApi.promoteToProduction(release.release_id, []);
        toast.success('Release marked as in production!');
        
        // Refresh data
        try {
          await onRefresh();
          const releasesRes = await releasesApi.list(productId);
          setReleases(releasesRes.data || []);
        } catch (refreshErr) {
          console.error('Error refreshing data:', refreshErr);
        }
        setSelectedRelease(null);
        return;
      }
      
      // Otherwise, show the modal for feature selection
      setProductionPreview(preview.data);
      setSelectedRelease(release);
      setShowProductionModal(true);
    } catch (err) {
      console.error('Error getting production preview:', err);
      toast.error(err.response?.data?.detail || 'Failed to get production preview');
    }
  };
  
  const confirmPromoteToProduction = async (featureIds) => {
    try {
      // Make the API call
      await releasesApi.promoteToProduction(selectedRelease.release_id, featureIds);
      
      // Show success message immediately
      toast.success(featureIds.length > 0 ? 'Features promoted to production!' : 'Release marked as in production!');
      
      // Close modal and clear state immediately (don't wait for refresh)
      setShowProductionModal(false);
      setProductionPreview(null);
      setSelectedRelease(null);
      
      // Refresh data in the background
      try {
        await onRefresh();
        const releasesRes = await releasesApi.list(productId);
        setReleases(releasesRes.data || []);
      } catch (refreshErr) {
        console.error('Error refreshing data:', refreshErr);
        // Don't throw - the promote succeeded, refresh is just a bonus
      }
    } catch (err) {
      console.error('Error promoting to production:', err);
      toast.error(err.response?.data?.detail || 'Failed to promote to production');
      throw err; // Re-throw so modal can handle it
    }
  };
  
  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return 'No date';
    // Parse date string without timezone conversion
    // Handle both "2026-01-13" and "2026-01-13T12:00:00.000Z" formats
    const datePart = dateStr.split('T')[0];
    const [year, month, day] = datePart.split('-').map(Number);
    // Create date at noon local time to avoid any timezone shifts
    const date = new Date(year, month - 1, day, 12, 0, 0);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar - Matches Kanban layout */}
      <div className="flex-shrink-0 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* Keyword Search */}
            <div className="relative w-64">
              <Search className={cn("absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4", colors.textMuted)} />
              <Input
                placeholder="Search features..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={cn("pl-9 w-full", colors.bgInput, colors.borderPrimary)}
              />
            </div>
            
            {/* Execution Type Filter */}
            <Select value={executionTypeFilter} onValueChange={setExecutionTypeFilter}>
              <SelectTrigger className={cn("w-[180px]", colors.bgInput, colors.borderPrimary)} data-testid="releases-execution-type-filter">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent className={cn(colors.bgCard, colors.borderPrimary)}>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="engineering">Engineering / Dev</SelectItem>
                <SelectItem value="strategic">Strategic / Ops</SelectItem>
              </SelectContent>
            </Select>
            
            {/* Epic Filter */}
            <Select value={epicFilter} onValueChange={setEpicFilter}>
              <SelectTrigger className={cn("w-44", colors.bgInput, colors.borderPrimary)}>
                <SelectValue placeholder="All Epics" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Epics</SelectItem>
                {epics.map(e => (
                  <SelectItem key={e.epic_id} value={e.epic_id}>{e.title}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Tag Filter */}
            {availableTags.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className={cn("gap-2", colors.bgInput, colors.borderPrimary)}>
                    <Filter className="w-4 h-4" />
                    Tags {tagFilter.length > 0 && `(${tagFilter.length})`}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className={cn("w-56", colors.bgCard)}>
                  {availableTags.map(tag => (
                    <DropdownMenuCheckboxItem
                      key={tag.tag_id}
                      checked={tagFilter.includes(tag.tag_id)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setTagFilter(prev => [...prev, tag.tag_id]);
                        } else {
                          setTagFilter(prev => prev.filter(id => id !== tag.tag_id));
                        }
                      }}
                    >
                      <span 
                        className="w-2 h-2 rounded-full mr-2" 
                        style={{ backgroundColor: tag.color || '#6366f1' }}
                      />
                      {tag.name}
                    </DropdownMenuCheckboxItem>
                  ))}
                  {tagFilter.length > 0 && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => setTagFilter([])}>
                        Clear all tags
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            
            {/* Clear Filters */}
            {(searchQuery || epicFilter !== 'all' || executionTypeFilter !== 'all' || tagFilter.length > 0) && (
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => {
                  setSearchQuery('');
                  setEpicFilter('all');
                  setExecutionTypeFilter('all');
                  setTagFilter([]);
                }}
                className={cn("text-xs px-2", colors.textMuted)}
              >
                Clear
              </Button>
            )}
          </div>

          {canEdit && (
            <div className="flex items-center gap-3">
              <Button 
                variant="outline"
                onClick={() => setShowSettingsModal(true)}
                className={cn("gap-2 border", colors.borderPrimary)}
                title="Release Settings"
                data-testid="release-settings-btn"
              >
                <Settings className="w-4 h-4" />
              </Button>
              <Button 
                onClick={() => setShowCreateModal(true)}
                className={cn("gap-2", colors.accentGradient)}
              >
                <Plus className="w-4 h-4" />
                New Release
              </Button>
            </div>
          )}
        </div>
      </div>
      
      {/* Main Content Area - Fixed height with internal scroll */}
      <div className="flex-1 flex px-6 pb-6 min-h-0 overflow-hidden">
        {loadingReleases ? (
          <div className="flex-1 flex items-center justify-center py-12">
            <Loader2 className={cn("w-6 h-6 animate-spin", colors.textMuted)} />
          </div>
        ) : (
          <>
            {/* Parking Lot Column - Fixed on left */}
            <div 
              className={cn(
                "w-72 flex-shrink-0 rounded-lg border transition-all mr-4 flex flex-col",
                colors.bgSecondary, 
                colors.borderPrimary,
                dragOverRelease === 'parking-lot' && "ring-2 ring-blue-500"
              )}
              onDragOver={(e) => handleDragOver(e, 'parking-lot')}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, 'parking-lot')}
            >
              <div className={cn("p-3 border-b font-medium flex-shrink-0", colors.borderPrimary, colors.textPrimary)}>
                🅿️ Parking Lot
                <span className={cn("ml-2 text-sm font-normal", colors.textMuted)}>({parkingLotFeatures.length})</span>
              </div>
              <div className="p-2 space-y-2 flex-1 overflow-y-auto">
                {parkingLotFeatures.map(f => {
                  const isPending = isFeaturePending(f.feature_id);
                  return (
                  <Card 
                    key={f.feature_id} 
                    draggable={canEdit && !isPending}
                    onDragStart={(e) => handleDragStart(e, f)}
                    className={cn(
                      "p-3 border transition-all group",
                      colors.bgCard, 
                      colors.borderPrimary,
                      canEdit && !isPending && "cursor-grab hover:shadow-md",
                      draggedFeature?.feature_id === f.feature_id && "opacity-50",
                      isPending && "opacity-70 animate-pulse"
                    )}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <p className={cn("text-sm font-medium line-clamp-2 flex-1", colors.textPrimary)}>{f.title}</p>
                      {isPending ? (
                        <Loader2 className="w-4 h-4 animate-spin text-blue-500 flex-shrink-0" />
                      ) : canEdit && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedFeature(f);
                          }}
                          className={cn("p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity", colors.textMuted)}
                          title="Edit feature"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center gap-1.5">
                        <Badge 
                          variant="outline" 
                          className={cn(
                            "text-xs",
                            (f.execution_type || 'engineering') === 'engineering'
                              ? "border-blue-300 text-blue-600 dark:border-blue-700 dark:text-blue-400"
                              : "border-purple-300 text-purple-600 dark:border-purple-700 dark:text-purple-400"
                          )}
                        >
                          {(f.execution_type || 'engineering') === 'engineering' ? 'Dev' : 'Strat.'}
                        </Badge>
                        <p className={cn("text-xs truncate max-w-[120px]", colors.textMuted)}>
                          {epics.find(e => e.epic_id === f.epic_id)?.title || 'Unknown epic'}
                        </p>
                      </div>
                      {f.effort_size && (
                        <Badge variant="outline" className="text-xs">{f.effort_size}</Badge>
                      )}
                    </div>
                    {/* Tags */}
                    {f.tag_ids && f.tag_ids.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {f.tag_ids.slice(0, 3).map(tagId => {
                          const tag = tagMap[tagId];
                          return tag ? (
                            <TagBadge key={tagId} tag={tag} size="sm" />
                          ) : null;
                        })}
                        {f.tag_ids.length > 3 && (
                          <span className={cn("text-xs px-1.5 py-0.5 rounded", colors.bgTertiary, colors.textMuted)}>
                            +{f.tag_ids.length - 3}
                          </span>
                        )}
                      </div>
                    )}
                  </Card>
                  );
                })}
                {parkingLotFeatures.length === 0 && (
                  <p className={cn("text-sm text-center py-4", colors.textMuted)}>No unscheduled features</p>
                )}
              </div>
            </div>
            
            {/* Release Columns - Scrollable container */}
            <div 
              ref={releasesContainerRef}
              className="flex-1 overflow-x-auto overflow-y-hidden"
            >
              <div className="flex gap-0 h-full">
                {/* Release Columns with Gap Indicators */}
            {(() => {
              // Sort releases by start date for gap calculation
              const sortedReleases = [...releases].sort((a, b) => 
                new Date(a.start_date) - new Date(b.start_date)
              );
              
              // Calculate gaps between consecutive releases
              const calculateGapDays = (prevEnd, nextStart) => {
                if (!prevEnd || !nextStart) return 0;
                // Parse dates and normalize to date-only (strip time)
                const endDate = new Date(prevEnd.split('T')[0] + 'T00:00:00');
                const startDate = new Date(nextStart.split('T')[0] + 'T00:00:00');
                // Gap is the number of days BETWEEN the two dates
                // e.g., if end=Mar 9 and start=Mar 11, gap is 1 day (Mar 10)
                const diffTime = startDate.getTime() - endDate.getTime();
                const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24)) - 1;
                return diffDays > 0 ? diffDays : 0;
              };
              
              const elements = [];
              
              sortedReleases.forEach((release, index) => {
                // Check for gap BEFORE this release (except first one)
                if (index > 0) {
                  const prevRelease = sortedReleases[index - 1];
                  const gapDays = calculateGapDays(prevRelease.end_date, release.start_date);
                  
                  if (gapDays > 0) {
                    elements.push(
                      <div 
                        key={`gap-${prevRelease.release_id}-${release.release_id}`}
                        className={cn(
                          "w-8 flex-shrink-0 flex items-center justify-center rounded border-2 border-dashed h-full ml-4",
                          "bg-orange-50 dark:bg-orange-950/20 border-orange-300 dark:border-orange-700"
                        )}
                        title={`${gapDays} day gap between ${prevRelease.name} and ${release.name}`}
                      >
                        <span 
                          className="text-xs font-medium text-orange-600 dark:text-orange-400 whitespace-nowrap"
                          style={{ writingMode: 'vertical-rl', textOrientation: 'mixed', transform: 'rotate(180deg)' }}
                        >
                          {gapDays} Day Gap
                        </span>
                      </div>
                    );
                  }
                }
                
                // Render the release column
                const releaseFeatures = getReleaseFeatures(release.release_id);
                const isSprintStarted = release.is_sprint_started;
                const isSprintClosed = release.is_sprint_closed || release.is_closed;
                const isReleased = release.is_released;
                
                // Check if previous sprint is closed (to determine if "Start Sprint" button should show)
                const prevRelease = index > 0 ? sortedReleases[index - 1] : null;
                const isPrevSprintClosed = !prevRelease || prevRelease.is_sprint_closed || prevRelease.is_closed;
                const canStartSprint = !isSprintStarted && !isSprintClosed && isPrevSprintClosed;
              
              // Separate features into three non-overlapping categories:
              // 1. In production - already deployed (in_production === true)
              const inProductionFeatures = releaseFeatures.filter(f => f.in_production);
              
              // 2. Done but not in production - completed but awaiting deployment
              //    Shows in special section in ANY sprint state (important for rolled-over features)
              const doneNotInProduction = releaseFeatures.filter(f => f.status === 'Done' && !f.in_production);
              
              // 3. Active features - work in progress (not Done, not in production)
              const activeFeatures = releaseFeatures.filter(f => f.status !== 'Done' && !f.in_production);
              
              // Column background based on state
              const columnBg = isReleased 
                ? "bg-green-50 dark:bg-green-950/20" // Light green for in production
                : isSprintClosed && !isReleased
                  ? "bg-yellow-50 dark:bg-yellow-950/20" // Light yellow for sprint closed but not released
                  : isSprintStarted && !isSprintClosed
                    ? "bg-blue-50 dark:bg-blue-950/20" // Light blue for active sprint
                    : colors.bgSecondary;
              
                // Check if this release is highlighted (from URL param navigation)
                const isHighlighted = highlightedReleaseId === release.release_id;
              
                const releaseColumn = (
                <div 
                  key={release.release_id}
                  ref={el => releaseRefs.current[release.release_id] = el}
                  className={cn(
                    "w-72 flex-shrink-0 rounded-lg border transition-all flex flex-col h-full ml-4",
                    columnBg, 
                    colors.borderPrimary,
                    dragOverRelease === release.release_id && !isSprintClosed && "ring-2 ring-blue-500",
                    // Highlight effect: pulsing ring when navigated to via URL param
                    isHighlighted && "ring-2 ring-amber-500 animate-pulse"
                  )}
                  onDragOver={(e) => !isSprintClosed && handleDragOver(e, release.release_id)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => !isSprintClosed && handleDrop(e, release.release_id)}
                >
                  {/* Release Header */}
                  <div className={cn("p-3 border-b flex-shrink-0 group/header", colors.borderPrimary)}>
                    <div className="flex items-center justify-between mb-1">
                      <span className={cn("font-medium", colors.textPrimary)}>{release.name}</span>
                      <div className="flex items-center gap-1">
                        {canEdit && !isSprintClosed && (
                          <button
                            onClick={() => {
                              setSelectedRelease(release);
                              setShowEditModal(true);
                            }}
                            className={cn("p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 opacity-0 group-hover/header:opacity-100 transition-opacity", colors.textMuted)}
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {isReleased ? (
                          <Badge variant="secondary" className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            In Production
                          </Badge>
                        ) : isSprintClosed ? (
                          <Badge variant="secondary" className="bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400">
                            <Lock className="w-3 h-3 mr-1" />
                            Sprint Closed
                          </Badge>
                        ) : isSprintStarted ? (
                          <Badge variant="secondary" className="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                            <Play className="w-3 h-3 mr-1" />
                            Active
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-gray-500">
                            <Clock className="w-3 h-3 mr-1" />
                            Planning
                          </Badge>
                        )}
                      </div>
                    </div>
                    
                    {/* Progress bar - only for open sprints */}
                    {!isSprintClosed && (
                      <div className="w-full h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full mb-2">
                        <div 
                          className="h-full bg-green-500 rounded-full transition-all"
                          style={{ width: `${release.progress?.progress_percent || 0}%` }}
                        />
                      </div>
                    )}
                    
                    <div className="flex items-center justify-between text-xs">
                      <span className={colors.textMuted}>
                        {formatDate(release.start_date)} - {formatDate(release.end_date)}
                      </span>
                      <span className={colors.textMuted}>
                        {releaseFeatures.length} features
                      </span>
                    </div>
                    
                    {/* Resource availability indicator */}
                    {!isSprintClosed && release.resource_availability !== 100 && (
                      <div className={cn("text-xs mt-1", colors.textMuted)}>
                        ⚡ {release.resource_availability}% capacity
                      </div>
                    )}
                  </div>
                  
                  {/* Release Features - Scrollable container */}
                  <div className="p-2 space-y-2 flex-1 overflow-y-auto min-h-0">
                    {/* In Production Features - shown at top for released sprints */}
                    {inProductionFeatures.length > 0 && (
                      <div className="mb-3">
                        <p className={cn("text-xs font-medium mb-2 flex items-center gap-1 text-green-600 dark:text-green-400")}>
                          <CheckCircle className="w-3 h-3" />
                          In Production ({inProductionFeatures.length})
                        </p>
                        {inProductionFeatures.map(f => (
                          <Card 
                            key={f.feature_id} 
                            className={cn(
                              "p-3 border transition-all mb-2 bg-green-50/50 dark:bg-green-950/20 border-green-200 dark:border-green-800 group"
                            )}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <p className={cn("text-sm font-medium line-clamp-2 flex-1", colors.textPrimary)}>{f.title}</p>
                              <div className="flex items-center gap-1 flex-shrink-0">
                                <CheckCircle className="w-4 h-4 text-green-500" />
                                {canEdit && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSelectedFeature(f);
                                    }}
                                    className={cn("p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 opacity-0 group-hover:opacity-100 transition-opacity", colors.textMuted)}
                                    title="Edit feature"
                                  >
                                    <Pencil className="w-3.5 h-3.5" />
                                  </button>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center justify-between mt-2">
                              <Badge 
                                variant="secondary" 
                                className="text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                              >
                                Done
                              </Badge>
                              {f.effort_size && (
                                <span className={cn("text-xs", colors.textMuted)}>{f.effort_size}</span>
                              )}
                            </div>
                            {/* Tags */}
                            {f.tag_ids && f.tag_ids.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {f.tag_ids.slice(0, 3).map(tagId => {
                                  const tag = tagMap[tagId];
                                  return tag ? (
                                    <TagBadge key={tagId} tag={tag} size="sm" />
                                  ) : null;
                                })}
                                {f.tag_ids.length > 3 && (
                                  <span className={cn("text-xs px-1.5 py-0.5 rounded", colors.bgTertiary, colors.textMuted)}>
                                    +{f.tag_ids.length - 3}
                                  </span>
                                )}
                              </div>
                            )}
                          </Card>
                        ))}
                      </div>
                    )}
                    
                    {/* Active Features (not yet in production) */}
                    {activeFeatures.map(f => {
                      const hasSprintSlippage = f.sprint_slippage_count > 0;
                      const hasReleaseSlippage = f.release_slippage_count > 0;
                      const totalSlippage = (f.sprint_slippage_count || 0) + (f.release_slippage_count || 0);
                      const isPending = isFeaturePending(f.feature_id);
                      
                      return (
                        <Card 
                          key={f.feature_id} 
                          draggable={canEdit && !isSprintClosed && !isPending}
                          onDragStart={(e) => handleDragStart(e, f)}
                          className={cn(
                            "p-3 border transition-all relative group",
                            colors.bgCard, 
                            colors.borderPrimary,
                            canEdit && !isSprintClosed && !isPending && "cursor-grab hover:shadow-md",
                            draggedFeature?.feature_id === f.feature_id && "opacity-50",
                            isPending && "opacity-70 animate-pulse",
                            // Slippage left border indicators
                            hasSprintSlippage && !hasReleaseSlippage && "border-l-4 border-l-orange-400",
                            hasReleaseSlippage && "border-l-4 border-l-yellow-400"
                          )}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <p className={cn("text-sm font-medium line-clamp-2 flex-1", colors.textPrimary)}>{f.title}</p>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              {isPending ? (
                                <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
                              ) : (
                                <>
                                  {f.status === 'Done' && (
                                    <CheckCircle className="w-4 h-4 text-green-500" />
                                  )}
                                  {canEdit && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedFeature(f);
                                      }}
                                      className={cn("p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 opacity-0 group-hover:opacity-100 transition-opacity", colors.textMuted)}
                                      title="Edit feature"
                                    >
                                      <Pencil className="w-3.5 h-3.5" />
                                    </button>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center justify-between mt-2">
                            <div className="flex items-center gap-2">
                              <Badge 
                                variant="outline" 
                                className={cn(
                                  "text-xs",
                                  (f.execution_type || 'engineering') === 'engineering'
                                    ? "border-blue-300 text-blue-600 dark:border-blue-700 dark:text-blue-400"
                                    : "border-purple-300 text-purple-600 dark:border-purple-700 dark:text-purple-400"
                                )}
                              >
                                {(f.execution_type || 'engineering') === 'engineering' ? 'Dev' : 'Strat.'}
                              </Badge>
                              <Badge 
                                variant="secondary" 
                                className={cn(
                                  "text-xs",
                                  f.status === 'Done' && "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
                                  f.status === 'In Progress' && "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400"
                                )}
                              >
                                {f.status || 'Backlog'}
                              </Badge>
                              {/* Slippage indicator badge */}
                              {totalSlippage > 0 && (
                                <Badge 
                                  variant="outline" 
                                  className={cn(
                                    "text-xs gap-1",
                                    hasReleaseSlippage 
                                      ? "border-yellow-400 text-yellow-600 dark:text-yellow-400" 
                                      : "border-orange-400 text-orange-600 dark:text-orange-400"
                                  )}
                                  title={`Slipped ${totalSlippage}x (${f.sprint_slippage_count || 0} from sprint, ${f.release_slippage_count || 0} from release)`}
                                >
                                  <RotateCcw className="w-3 h-3" />
                                  {totalSlippage}x
                                </Badge>
                              )}
                            </div>
                            {f.effort_size && (
                              <span className={cn("text-xs", colors.textMuted)}>{f.effort_size}</span>
                            )}
                          </div>
                          {/* Tags */}
                          {f.tag_ids && f.tag_ids.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {f.tag_ids.slice(0, 3).map(tagId => {
                                const tag = tagMap[tagId];
                                return tag ? (
                                  <TagBadge key={tagId} tag={tag} size="sm" />
                                ) : null;
                              })}
                              {f.tag_ids.length > 3 && (
                                <span className={cn("text-xs px-1.5 py-0.5 rounded", colors.bgTertiary, colors.textMuted)}>
                                  +{f.tag_ids.length - 3}
                                </span>
                              )}
                            </div>
                          )}
                        </Card>
                      );
                    })}
                    
                    {/* Done but not in production section - show for any column that has such features */}
                    {doneNotInProduction.length > 0 && (
                      <div className={cn(activeFeatures.length > 0 && "mt-4")}>
                        <div className={cn(activeFeatures.length > 0 && "border-t pt-3", colors.borderPrimary)}>
                          <p className={cn("text-xs font-medium mb-2 flex items-center gap-1", colors.textMuted)}>
                            <Clock className="w-3 h-3" />
                            Done but not in production ({doneNotInProduction.length})
                          </p>
                          {doneNotInProduction.map(f => {
                            const hasSprintSlippage = f.sprint_slippage_count > 0;
                            const hasReleaseSlippage = f.release_slippage_count > 0;
                            const totalSlippage = (f.sprint_slippage_count || 0) + (f.release_slippage_count || 0);
                            
                            return (
                              <Card 
                                key={f.feature_id} 
                                className={cn(
                                  "p-3 border transition-all mb-2 bg-yellow-50/50 dark:bg-yellow-950/20 group",
                                  colors.borderPrimary,
                                  // Slippage left border
                                  totalSlippage > 0 && "border-l-4 border-l-yellow-400"
                                )}
                              >
                                <div className="flex items-start justify-between gap-2">
                                  <p className={cn("text-sm font-medium line-clamp-2 flex-1", colors.textPrimary)}>{f.title}</p>
                                  <div className="flex items-center gap-1 flex-shrink-0">
                                    <Clock className="w-4 h-4 text-yellow-600" />
                                    {canEdit && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setSelectedFeature(f);
                                        }}
                                        className={cn("p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 opacity-0 group-hover:opacity-100 transition-opacity", colors.textMuted)}
                                        title="Edit feature"
                                      >
                                        <Pencil className="w-3.5 h-3.5" />
                                      </button>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center justify-between mt-2">
                                  <div className="flex items-center gap-2">
                                    <Badge 
                                      variant="secondary" 
                                      className="text-xs bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400"
                                    >
                                      Done
                                    </Badge>
                                    {/* Slippage indicator badge */}
                                    {totalSlippage > 0 && (
                                      <Badge 
                                        variant="outline" 
                                        className="text-xs gap-1 border-yellow-400 text-yellow-600 dark:text-yellow-400"
                                        title={`Slipped ${totalSlippage}x (${f.sprint_slippage_count || 0} from sprint, ${f.release_slippage_count || 0} from release)`}
                                      >
                                        <RotateCcw className="w-3 h-3" />
                                        {totalSlippage}x
                                      </Badge>
                                    )}
                                  </div>
                                  {f.effort_size && (
                                    <span className={cn("text-xs", colors.textMuted)}>{f.effort_size}</span>
                                  )}
                                </div>
                                {/* Tags */}
                                {f.tag_ids && f.tag_ids.length > 0 && (
                                  <div className="flex flex-wrap gap-1 mt-2">
                                    {f.tag_ids.slice(0, 3).map(tagId => {
                                      const tag = tagMap[tagId];
                                      return tag ? (
                                        <TagBadge key={tagId} tag={tag} size="sm" />
                                      ) : null;
                                    })}
                                    {f.tag_ids.length > 3 && (
                                      <span className={cn("text-xs px-1.5 py-0.5 rounded", colors.bgTertiary, colors.textMuted)}>
                                        +{f.tag_ids.length - 3}
                                      </span>
                                    )}
                                  </div>
                                )}
                              </Card>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    
                    {activeFeatures.length === 0 && doneNotInProduction.length === 0 && inProductionFeatures.length === 0 && !isSprintClosed && (
                      <div className={cn("border-2 border-dashed rounded-lg p-4 text-center", colors.borderPrimary)}>
                        <p className={cn("text-sm", colors.textMuted)}>Drop features here</p>
                      </div>
                    )}
                  </div>
                  
                  {/* Action Button: Start Sprint, Close Release, OR Promote to Production */}
                  {canEdit && releaseFeatures.length > 0 && (
                    <div className={cn("p-2 border-t flex-shrink-0", colors.borderPrimary)}>
                      {canStartSprint ? (
                        // Stage 0: Start Sprint (only if previous sprint is closed)
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="w-full border-blue-500 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950/30"
                          onClick={async () => {
                            try {
                              await releasesApi.start(release.release_id);
                              toast.success(`Sprint "${release.name}" started`);
                              // Refresh releases
                              const releasesRes = await releasesApi.list(productId);
                              setReleases(releasesRes.data || []);
                            } catch (err) {
                              console.error('Error starting sprint:', err);
                              toast.error(err.response?.data?.detail || 'Failed to start sprint');
                            }
                          }}
                        >
                          <Play className="w-3.5 h-3.5 mr-2" />
                          Start Sprint
                        </Button>
                      ) : isSprintStarted && !isSprintClosed ? (
                        // Stage 1: Close Sprint (only visible after sprint started)
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="w-full"
                          onClick={() => handleCloseRelease(release)}
                        >
                          <Lock className="w-3.5 h-3.5 mr-2" />
                          Close Sprint
                        </Button>
                      ) : isSprintClosed && !isReleased ? (
                        // Stage 2: Promote to Production (only after sprint is closed)
                        <Button 
                          variant="default" 
                          size="sm" 
                          className="w-full bg-green-600 hover:bg-green-700"
                          onClick={() => handlePromoteToProduction(release)}
                        >
                          <Rocket className="w-3.5 h-3.5 mr-2" />
                          Promote to Production
                        </Button>
                      ) : null}
                    </div>
                  )}
                  
                  {/* Show Start Sprint even with 0 features if sprint hasn't started and previous sprint is closed */}
                  {canEdit && releaseFeatures.length === 0 && canStartSprint && (
                    <div className={cn("p-2 border-t flex-shrink-0", colors.borderPrimary)}>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="w-full border-blue-500 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950/30"
                        onClick={async () => {
                          try {
                            await releasesApi.start(release.release_id);
                            toast.success(`Sprint "${release.name}" started`);
                            const releasesRes = await releasesApi.list(productId);
                            setReleases(releasesRes.data || []);
                          } catch (err) {
                            console.error('Error starting sprint:', err);
                            toast.error(err.response?.data?.detail || 'Failed to start sprint');
                          }
                        }}
                      >
                        <Play className="w-3.5 h-3.5 mr-2" />
                        Start Sprint
                      </Button>
                    </div>
                  )}
                </div>
                );
                
                elements.push(releaseColumn);
              });
              
              return elements;
            })()}
            
            {/* Add Release Column */}
            {canEdit && (
              <div 
                onClick={() => setShowCreateModal(true)}
                className={cn(
                  "w-72 flex-shrink-0 rounded-lg border-2 border-dashed flex items-center justify-center cursor-pointer transition-all hover:border-solid h-full min-h-[300px] ml-4",
                  colors.borderPrimary, 
                  colors.bgHover
                )}
              >
                <div className="text-center">
                  <Plus className={cn("w-8 h-8 mx-auto mb-2", colors.textMuted)} />
                  <p className={cn("text-sm font-medium", colors.textMuted)}>Add Release</p>
                </div>
              </div>
            )}
              </div>
            </div>
          </>
        )}
      </div>
      
      {/* Create Release Modal */}
      <CreateReleaseModal 
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        productId={productId}
        onCreated={async () => {
          const res = await releasesApi.list(productId);
          setReleases(res.data || []);
        }}
      />
      
      {/* Edit Release Modal */}
      {selectedRelease && showEditModal && (
        <EditReleaseModal 
          open={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setSelectedRelease(null);
          }}
          release={selectedRelease}
          allReleases={releases}
          onUpdated={async () => {
            const res = await releasesApi.list(productId);
            setReleases(res.data || []);
          }}
        />
      )}
      
      {/* Close Release Confirmation Modal */}
      {showCloseModal && closePreview && (
        <CloseReleaseModal
          open={showCloseModal}
          onClose={() => {
            setShowCloseModal(false);
            setSelectedRelease(null);
            setClosePreview(null);
          }}
          release={selectedRelease}
          preview={closePreview}
          onConfirm={confirmCloseRelease}
        />
      )}
      
      {/* Promote to Production Modal */}
      {showProductionModal && productionPreview && (
        <PromoteToProductionModal
          open={showProductionModal}
          onClose={() => {
            setShowProductionModal(false);
            setSelectedRelease(null);
            setProductionPreview(null);
          }}
          release={selectedRelease}
          preview={productionPreview}
          onConfirm={confirmPromoteToProduction}
        />
      )}
      
      {/* Release Settings Modal */}
      <SprintSettingsModal
        open={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
        productId={productId}
        onSettingsSaved={loadReleases}
      />
      
      {/* Feature Detail Modal */}
      {selectedFeature && (
        <FeatureDetailModal
          isOpen={!!selectedFeature}
          feature={selectedFeature}
          onUpdate={() => {
            onRefresh();
            loadReleases();
          }}
          onClose={() => setSelectedFeature(null)}
          personas={personas}
          goals={goals}
          releases={releases}
          teamMembers={teamMembers}
          productContext={productContext}
          epicContext={{
            epic: epics.find(e => e.epic_id === selectedFeature?.epic_id),
            siblingFeatures: features.filter(f => f.epic_id === selectedFeature?.epic_id && f.feature_id !== selectedFeature?.feature_id),
            epics
          }}
        />
      )}
      
      {/* AI Bottom Panel */}
      {canUseAI && <CollectionCopilotDrawer 
        context="releases" 
        contextId={productId} 
        title="Release Planner"
        onRefresh={onRefresh}
       
      />}
    </div>
  );
};


// ==================== CREATE RELEASE MODAL ====================
const CreateReleaseModal = ({ open, onClose, productId, onCreated }) => {
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [resourceAvailability, setResourceAvailability] = useState(100);
  const [loading, setLoading] = useState(false);
  const [loadingDefault, setLoadingDefault] = useState(true);
  
  // Load default name on open
  useEffect(() => {
    if (open) {
      setLoadingDefault(true);
      releasesApi.getDefaultName(productId)
        .then(res => {
          setName(res.data.default_name);
        })
        .catch(() => setName('v1.0'))
        .finally(() => setLoadingDefault(false));
    }
  }, [open, productId]);
  
  const handleCreate = async () => {
    if (!name.trim()) return;
    
    setLoading(true);
    try {
      await releasesApi.create(productId, {
        name: name.trim(),
        description,
        resource_availability: resourceAvailability,
      });
      onCreated();
      onClose();
      setName('');
      setDescription('');
      setResourceAvailability(100);
    } catch (err) {
      console.error('Error creating release:', err);
    } finally {
      setLoading(false);
    }
  };
  
  if (!open) return null;
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <Card className={cn("w-full max-w-md p-6", colors.bgCard)}>
        <h3 className={cn("text-lg font-semibold mb-4", colors.textPrimary)}>Create New Release</h3>
        
        <div className="space-y-4">
          <div>
            <label className={cn("text-sm font-medium", colors.textSecondary)}>Release Name</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., v2.0, Sprint 5"
              className={cn("mt-1", colors.bgInput, colors.borderPrimary)}
              disabled={loadingDefault}
            />
          </div>
          
          <div>
            <label className={cn("text-sm font-medium", colors.textSecondary)}>Description (optional)</label>
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of release goals"
              className={cn("mt-1", colors.bgInput, colors.borderPrimary)}
            />
          </div>
          
          <div>
            <label className={cn("text-sm font-medium", colors.textSecondary)}>
              Resource Availability: {resourceAvailability}%
            </label>
            <input
              type="range"
              min="0"
              max="100"
              value={resourceAvailability}
              onChange={(e) => setResourceAvailability(parseInt(e.target.value))}
              className="w-full mt-2"
            />
            <p className={cn("text-xs mt-1", colors.textMuted)}>
              Adjust if team has reduced capacity this sprint
            </p>
          </div>
        </div>
        
        <div className="flex justify-end gap-3 mt-6">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button 
            onClick={handleCreate} 
            disabled={loading || !name.trim()}
            className={colors.accentGradient}
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create Release'}
          </Button>
        </div>
      </Card>
    </div>
  );
};


// ==================== EDIT RELEASE MODAL ====================
const EditReleaseModal = ({ open, onClose, release, onUpdated, allReleases = [] }) => {
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();
  const [name, setName] = useState(release?.name || '');
  const [releaseNotes, setReleaseNotes] = useState(release?.release_notes || '');
  const [resourceAvailability, setResourceAvailability] = useState(release?.resource_availability || 100);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Overlap warning modal state
  const [showOverlapWarning, setShowOverlapWarning] = useState(false);
  const [overlapDetails, setOverlapDetails] = useState(null);
  
  const canEditDates = !release?.is_sprint_started && !release?.is_sprint_closed;
  
  useEffect(() => {
    if (release) {
      setName(release.name || '');
      setReleaseNotes(release.release_notes || '');
      setResourceAvailability(release.resource_availability || 100);
      // Parse dates
      if (release.start_date) {
        setStartDate(release.start_date.split('T')[0]);
      }
      if (release.end_date) {
        setEndDate(release.end_date.split('T')[0]);
      }
    }
  }, [release]);
  
  // Check for overlaps with other releases
  const checkOverlaps = () => {
    if (!canEditDates || !startDate || !endDate) return null;
    
    const newStart = new Date(startDate);
    const newEnd = new Date(endDate);
    
    // Sort releases by start date
    const sortedReleases = [...allReleases]
      .filter(r => r.release_id !== release?.release_id)
      .sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
    
    for (const otherRelease of sortedReleases) {
      const otherStart = new Date(otherRelease.start_date);
      const otherEnd = new Date(otherRelease.end_date);
      
      // Check if new start date is before other's end date (overlap on left)
      if (newStart <= otherEnd && newStart >= otherStart) {
        return {
          type: 'start_overlap',
          conflictRelease: otherRelease,
          message: `Start date overlaps with "${otherRelease.name}" which ends on ${otherEnd.toLocaleDateString()}`,
          suggestedFix: `Adjust "${otherRelease.name}" end date to ${new Date(newStart.getTime() - 86400000).toLocaleDateString()}`
        };
      }
      
      // Check if new end date extends into other's start date (overlap on right)
      if (newEnd >= otherStart && newEnd <= otherEnd) {
        return {
          type: 'end_overlap',
          conflictRelease: otherRelease,
          message: `End date overlaps with "${otherRelease.name}" which starts on ${otherStart.toLocaleDateString()}`,
          suggestedFix: `Adjust "${otherRelease.name}" start date to ${new Date(newEnd.getTime() + 86400000).toLocaleDateString()}`
        };
      }
      
      // Check if this release completely contains another
      if (newStart <= otherStart && newEnd >= otherEnd) {
        return {
          type: 'contains',
          conflictRelease: otherRelease,
          message: `Date range completely overlaps with "${otherRelease.name}"`,
          suggestedFix: 'Please choose dates that don\'t overlap with existing releases'
        };
      }
    }
    
    return null;
  };
  
  const handleSave = async () => {
    // Check for overlaps first
    const overlap = checkOverlaps();
    if (overlap && overlap.type !== 'contains') {
      setOverlapDetails(overlap);
      setShowOverlapWarning(true);
      return;
    }
    
    if (overlap && overlap.type === 'contains') {
      toast.error(overlap.message);
      return;
    }
    
    await performSave(false);
  };
  
  const performSave = async (adjustConflict = false) => {
    setLoading(true);
    try {
      const updateData = {
        name,
        release_notes: releaseNotes,
        resource_availability: resourceAvailability,
      };
      
      // Add dates if editable
      if (canEditDates && startDate && endDate) {
        updateData.start_date = new Date(startDate + 'T12:00:00').toISOString();
        updateData.end_date = new Date(endDate + 'T12:00:00').toISOString();
      }
      
      await releasesApi.update(release.release_id, updateData);
      
      // If adjusting conflict, update the other release too
      if (adjustConflict && overlapDetails?.conflictRelease) {
        const conflict = overlapDetails.conflictRelease;
        const newStart = new Date(startDate);
        const newEnd = new Date(endDate);
        
        if (overlapDetails.type === 'start_overlap') {
          // Adjust conflict's end date to day before our start
          const adjustedEnd = new Date(newStart.getTime() - 86400000);
          await releasesApi.update(conflict.release_id, {
            end_date: adjustedEnd.toISOString()
          });
          toast.success(`Adjusted "${conflict.name}" end date`);
        } else if (overlapDetails.type === 'end_overlap') {
          // Adjust conflict's start date to day after our end
          const adjustedStart = new Date(newEnd.getTime() + 86400000);
          await releasesApi.update(conflict.release_id, {
            start_date: adjustedStart.toISOString()
          });
          toast.success(`Adjusted "${conflict.name}" start date`);
        }
      }
      
      onUpdated();
      onClose();
      setShowOverlapWarning(false);
    } catch (err) {
      console.error('Error updating release:', err);
      toast.error('Failed to update release');
    } finally {
      setLoading(false);
    }
  };
  
  if (!open) return null;
  
  // Overlap Warning Modal
  if (showOverlapWarning && overlapDetails) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <Card className={cn("w-full max-w-md p-6", colors.bgCard)}>
          <div className="flex items-center gap-3 mb-4">
            <AlertTriangle className="w-6 h-6 text-amber-500" />
            <h3 className={cn("text-lg font-semibold", colors.textPrimary)}>Date Overlap Detected</h3>
          </div>
          
          <p className={cn("text-sm mb-4", colors.textSecondary)}>
            {overlapDetails.message}
          </p>
          
          <div className={cn("p-3 rounded-lg border mb-4", "bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800")}>
            <p className="text-sm text-amber-700 dark:text-amber-300">
              <strong>Suggested fix:</strong> {overlapDetails.suggestedFix}
            </p>
          </div>
          
          <p className={cn("text-sm mb-4", colors.textMuted)}>
            Do you want to automatically adjust the conflicting release dates?
          </p>
          
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setShowOverlapWarning(false)}>
              Cancel
            </Button>
            <Button 
              onClick={() => performSave(true)}
              disabled={loading}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Adjust Both Dates'}
            </Button>
          </div>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <Card className={cn("w-full max-w-md p-6", colors.bgCard)}>
        <h3 className={cn("text-lg font-semibold mb-4", colors.textPrimary)}>Edit Release</h3>
        
        <div className="space-y-4">
          <div>
            <label className={cn("text-sm font-medium", colors.textSecondary)}>Release Name</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={cn("mt-1", colors.bgInput, colors.borderPrimary)}
            />
          </div>
          
          {/* Date Fields - Only editable if sprint not started */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={cn("text-sm font-medium", colors.textSecondary)}>Start Date</label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                disabled={!canEditDates}
                className={cn("mt-1", colors.bgInput, colors.borderPrimary, !canEditDates && "opacity-50 cursor-not-allowed")}
              />
            </div>
            <div>
              <label className={cn("text-sm font-medium", colors.textSecondary)}>End Date</label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                disabled={!canEditDates}
                className={cn("mt-1", colors.bgInput, colors.borderPrimary, !canEditDates && "opacity-50 cursor-not-allowed")}
              />
            </div>
          </div>
          {!canEditDates && (
            <p className={cn("text-xs", colors.textMuted)}>
              <Lock className="w-3 h-3 inline mr-1" />
              Dates cannot be edited after sprint has started
            </p>
          )}
          
          <div>
            <label className={cn("text-sm font-medium", colors.textSecondary)}>Release Notes</label>
            <textarea
              value={releaseNotes}
              onChange={(e) => setReleaseNotes(e.target.value)}
              placeholder="Document what was shipped in this release..."
              rows={4}
              className={cn("w-full mt-1 p-2 rounded-md border", colors.bgInput, colors.borderPrimary, colors.textPrimary)}
            />
          </div>
          
          <div>
            <label className={cn("text-sm font-medium", colors.textSecondary)}>
              Resource Availability: {resourceAvailability}%
            </label>
            <input
              type="range"
              min="0"
              max="100"
              value={resourceAvailability}
              onChange={(e) => setResourceAvailability(parseInt(e.target.value))}
              className="w-full mt-2"
            />
          </div>
        </div>
        
        <div className="flex justify-end gap-3 mt-6">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button 
            onClick={handleSave} 
            disabled={loading}
            className={colors.accentGradient}
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Changes'}
          </Button>
        </div>
      </Card>
    </div>
  );
};


// ==================== CLOSE SPRINT MODAL ====================
const CloseReleaseModal = ({ open, onClose, release, preview, onConfirm }) => {
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();
  const [loading, setLoading] = useState(false);
  
  const handleConfirm = async () => {
    if (loading) return; // Prevent double-click
    
    setLoading(true);
    try {
      await onConfirm(true); // Always move to next release
      // onConfirm handles modal closure and refresh
    } catch (err) {
      console.error('Error closing sprint:', err);
      toast.error(err?.response?.data?.detail || 'Failed to close sprint');
      setLoading(false); // Only reset loading on error, not success (modal closes on success)
    }
  };
  
  if (!open) return null;
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <Card className={cn("w-full max-w-lg p-6", colors.bgCard)}>
        <h3 className={cn("text-lg font-semibold mb-2", colors.textPrimary)}>
          Close Sprint: {release?.name}
        </h3>
        <p className={cn("text-sm mb-4", colors.textMuted)}>
          This action cannot be undone. The sprint will be locked.
        </p>
        
        {/* Summary */}
        <div className={cn("p-4 rounded-lg mb-4", colors.bgTertiary)}>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className={cn("text-sm font-medium", colors.textSecondary)}>Completed</p>
              <p className={cn("text-2xl font-bold text-green-500")}>
                {preview?.done_features?.length || 0}
              </p>
            </div>
            <div>
              <p className={cn("text-sm font-medium", colors.textSecondary)}>Incomplete</p>
              <p className={cn("text-2xl font-bold text-amber-500")}>
                {preview?.incomplete_features?.length || 0}
              </p>
            </div>
          </div>
        </div>
        
        {/* Incomplete features warning */}
        {preview?.incomplete_features?.length > 0 && (
          <div className={cn("p-4 rounded-lg mb-4 border border-amber-500/30 bg-amber-500/10")}>
            <p className={cn("text-sm font-medium mb-2", colors.textPrimary)}>
              ⚠️ {preview.incomplete_features.length} incomplete features will be moved to:
            </p>
            <p className="text-sm font-semibold text-amber-600 dark:text-amber-400">
              {preview.next_release_exists 
                ? preview.next_release_name 
                : `New release (auto-created)`}
            </p>
            <ul className={cn("mt-2 text-xs space-y-1", colors.textMuted)}>
              {preview.incomplete_features.slice(0, 5).map(f => (
                <li key={f.feature_id}>• {f.title}</li>
              ))}
              {preview.incomplete_features.length > 5 && (
                <li>...and {preview.incomplete_features.length - 5} more</li>
              )}
            </ul>
          </div>
        )}
        
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button 
            onClick={handleConfirm}
            disabled={loading}
            className="bg-amber-600 hover:bg-amber-700 text-white"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
              <>
                <Lock className="w-4 h-4 mr-2" />
                Close Sprint
              </>
            )}
          </Button>
        </div>
      </Card>
    </div>
  );
};


// ==================== PROMOTE TO PRODUCTION MODAL ====================
const PromoteToProductionModal = ({ open, onClose, release, preview, onConfirm }) => {
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();
  const [loading, setLoading] = useState(false);
  const [selectedFeatures, setSelectedFeatures] = useState([]);
  
  // Select all by default
  useEffect(() => {
    if (open && preview?.promotable_features) {
      setSelectedFeatures(preview.promotable_features.map(f => f.feature_id));
    }
  }, [open, preview]);
  
  const handleToggleFeature = (featureId) => {
    setSelectedFeatures(prev => 
      prev.includes(featureId) 
        ? prev.filter(id => id !== featureId)
        : [...prev, featureId]
    );
  };
  
  const handleSelectAll = () => {
    if (preview?.promotable_features) {
      setSelectedFeatures(preview.promotable_features.map(f => f.feature_id));
    }
  };
  
  const handleSelectNone = () => {
    setSelectedFeatures([]);
  };
  
  const handleConfirm = async () => {
    if (loading) return; // Prevent double-click
    
    setLoading(true);
    try {
      await onConfirm(selectedFeatures);
      // onConfirm handles modal closure and refresh
    } catch (err) {
      console.error('Error promoting to production:', err);
      setLoading(false); // Only reset loading on error, not success (modal closes on success)
    }
  };
  
  if (!open || !preview) return null;
  
  const notSelectedCount = preview.promotable_features.length - selectedFeatures.length;
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <Card className={cn("w-full max-w-lg p-6", colors.bgCard)}>
        <h3 className={cn("text-lg font-semibold mb-2 flex items-center gap-2", colors.textPrimary)}>
          <Rocket className="w-5 h-5 text-green-600" />
          Promote to Production
        </h3>
        <p className={cn("text-sm mb-4", colors.textMuted)}>
          Select features to mark as deployed to production. Unselected features will be moved to the next release as "Done but not in production".
        </p>
        
        {!preview.can_promote && preview.blocker_release && (
          <div className="p-3 rounded-lg bg-amber-100 dark:bg-amber-900/30 border border-amber-300 dark:border-amber-700 mb-4">
            <p className="text-sm text-amber-800 dark:text-amber-200 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              Cannot promote: {preview.blocker_release} must be promoted first.
            </p>
          </div>
        )}
        
        {preview.can_promote && preview.promotable_features.length > 0 && (
          <>
            <div className="flex justify-between items-center mb-2">
              <span className={cn("text-sm font-medium", colors.textSecondary)}>
                Done Features ({preview.promotable_features.length})
              </span>
              <div className="flex gap-2">
                <button 
                  onClick={handleSelectAll}
                  className={cn("text-xs px-2 py-1 rounded", colors.bgTertiary, colors.textMuted, "hover:opacity-80")}
                >
                  Select All
                </button>
                <button 
                  onClick={handleSelectNone}
                  className={cn("text-xs px-2 py-1 rounded", colors.bgTertiary, colors.textMuted, "hover:opacity-80")}
                >
                  Select None
                </button>
              </div>
            </div>
            
            <div className={cn("max-h-64 overflow-y-auto rounded-lg border p-2 mb-4", colors.borderPrimary, colors.bgSecondary)}>
              {preview.promotable_features.map(feature => (
                <label 
                  key={feature.feature_id}
                  className={cn(
                    "flex items-center gap-3 p-2 rounded-md cursor-pointer transition-colors",
                    selectedFeatures.includes(feature.feature_id) 
                      ? "bg-green-50 dark:bg-green-950/30" 
                      : "hover:bg-gray-100 dark:hover:bg-gray-800"
                  )}
                >
                  <input
                    type="checkbox"
                    checked={selectedFeatures.includes(feature.feature_id)}
                    onChange={() => handleToggleFeature(feature.feature_id)}
                    className="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                  />
                  <div className="flex-1">
                    <p className={cn("text-sm font-medium", colors.textPrimary)}>{feature.title}</p>
                    {feature.effort_size && (
                      <span className={cn("text-xs", colors.textMuted)}>{feature.effort_size}</span>
                    )}
                  </div>
                  {selectedFeatures.includes(feature.feature_id) && (
                    <CheckCircle className="w-4 h-4 text-green-600" />
                  )}
                </label>
              ))}
            </div>
            
            {notSelectedCount > 0 && (
              <div className="p-3 rounded-lg bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-300 dark:border-yellow-700 mb-4">
                <p className="text-sm text-yellow-800 dark:text-yellow-200 flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  {notSelectedCount} feature{notSelectedCount > 1 ? 's' : ''} will be moved to the next release as "Done but not in production".
                </p>
              </div>
            )}
          </>
        )}
        
        {preview.can_promote && preview.promotable_features.length === 0 && (
          <div className="p-4 rounded-lg bg-gray-100 dark:bg-gray-800 text-center mb-4">
            <p className={cn("text-sm", colors.textMuted)}>No features to promote.</p>
          </div>
        )}
        
        <div className="flex justify-between items-center">
          <span className={cn("text-sm", colors.textMuted)}>
            {selectedFeatures.length} of {preview.promotable_features.length} selected
          </span>
          <div className="flex gap-3">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button 
              onClick={handleConfirm}
              disabled={loading || !preview.can_promote || selectedFeatures.length === 0}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                <>
                  <Rocket className="w-4 h-4 mr-2" />
                  Promote to Production
                </>
              )}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};


// ==================== SPRINT SETTINGS MODAL ====================
const RELEASE_STRATEGIES = [
  { value: 'continuous', label: 'Continuous (CD)', description: 'Items are promoted to production individually as they hit "Done"' },
  { value: 'fixed_interval', label: 'Fixed Interval (Sprint-Based)', description: 'Items are promoted as a batch when the Sprint/Release column is closed' },
  { value: 'day_of_week', label: 'Day of Week (Weekly/Bi-Weekly)', description: 'Items are held until a specific calendar anchor day' },
  { value: 'calendar_monthly', label: 'Calendar Monthly', description: 'Targets specific monthly milestones' },
];

const DAY_OF_WEEK_OPTIONS = [
  { value: 0, label: 'Monday' },
  { value: 1, label: 'Tuesday' },
  { value: 2, label: 'Wednesday' },
  { value: 3, label: 'Thursday' },
  { value: 4, label: 'Friday' },
  { value: 5, label: 'Saturday' },
  { value: 6, label: 'Sunday' },
];

const CALENDAR_MONTHLY_OPTIONS = [
  { value: 'last_friday', label: 'Last Friday of month' },
  { value: 'first_monday', label: 'First Monday of month' },
  { value: 'last_business_day', label: 'Last business day' },
  { value: '1st', label: '1st of month' },
  { value: '15th', label: '15th of month' },
];

const SprintSettingsModal = ({ open, onClose, productId, onSettingsSaved }) => {
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();
  
  // Existing settings
  const [sprintLength, setSprintLength] = useState(14);
  const [firstSprintStart, setFirstSprintStart] = useState('');
  
  // New release strategy settings
  const [releaseStrategy, setReleaseStrategy] = useState('fixed_interval');
  const [cdThresholdHours, setCdThresholdHours] = useState(24);
  const [releaseDayOfWeek, setReleaseDayOfWeek] = useState(4); // Friday
  const [releaseCadence, setReleaseCadence] = useState('weekly');
  const [calendarMonthlyAnchor, setCalendarMonthlyAnchor] = useState('last_friday');
  const [maxDoneAgeDays, setMaxDoneAgeDays] = useState(30);
  
  // Sprint Start/Close Mode Settings (NEW)
  const [sprintStartMode, setSprintStartMode] = useState('manual');
  const [sprintCloseMode, setSprintCloseMode] = useState('manual');
  
  const [loading, setLoading] = useState(false);
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [openReleaseCount, setOpenReleaseCount] = useState(0);
  const [showRecalculateWarning, setShowRecalculateWarning] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  
  // Load current settings and check for existing releases
  useEffect(() => {
    if (open) {
      setLoadingSettings(true);
      setShowRecalculateWarning(false);
      
      Promise.all([
        releasesApi.getSprintSettings(productId),
        releasesApi.list(productId)
      ])
        .then(([settingsRes, releasesRes]) => {
          const settings = settingsRes.data || {};
          
          // Existing settings
          setSprintLength(settings.sprint_length_days || 14);
          if (settings.first_sprint_start) {
            const dateStr = settings.first_sprint_start;
            setFirstSprintStart(dateStr.split('T')[0]);
          }
          
          // New release strategy settings
          setReleaseStrategy(settings.release_strategy || 'fixed_interval');
          setCdThresholdHours(settings.cd_threshold_hours || 24);
          setReleaseDayOfWeek(settings.release_day_of_week ?? 4);
          setReleaseCadence(settings.release_cadence || 'weekly');
          setCalendarMonthlyAnchor(settings.calendar_monthly_anchor || 'last_friday');
          setMaxDoneAgeDays(settings.max_done_age_days || 30);
          
          // Sprint Start/Close Mode
          setSprintStartMode(settings.sprint_start_mode || 'manual');
          setSprintCloseMode(settings.sprint_close_mode || 'manual');
          
          // Count open (non-closed) releases
          const releases = releasesRes.data || [];
          const openCount = releases.filter(r => !r.is_closed).length;
          setOpenReleaseCount(openCount);
        })
        .catch(console.error)
        .finally(() => setLoadingSettings(false));
    }
  }, [open, productId]);
  
  const handleSaveClick = () => {
    // If there are open releases and we have a start date, show warning
    if (openReleaseCount > 0 && firstSprintStart && releaseStrategy === 'fixed_interval') {
      setShowRecalculateWarning(true);
    } else {
      // No releases or no start date - just save settings
      saveSettings(false);
    }
  };
  
  const saveSettings = async (recalculateDates = false) => {
    setLoading(true);
    try {
      // First save the settings
      // Use noon time to avoid timezone date shifts
      const startDateISO = firstSprintStart 
        ? new Date(firstSprintStart + 'T12:00:00').toISOString() 
        : null;
      
      await releasesApi.updateSprintSettings(productId, {
        sprint_length_days: sprintLength,
        first_sprint_start: startDateISO,
        release_strategy: releaseStrategy,
        cd_threshold_hours: cdThresholdHours,
        release_day_of_week: releaseDayOfWeek,
        release_cadence: releaseCadence,
        calendar_monthly_anchor: calendarMonthlyAnchor,
        max_done_age_days: maxDoneAgeDays,
        sprint_start_mode: sprintStartMode,
        sprint_close_mode: sprintCloseMode,
      });
      
      // If user chose to recalculate dates
      if (recalculateDates && firstSprintStart) {
        setRecalculating(true);
        try {
          const result = await releasesApi.recalculateDates(productId);
          toast.success(`Updated dates for ${result.data.updated_count} releases`);
        } catch (err) {
          console.error('Error recalculating dates:', err);
          toast.error('Failed to recalculate release dates');
        }
        setRecalculating(false);
      }
      
      // Notify parent to refresh data and WAIT for it to complete
      if (onSettingsSaved) {
        await onSettingsSaved();
      }
      
      setShowRecalculateWarning(false);
      onClose();
    } catch (err) {
      console.error('Error saving sprint settings:', err);
      toast.error('Failed to save sprint settings');
    } finally {
      setLoading(false);
    }
  };
  
  if (!open) return null;
  
  // Recalculate Warning Modal
  if (showRecalculateWarning) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <Card className={cn("w-full max-w-lg p-6", colors.bgCard)}>
          <div className="flex items-start gap-3 mb-4">
            <div className="p-2 rounded-full bg-amber-100 dark:bg-amber-900/30">
              <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <h3 className={cn("text-lg font-semibold", colors.textPrimary)}>
                Recalculate Release Dates?
              </h3>
              <p className={cn("text-sm mt-1", colors.textMuted)}>
                You have <span className="font-semibold text-amber-600 dark:text-amber-400">{openReleaseCount} open release{openReleaseCount !== 1 ? 's' : ''}</span> that will be affected.
              </p>
            </div>
          </div>
          
          <div className={cn("p-4 rounded-lg mb-4", colors.bgTertiary)}>
            <p className={cn("text-sm", colors.textSecondary)}>
              If you apply these settings, <strong>all open release dates will be recalculated</strong> sequentially based on:
            </p>
            <ul className={cn("text-sm mt-2 ml-4 list-disc space-y-1", colors.textMuted)}>
              <li>First Sprint Start: <strong>{(() => {
                const [year, month, day] = firstSprintStart.split('-').map(Number);
                return new Date(year, month - 1, day, 12, 0, 0).toLocaleDateString();
              })()}</strong></li>
              <li>Sprint Length: <strong>{sprintLength} days</strong></li>
            </ul>
            <p className={cn("text-sm mt-3 text-amber-600 dark:text-amber-400")}>
              ⚠️ Any existing start/end dates on open releases will be overwritten.
            </p>
          </div>
          
          <div className="flex justify-end gap-3">
            <Button 
              variant="outline" 
              onClick={() => {
                // Save settings without recalculating
                saveSettings(false);
              }}
              disabled={loading || recalculating}
            >
              Save Without Recalculating
            </Button>
            <Button 
              onClick={() => saveSettings(true)}
              disabled={loading || recalculating}
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              {recalculating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Recalculating...
                </>
              ) : (
                'Apply & Recalculate Dates'
              )}
            </Button>
          </div>
        </Card>
      </div>
    );
  }
  
  // Main Settings Modal - with Tabs
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <Card className={cn("w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto", colors.bgCard)} style={{ minWidth: '650px' }}>
        <h3 className={cn("text-lg font-semibold mb-4", colors.textPrimary)}>Release Settings</h3>
        
        {loadingSettings ? (
          <div className="flex justify-center py-8">
            <Loader2 className={cn("w-6 h-6 animate-spin", colors.textMuted)} />
          </div>
        ) : (
          <Tabs defaultValue="sprint-cadence" className="w-full">
            <TabsList className={cn("grid w-full grid-cols-3 mb-6", colors.bgTertiary)}>
              <TabsTrigger value="sprint-cadence" className="data-[state=active]:bg-white dark:data-[state=active]:bg-gray-800">
                <Clock className="w-4 h-4 mr-2" />
                Sprint Cadence
              </TabsTrigger>
              <TabsTrigger value="release-to-production" className="data-[state=active]:bg-white dark:data-[state=active]:bg-gray-800">
                <Rocket className="w-4 h-4 mr-2" />
                Release to Production
              </TabsTrigger>
              <TabsTrigger value="automation" className="data-[state=active]:bg-white dark:data-[state=active]:bg-gray-800">
                <Zap className="w-4 h-4 mr-2" />
                Automation
              </TabsTrigger>
            </TabsList>
            
            {/* ========== TAB 1: SPRINT CADENCE (Column Dates) ========== */}
            <TabsContent value="sprint-cadence" className="space-y-4">
              <p className={cn("text-sm", colors.textMuted)}>
                Configure sprint length and start date for release column date calculation.
              </p>
              
              <div className={cn("p-5 rounded-lg border space-y-5", colors.bgTertiary, colors.borderPrimary)}>
                <div>
                  <label className={cn("text-sm font-medium", colors.textSecondary)}>Sprint Length (days)</label>
                  <Input
                    type="number"
                    min="1"
                    max="90"
                    value={sprintLength}
                    onChange={(e) => setSprintLength(parseInt(e.target.value) || 14)}
                    className={cn("mt-2", colors.bgInput, colors.borderPrimary)}
                  />
                  <p className={cn("text-xs mt-2", colors.textMuted)}>
                    Common: 7 (weekly), 14 (bi-weekly), 21 (3 weeks)
                  </p>
                </div>
                
                <div>
                  <label className={cn("text-sm font-medium", colors.textSecondary)}>First Sprint Start Date</label>
                  <Input
                    type="date"
                    value={firstSprintStart}
                    onChange={(e) => setFirstSprintStart(e.target.value)}
                    className={cn("mt-2", colors.bgInput, colors.borderPrimary)}
                  />
                  <p className={cn("text-xs mt-2", colors.textMuted)}>
                    Release column dates will be calculated from this start date
                  </p>
                </div>
                
                {openReleaseCount > 0 && (
                  <div className={cn("p-4 rounded-lg border", "bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800")}>
                    <p className="text-sm text-blue-700 dark:text-blue-300">
                      <strong>{openReleaseCount}</strong> open release{openReleaseCount !== 1 ? 's' : ''} exist{openReleaseCount === 1 ? 's' : ''}. 
                      You&apos;ll have the option to recalculate their dates.
                    </p>
                  </div>
                )}
              </div>
            </TabsContent>
            
            {/* ========== TAB 2: RELEASE TO PRODUCTION ========== */}
            <TabsContent value="release-to-production" className="space-y-4">
              <p className={cn("text-sm", colors.textMuted)}>
                Configure when and how items are promoted to production.
              </p>
              
              {/* Release Strategy Dropdown */}
              <div className={cn("p-5 rounded-lg border", colors.bgTertiary, colors.borderPrimary)}>
                <label className={cn("text-sm font-medium", colors.textSecondary)}>Release Strategy</label>
                <Select value={releaseStrategy} onValueChange={setReleaseStrategy}>
                  <SelectTrigger className={cn("mt-2", colors.bgInput, colors.borderPrimary)}>
                    <SelectValue placeholder="Select strategy" />
                  </SelectTrigger>
                  <SelectContent>
                    {RELEASE_STRATEGIES.map(strategy => (
                      <SelectItem key={strategy.value} value={strategy.value}>
                        {strategy.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className={cn("text-xs mt-2", colors.textMuted)}>
                  {RELEASE_STRATEGIES.find(s => s.value === releaseStrategy)?.description}
                </p>
              </div>
              
              {/* Conditional Fields based on Strategy */}
              {releaseStrategy === 'continuous' && (
                <div className={cn("p-5 rounded-lg border", colors.bgTertiary, colors.borderPrimary)}>
                  <h5 className={cn("text-sm font-medium mb-4", colors.textPrimary)}>Continuous Deployment Settings</h5>
                  <div>
                    <label className={cn("text-sm", colors.textSecondary)}>On Pattern Threshold (hours)</label>
                    <Input
                      type="number"
                      min="1"
                      max="168"
                      value={cdThresholdHours}
                      onChange={(e) => setCdThresholdHours(parseInt(e.target.value) || 24)}
                      className={cn("mt-2", colors.bgInput, colors.borderPrimary)}
                    />
                    <p className={cn("text-xs mt-2", colors.textMuted)}>
                      Features promoted within this time from "Done" are considered "On Pattern"
                    </p>
                  </div>
                </div>
              )}
              
              {releaseStrategy === 'fixed_interval' && (
                <div className={cn("p-4 rounded-lg border", "bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800")}>
                  <p className="text-sm text-green-700 dark:text-green-300">
                    Items are promoted as a batch when the Sprint/Release column is closed. 
                    Uses the Sprint Length ({sprintLength} days) defined in the Sprint Cadence tab.
                  </p>
                </div>
              )}
              
              {releaseStrategy === 'day_of_week' && (
                <div className={cn("p-5 rounded-lg border", colors.bgTertiary, colors.borderPrimary)}>
                  <h5 className={cn("text-sm font-medium mb-4", colors.textPrimary)}>Day of Week Settings</h5>
                  <div className="space-y-5">
                    <div>
                      <label className={cn("text-sm", colors.textSecondary)}>Release Day</label>
                      <Select value={releaseDayOfWeek.toString()} onValueChange={(v) => setReleaseDayOfWeek(parseInt(v))}>
                        <SelectTrigger className={cn("mt-2", colors.bgInput, colors.borderPrimary)}>
                          <SelectValue placeholder="Select day" />
                        </SelectTrigger>
                        <SelectContent>
                          {DAY_OF_WEEK_OPTIONS.map(day => (
                            <SelectItem key={day.value} value={day.value.toString()}>
                              {day.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <label className={cn("text-sm", colors.textSecondary)}>Cadence</label>
                      <Select value={releaseCadence} onValueChange={setReleaseCadence}>
                        <SelectTrigger className={cn("mt-2", colors.bgInput, colors.borderPrimary)}>
                          <SelectValue placeholder="Select cadence" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="weekly">Weekly</SelectItem>
                          <SelectItem value="biweekly">Bi-Weekly</SelectItem>
                        </SelectContent>
                      </Select>
                      {releaseCadence === 'biweekly' && (
                        <p className={cn("text-xs mt-2", colors.textMuted)}>
                          Bi-weekly releases anchor from First Sprint Start Date in Sprint Cadence tab
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
              
              {releaseStrategy === 'calendar_monthly' && (
                <div className={cn("p-5 rounded-lg border", colors.bgTertiary, colors.borderPrimary)}>
                  <h5 className={cn("text-sm font-medium mb-4", colors.textPrimary)}>Calendar Monthly Settings</h5>
                  <div className="space-y-5">
                    <div>
                      <label className={cn("text-sm", colors.textSecondary)}>Release Anchor</label>
                      <Select value={calendarMonthlyAnchor} onValueChange={setCalendarMonthlyAnchor}>
                        <SelectTrigger className={cn("mt-2", colors.bgInput, colors.borderPrimary)}>
                          <SelectValue placeholder="Select anchor" />
                        </SelectTrigger>
                        <SelectContent>
                          {CALENDAR_MONTHLY_OPTIONS.map(opt => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <label className={cn("text-sm", colors.textSecondary)}>Max Days in "Done" Before Alert</label>
                      <Input
                        type="number"
                        min="1"
                        max="90"
                        value={maxDoneAgeDays}
                        onChange={(e) => setMaxDoneAgeDays(parseInt(e.target.value) || 30)}
                        className={cn("mt-2", colors.bgInput, colors.borderPrimary)}
                      />
                      <p className={cn("text-xs mt-2", colors.textMuted)}>
                        Features sitting in "Done" longer than this will be flagged
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* On Pattern Reporting Info */}
              <div className={cn("p-4 rounded-lg border", "bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800")}>
                <div className="flex items-start gap-3">
                  <TrendingUp className="w-5 h-5 text-purple-600 dark:text-purple-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-purple-700 dark:text-purple-300">
                      "On Pattern" Reporting
                    </p>
                    <p className="text-xs text-purple-600 dark:text-purple-400 mt-1">
                      {releaseStrategy === 'continuous' && `Features promoted within ${cdThresholdHours}h of Done → Production are "On Pattern"`}
                      {releaseStrategy === 'fixed_interval' && `Releases closed within their ${sprintLength}-day window are "On Pattern"`}
                      {releaseStrategy === 'day_of_week' && `Features promoted on ${DAY_OF_WEEK_OPTIONS.find(d => d.value === releaseDayOfWeek)?.label} are "On Pattern"`}
                      {releaseStrategy === 'calendar_monthly' && `Features not exceeding ${maxDoneAgeDays} days in Done are "On Pattern"`}
                    </p>
                  </div>
                </div>
              </div>
            </TabsContent>
            
            {/* ========== TAB 3: AUTOMATION ========== */}
            <TabsContent value="automation" className="space-y-4">
              <p className={cn("text-sm", colors.textMuted)}>
                Configure automatic sprint start and close behavior.
              </p>
              
              {/* Sprint Start Mode */}
              <div className={cn("p-5 rounded-lg border space-y-4", colors.bgTertiary, colors.borderPrimary)}>
                <div>
                  <label className={cn("text-sm font-medium", colors.textSecondary)}>Sprint Start Mode</label>
                  <Select value={sprintStartMode} onValueChange={setSprintStartMode}>
                    <SelectTrigger className={cn("mt-2", colors.bgInput, colors.borderPrimary)}>
                      <SelectValue placeholder="Select mode" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="manual">Manual Start (Default)</SelectItem>
                      <SelectItem value="auto">Auto Start</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className={cn("text-xs mt-2", colors.textMuted)}>
                    {sprintStartMode === 'manual' 
                      ? 'You must click "Start Sprint" before 11:59 PM on the first day of the sprint'
                      : 'Sprint automatically starts at 11:00 PM on the first day (org timezone)'}
                  </p>
                </div>
                
                <div className={cn("p-3 rounded-lg border", sprintStartMode === 'manual' 
                  ? "bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800"
                  : "bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800")}>
                  <div className="flex items-start gap-2">
                    {sprintStartMode === 'manual' ? (
                      <Play className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                    ) : (
                      <Zap className="w-4 h-4 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                    )}
                    <div>
                      <p className="text-sm font-medium text-blue-700 dark:text-blue-300">
                        {sprintStartMode === 'manual' ? 'Manual Start' : 'Auto Start Enabled'}
                      </p>
                      <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                        {sprintStartMode === 'manual' 
                          ? 'After sprint starts, moving features OUT counts as slippage'
                          : 'Sprint will auto-start and begin tracking slippage automatically'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Sprint Close Mode */}
              <div className={cn("p-5 rounded-lg border space-y-4", colors.bgTertiary, colors.borderPrimary)}>
                <div>
                  <label className={cn("text-sm font-medium", colors.textSecondary)}>Sprint Close Mode</label>
                  <Select value={sprintCloseMode} onValueChange={setSprintCloseMode}>
                    <SelectTrigger className={cn("mt-2", colors.bgInput, colors.borderPrimary)}>
                      <SelectValue placeholder="Select mode" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="manual">Manual Close (Default)</SelectItem>
                      <SelectItem value="auto">Auto Close</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className={cn("text-xs mt-2", colors.textMuted)}>
                    {sprintCloseMode === 'manual' 
                      ? 'You must click "Close Sprint" to end the sprint and move incomplete features'
                      : 'Sprint automatically closes at 11:59 PM on the last day (org timezone)'}
                  </p>
                </div>
                
                <div className={cn("p-3 rounded-lg border", sprintCloseMode === 'manual' 
                  ? "bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800"
                  : "bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800")}>
                  <div className="flex items-start gap-2">
                    {sprintCloseMode === 'manual' ? (
                      <Square className="w-4 h-4 text-orange-600 dark:text-orange-400 mt-0.5 flex-shrink-0" />
                    ) : (
                      <Zap className="w-4 h-4 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                    )}
                    <div>
                      <p className="text-sm font-medium text-orange-700 dark:text-orange-300">
                        {sprintCloseMode === 'manual' ? 'Manual Close' : 'Auto Close Enabled'}
                      </p>
                      <p className="text-xs text-orange-600 dark:text-orange-400 mt-1">
                        {sprintCloseMode === 'manual' 
                          ? 'Incomplete features will be moved to next sprint on close'
                          : 'Incomplete features will automatically be moved at sprint end'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Info about slippage tracking */}
              <div className={cn("p-4 rounded-lg border", "bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800")}>
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-amber-700 dark:text-amber-300">
                      Slippage Tracking
                    </p>
                    <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                      Once a sprint starts, moving features between sprints or to the parking lot is tracked as slippage. 
                      Moving features <strong>before</strong> sprint starts is considered planning and is not tracked.
                    </p>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        )}
        
        <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button 
            onClick={handleSaveClick} 
            disabled={loading || loadingSettings}
            className={colors.accentGradient}
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Settings'}
          </Button>
        </div>
      </Card>
    </div>
  );
};


// Track which tabs have been visited this session (for auto-collapse)
const visitedTabsRef = { current: new Set() };


export default ProductDetailPage;
